import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, RefreshCw, AlertCircle, Plus, X, FileJson, FileCode, Sparkles, BarChart3, Trash2, User, LogOut, FolderOpen, FolderPlus, Sun, Moon, Download, Upload, Undo2, BookOpen, Globe, Newspaper, Pencil, ChevronLeft, ChevronRight, ChevronDown, CheckCircle2, CloudOff, RotateCw, Radar } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import { useAuth } from '../auth/AuthContext';
import { useTheme } from '../theme/ThemeContext';
import { PRIVACY_POLICY_URL, TERMS_OF_SERVICE_URL } from '../constants/legalLinks';
import LocationSelector from './LocationSelector';
import FilterPanel from './FilterPanel';
import PriceChart from './PriceChart';
import CompetitorCard from './CompetitorCard';
import SentimentComparison from './SentimentComparison';
import { generateProjectHtml, generateTabHtml, safeFilename } from '../utils/htmlExport';

const STORAGE_KEY_PREFIX = 'market_tool_dashboard_';
const DIRTY_AFTER_HYDRATION_KEY = 'dirtyAfterHydration';

// Generate a unique ID for tabs and projects
const generateId = () => Math.random().toString(36).substring(2, 11);

// Serialize tab for storage so all user data is persisted (Set -> array, strip transient loading/error).
function tabToStorage(tab) {
  const base = {
    id: tab.id,
    title: tab.title ?? 'New Search',
    product: tab.product ?? '',
    location: tab.location ?? 'United States',
    yearsBack: tab.yearsBack ?? 5,
    filters: tab.filters ?? { minPrice: '', maxPrice: '', minSentiment: 0, sortBy: 'price', showOnlySelected: false },
    selectedCompetitors: tab.selectedCompetitors ? Array.from(tab.selectedCompetitors) : [],
    data: tab.data ?? null,
    competitorSearchQuery: tab.competitorSearchQuery ?? '',
    showComparison: tab.showComparison ?? false,
    // Strip in-flight (loading-only) entries — they have no data and would restore as stuck spinners
    deepDives: Object.fromEntries(Object.entries(tab.deepDives ?? {}).filter(([, v]) => v?.data)),
    activeDeepDiveName: tab.activeDeepDiveName ?? null,
    seoAnalysis: tab.seoAnalysis ?? null,
    seoWarning: tab.seoWarning ?? null,
    marketShare: tab.marketShare ?? null,
    marketShareWarning: tab.marketShareWarning ?? null,
    events: Object.fromEntries(Object.entries(tab.events ?? {}).filter(([, v]) => v?.data || v?.events)),
    activeEventName: tab.activeEventName ?? null,
    eventsMonthsBack: tab.eventsMonthsBack ?? 1,
    researchJobId: tab.researchJobId ?? null,
    researchRequestId: tab.researchRequestId ?? null,
    researchProgress: tab.researchJobId ? (tab.researchProgress ?? 0) : 0,
    researchProgressLabel: tab.researchJobId ? (tab.researchProgressLabel ?? '') : '',
    // Strip in-flight (loading-only) trend analysis entries — same pattern as deepDives/events
    trendAnalyses: Object.fromEntries(Object.entries(tab.trendAnalyses ?? {}).filter(([, v]) => v?.data)),
    activeTrendName: tab.activeTrendName ?? null,
    furtherReadingSources: tab.furtherReadingSources ?? null,
  };
  return {
    ...base,
    seoAnalysisLoading: false,
    seoAnalysisError: null,
    marketShareLoading: false,
    marketShareError: null,
    eventsLoading: false,
    eventsError: null,
  };
}

// Deserialize tab from storage (array -> Set)

function storageToTab(raw) {
  return {
    ...raw,
    selectedCompetitors: new Set(raw.selectedCompetitors || []),
    error: null,
    expandingCompetitors: false,
    competitorSearchQuery: raw.competitorSearchQuery || '',
    searchingCompetitor: false,
    showComparison: raw.showComparison || false,
    // Safety net: strip any loading-only entries that survived in old persisted state
    deepDives: Object.fromEntries(Object.entries(raw.deepDives || {}).filter(([, v]) => v?.data)),
    activeDeepDiveName: raw.activeDeepDiveName || null,
    seoAnalysis: raw.seoAnalysis || null,
    seoAnalysisLoading: false,
    seoAnalysisError: null,
    seoWarning: raw.seoWarning || null,
    marketShare: raw.marketShare || null,
    marketShareLoading: false,
    marketShareError: null,
    marketShareWarning: raw.marketShareWarning || null,
    events: Object.fromEntries(Object.entries(raw.events || {}).filter(([, v]) => v?.data || v?.events)),
    activeEventName: raw.activeEventName || null,
    eventsMonthsBack: raw.eventsMonthsBack ?? 1,
    eventsLoading: false,
    eventsError: null,
    researchJobId: raw.researchJobId || null,
    researchRequestId: raw.researchRequestId || null,
    researchProgress: raw.researchProgress ?? 0,
    researchProgressLabel: raw.researchProgressLabel ?? '',
    researchPollingState: raw.researchJobId && !raw.data ? 'active' : 'idle',
    previewCompetitors: null,
    // Safety net: strip any loading-only trend analysis entries from old persisted state
    trendAnalyses: Object.fromEntries(Object.entries(raw.trendAnalyses || {}).filter(([, v]) => v?.data)),
    activeTrendName: raw.activeTrendName || null,
    furtherReadingSources: raw.furtherReadingSources || null,
    furtherReadingLoading: false,
    furtherReadingError: null,
    // Restore loading when we have a job in progress but no result yet (e.g. after refresh/navigation)
    loading: raw.loading === true || !!(raw.researchJobId && !raw.data),
  };
}

function loadDashboardState(userId) {
  try {
    const key = `${STORAGE_KEY_PREFIX}${userId || 'anonymous'}`;
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem(key) : null;
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data?.projects?.length) return data;
    return null;
  } catch {
    return null;
  }
}

function saveDashboardState(userId, state) {
  try {
    const key = `${STORAGE_KEY_PREFIX}${userId || 'anonymous'}`;
    window.localStorage.setItem(key, JSON.stringify(state));
  } catch (e) {
    console.warn('Failed to save dashboard state:', e);
  }
}

// Default tab state
const createDefaultTab = () => ({
  id: generateId(),
  title: 'New Search',
  product: '',
  location: 'United States',
  yearsBack: 5,
  filters: {
    minPrice: '',
    maxPrice: '',
    minSentiment: 0,
    sortBy: 'price',
    showOnlySelected: false,
  },
  selectedCompetitors: new Set(),
  data: null,
  loading: false,
  error: null,
  expandingCompetitors: false,
  competitorSearchQuery: '',
  searchingCompetitor: false,
  showComparison: false,
  deepDives: {},
  activeDeepDiveName: null,
  seoAnalysis: null,
  seoAnalysisLoading: false,
  seoAnalysisError: null,
  seoWarning: null,
  marketShare: null,
  marketShareLoading: false,
  marketShareError: null,
  marketShareWarning: null,
  events: {},
  activeEventName: null,
  eventsMonthsBack: 1,
  eventsLoading: false,
  eventsError: null,
  researchJobId: null,
  researchRequestId: null,
  researchPollingState: 'idle',
  researchProgress: 0,
  researchProgressLabel: '',
  previewCompetitors: null,
  trendAnalyses: {},
  activeTrendName: null,
  furtherReadingSources: null,
  furtherReadingLoading: false,
  furtherReadingError: null,
});

// Safe initial state so activeTab is never undefined (e.g. on page refresh before hydration).
function getDefaultDashboardState() {
  const defaultTab = createDefaultTab();
  const defaultProject = {
    id: generateId(),
    name: 'Project 1',
    tabs: [tabToStorage(defaultTab)],
  };
  return {
    projects: [defaultProject],
    activeProjectId: defaultProject.id,
    tabs: [defaultTab],
    activeTabId: defaultTab.id,
  };
}

function Dashboard() {
  const navigate = useNavigate();
  const { user, logout, updateUsage, refreshUserProfile } = useAuth();
  const userId = user?.id || null;
  const initialLoadDone = useRef(false);
  const lastUserId = useRef(userId);
  const didLogMount = useRef(false);
  if (!didLogMount.current) {
    didLogMount.current = true;
    console.log('[Dashboard] mounted', { userId: userId || 'anonymous' });
  }

  // Initialize with default state so activeTab is always defined (avoids crash on refresh before hydration).
  const defaultState = useMemo(() => getDefaultDashboardState(), []);
  const [projects, setProjects] = useState(defaultState.projects);
  const [activeProjectId, setActiveProjectId] = useState(defaultState.activeProjectId);
  const [tabs, setTabs] = useState(defaultState.tabs);
  const [activeTabId, setActiveTabId] = useState(defaultState.activeTabId);
  const tabsRef = useRef(defaultState.tabs);

  // True after we've loaded state from server/local or applied default (so we show real content, not loading).
  const [stateHydrated, setStateHydrated] = useState(false);
  const hydratedAtRef = useRef(0);
  const hydrationSourceRef = useRef('initial');
  const prehydratedFromLocalRef = useRef(false);
  const serverHydrationFinishedRef = useRef(false);

  // Get active tab (always defined due to safe initial state)
  const activeTab = tabs.find(t => t.id === activeTabId) || tabs[0];
  const showInitialResearchNotice = !!activeTab?.loading && !activeTab?.data;

  // Track API usage (backend returns queries_used / queries_limit on user)
  const [apiCalls, setApiCalls] = useState(user?.queries_used ?? 0);
  const apiLimit = user?.queries_limit ?? 100;

  const debugRefresh = () => typeof window !== 'undefined' && window.localStorage?.getItem('DEBUG_REFRESH');
  const logRefresh = (msg, data) => { if (debugRefresh()) console.log('[Refresh]', msg, data !== undefined ? data : ''); };
  const debugResearchPoll = () => typeof window !== 'undefined' && window.localStorage?.getItem('DEBUG_RESEARCH_POLL');
  const logPoll = (msg, data) => { if (debugResearchPoll()) console.log('[ResearchPoll]', msg, data !== undefined ? data : ''); };

  const dashboardJsonInputRef = useRef(null);
  const projectJsonInputRef = useRef(null);
  const deepDiveSectionRef = useRef(null);
  const trendAnalysisSectionRef = useRef(null);
  const htmlExportMenuRef = useRef(null);
  const [htmlExportMenuOpen, setHtmlExportMenuOpen] = useState(false);

  useEffect(() => {
    tabsRef.current = tabs;
  }, [tabs]);

  useEffect(() => {
    if (!htmlExportMenuOpen) return undefined;
    const handlePointerDown = (event) => {
      if (!htmlExportMenuRef.current?.contains(event.target)) {
        setHtmlExportMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [htmlExportMenuOpen]);

  // Refs that always hold the latest values for use inside async callbacks (avoids stale closures).
  const activeTabIdRef = useRef(activeTabId);
  const activeTabRef = useRef(activeTab);
  useEffect(() => { activeTabIdRef.current = activeTabId; }, [activeTabId]);
  useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);

  // When true, the next persistence effect run will skip the debounce and save immediately to the
  // server. Set to true just before a research job completes so the user's results are persisted
  // before they can refresh the page and hit the stale server state.
  const forceSaveRef = useRef(false);

  // Save-status indicator: 'idle' | 'saving' | 'saved' | 'error'
  const [saveStatus, setSaveStatus] = useState('idle');
  const [saveError, setSaveError] = useState('');
  // Holds the last state payload that failed to save so the retry handler can resend it.
  const lastFailedSaveRef = useRef(null);
  const savedClearTimerRef = useRef(null);

  // Load persisted state on mount / user change (server when logged in, else localStorage).
  // After refresh we always pull latest from DB when logged in; safe initial state avoids crash before hydration.
  useEffect(() => {
    console.log('[Dashboard] load effect ran', { userId: userId || 'anonymous', initialLoadDone: initialLoadDone.current });
    if (lastUserId.current !== userId) {
      lastUserId.current = userId;
      initialLoadDone.current = false;
    }
    if (initialLoadDone.current) {
      console.log('[Dashboard] load effect skipped (initialLoadDone)');
      return;
    }
    initialLoadDone.current = true;
    logRefresh('Dashboard load effect', { userId: userId || 'anonymous' });

    const applyState = (saved, { source = 'unknown', authoritative = true } = {}) => {
      try {
        const projectsList = saved?.projects && Array.isArray(saved.projects) ? saved.projects : null;
        const activeId = saved?.activeProjectId;
        if (projectsList?.length && activeId) {
          const proj = projectsList.find(p => p && p.id === activeId) || projectsList[0];
          const rawTabs = proj?.tabs && Array.isArray(proj.tabs) ? proj.tabs : [];
          const initialTabs = rawTabs.map(t => {
            try {
              return storageToTab(t);
            } catch {
              return createDefaultTab();
            }
          });
          const safeTabs = initialTabs.length ? initialTabs : [createDefaultTab()];
          const firstId = safeTabs[0].id;
          const restoredTabId = saved?.activeTabId && safeTabs.some(t => t.id === saved.activeTabId)
            ? saved.activeTabId
            : firstId;
          setProjects(projectsList);
          setActiveProjectId(proj?.id ?? activeId ?? projectsList[0].id);
          setTabs(safeTabs);
          setActiveTabId(restoredTabId);
        } else {
          const def = getDefaultDashboardState();
          setProjects(def.projects);
          setActiveProjectId(def.activeProjectId);
          setTabs(def.tabs);
          setActiveTabId(def.activeTabId);
        }
      } catch (e) {
        console.warn('Dashboard applyState error, using default:', e);
        const def = getDefaultDashboardState();
        setProjects(def.projects);
        setActiveProjectId(def.activeProjectId);
        setTabs(def.tabs);
        setActiveTabId(def.activeTabId);
      }
      hydrationSourceRef.current = source;
      if (authoritative) {
        hydratedAtRef.current = Date.now();
        setStateHydrated(true);
      }
      if (typeof window !== 'undefined' && window.localStorage?.getItem('DEBUG_REFRESH')) {
        console.log('[Refresh] applyState done', { source, authoritative, stateHydrated: authoritative });
      }
    };

    if (userId) {
      setStateHydrated(false);
      serverHydrationFinishedRef.current = false;
      prehydratedFromLocalRef.current = false;
      // Pre-hydrate from localStorage IMMEDIATELY for instant visual feedback.
      // This eliminates the blank-screen window (800ms stagger + server RTT) that the
      // user would otherwise see on every navigation to /dashboard.
      const localState = loadDashboardState(userId);
      const localAlreadyApplied = !!(localState?.projects?.length);
      if (localAlreadyApplied) {
        prehydratedFromLocalRef.current = true;
        logRefresh('pre-hydrating from localStorage as non-authoritative', { projectCount: localState.projects.length, savedAt: localState.savedAt, dirtyAfterHydration: !!localState[DIRTY_AFTER_HYDRATION_KEY] });
        applyState(localState, { source: 'local-prehydrate', authoritative: false });
      }
      // No local state: keep stateHydrated false until server or fallback responds.

      let timerCleared = false;
      const fallbackTimer = setTimeout(() => {
        if (timerCleared) return;
        timerCleared = true;
        const fallbackState = loadDashboardState(userId);
        logRefresh('dashboard/state fallback timer fired', {
          hasLocal: !!fallbackState,
          prehydratedFromLocal: prehydratedFromLocalRef.current,
        });
        applyState(fallbackState, {
          source: fallbackState ? 'local-fallback-server-slow' : 'default-fallback-server-slow',
          authoritative: true,
        });
      }, 20000);  // Increased from 12s to 20s for cold backend starts
      const clearFallback = () => {
        if (!timerCleared) {
          timerCleared = true;
          clearTimeout(fallbackTimer);
        }
      };
      const abortController = new AbortController();
      // Stagger: wait briefly so backend isn't hit with auth/me and dashboard/state back-to-back
      // (reduces timeouts on cold start / refresh when both would run close together).
      const STAGGER_MS = 800;  // Increased from 400ms for cold Railway backend
      const fetchDashboardState = () => {
        console.log('[Dashboard] fetchDashboardState (after stagger)', { userId });
        logRefresh('GET /api/dashboard/state (after stagger)');
        return import('../utils/api')
          .then(({ getDashboardState }) => {
            return getDashboardState(abortController.signal)
              .then((res) => {
                clearFallback();
                serverHydrationFinishedRef.current = true;
                const projectCount = res?.state?.projects?.length ?? 0;
                const serverSavedAt = res?.state?.savedAt || 0;
                const localSavedAt = localState?.savedAt || 0;
                const localDirty = !!localState?.[DIRTY_AFTER_HYDRATION_KEY];
                logRefresh('dashboard/state response', {
                  success: res?.success,
                  hasState: !!projectCount,
                  projectCount,
                  activeProjectId: res?.state?.activeProjectId ? 'set' : 'missing',
                  serverSavedAt,
                  localSavedAt,
                  localDirty,
                });
                if (res?.success && res.state && Array.isArray(res.state.projects) && res.state.projects.length > 0) {
                  // On logged-in first load, server is authoritative unless local state
                  // was dirtied after hydration and is newer than the server copy.
                  if (localAlreadyApplied && localDirty && localSavedAt > serverSavedAt) {
                    applyState(localState, { source: 'local-dirty-newer-than-server', authoritative: true });
                    logRefresh('dirty local state is newer than server; keeping local', { localSavedAt, serverSavedAt });
                  } else {
                    logRefresh('server state is authoritative on first logged-in load', { serverSavedAt, localSavedAt, localDirty });
                    applyState(res.state, { source: 'server', authoritative: true });
                  }
                  return;
                }
                logRefresh('using local/default state (server state missing or empty)');
                if (!localAlreadyApplied) {
                  applyState(loadDashboardState(userId), { source: 'default-empty-server', authoritative: true });
                } else {
                  applyState(localState, { source: 'local-empty-server', authoritative: true });
                }
              })
              .catch((err) => {
                clearFallback();
                serverHydrationFinishedRef.current = true;
                console.warn('[Dashboard] state fetch failed, using local/default', err?.message || err, err?.code);
                if (!localAlreadyApplied) {
                  applyState(loadDashboardState(userId), { source: 'default-server-fetch-failed', authoritative: true });
                } else {
                  applyState(localState, { source: 'local-server-fetch-failed', authoritative: true });
                }
              });
          })
          .catch((err) => {
            clearFallback();
            serverHydrationFinishedRef.current = true;
            console.warn('[Dashboard] import(api) or getDashboardState failed', err?.message || err);
            if (!localAlreadyApplied) {
              applyState(loadDashboardState(userId), { source: 'default-api-import-failed', authoritative: true });
            } else {
              applyState(localState, { source: 'local-api-import-failed', authoritative: true });
            }
          });
      };
      const staggerId = setTimeout(fetchDashboardState, STAGGER_MS);
      return () => {
        abortController.abort();
        clearTimeout(staggerId);
        clearTimeout(fallbackTimer);
        timerCleared = true;
        // Reset so a remount (e.g. React Strict Mode) runs the load again; otherwise
        // we skip with "initialLoadDone" and server state never populates until refresh.
        initialLoadDone.current = false;
      };
    } else {
      serverHydrationFinishedRef.current = true;
      applyState(loadDashboardState(userId), { source: 'anonymous-local', authoritative: true });
    }
  }, [userId]);

  // Persist when tabs, active tab, or project selection changes (localStorage + server when logged in).
  // Full state: projects (with serialized tabs), activeProjectId, activeTabId so restore is accurate.
  const SAVE_AFTER_HYDRATION_MS = 1500;
  const SERVER_SAVE_DEBOUNCE_MS = 700;

  useEffect(() => {
    if (!activeProjectId || projects.length === 0 || tabs.length === 0) return;
    const justHydrated = hydratedAtRef.current && (Date.now() - hydratedAtRef.current < SAVE_AFTER_HYDRATION_MS);
    if (userId && (!stateHydrated || !serverHydrationFinishedRef.current)) {
      logRefresh('skipping persistence before authoritative hydration', {
        stateHydrated,
        serverHydrationFinished: serverHydrationFinishedRef.current,
        hydrationSource: hydrationSourceRef.current,
      });
      return;
    }
    const serialized = tabs.map(tabToStorage);
    const nextProjects = projects.map(p =>
      p.id === activeProjectId ? { ...p, tabs: serialized } : p
    );
    const state = {
      projects: nextProjects,
      activeProjectId,
      activeTabId,
      savedAt: Date.now(),  // timestamp used to resolve server-vs-local conflicts on load
      [DIRTY_AFTER_HYDRATION_KEY]: userId ? !justHydrated : true,
    };
    // Always persist to localStorage immediately (fast, no network, powers instant pre-hydration).
    saveDashboardState(userId, state);

    // Suppress server saves while research is actively polling. The poller updates tab state
    // every 2.5 s with transient progress data; sending that to the server would generate
    // constant network traffic and cause the save indicator to flicker on every poll tick.
    // forceSaveRef is set to true just before the completion setTabs call, so the one
    // meaningful save (with actual results) always fires immediately via forceImmediate below.
    const isResearchActive = tabs.some(t => t?.loading && t?.researchJobId);
    if (userId && stateHydrated && isResearchActive) {
      logRefresh('server save skipped while research is active', {
        activeJobs: tabs.filter(t => t?.loading && t?.researchJobId).map(t => t.researchJobId),
      });
    }

    if (userId && stateHydrated && !justHydrated && !isResearchActive) {
      // If a research job just completed, save immediately (bypass debounce) so a rapid page
      // refresh doesn't cause the server's older state to win over the fresh results.
      const forceImmediate = forceSaveRef.current;
      forceSaveRef.current = false;
      const delay = forceImmediate ? 0 : SERVER_SAVE_DEBOUNCE_MS;
      const t = setTimeout(() => {
        setSaveStatus('saving');
        import('../utils/api').then(({ saveDashboardStateToServer }) => {
          saveDashboardStateToServer(state)
            .then(() => {
              lastFailedSaveRef.current = null;
              setSaveError('');
              setSaveStatus('saved');
              // Auto-clear the "Saved" badge after 3 s
              clearTimeout(savedClearTimerRef.current);
              savedClearTimerRef.current = setTimeout(() => setSaveStatus('idle'), 3000);
            })
            .catch((err) => {
              console.warn('[Dashboard] Server save failed:', err?.message || err);
              lastFailedSaveRef.current = state;
              setSaveError(err?.message || 'Save failed');
              setSaveStatus('error');
            });
        });
      }, delay);
      return () => clearTimeout(t);
    // else: isResearchActive — skip server save; wait for forceSaveRef completion trigger.
    }
  }, [userId, stateHydrated, activeProjectId, activeTabId, projects, tabs]);

  // Retry the last failed server save (triggered by the save-status indicator in the header).
  const handleRetrySave = useCallback(async () => {
    const payload = lastFailedSaveRef.current;
    if (!payload) return;
    setSaveStatus('saving');
    try {
      const { saveDashboardStateToServer } = await import('../utils/api');
      await saveDashboardStateToServer(payload);
      lastFailedSaveRef.current = null;
      setSaveError('');
      setSaveStatus('saved');
      clearTimeout(savedClearTimerRef.current);
      savedClearTimerRef.current = setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (err) {
      console.warn('[Dashboard] Retry save failed:', err?.message || err);
      setSaveError(err?.message || 'Save failed');
      setSaveStatus('error');
    }
  }, []);

  useEffect(() => {
    if (user?.queries_used != null) setApiCalls(user.queries_used);
  }, [user?.queries_used]);

  // Refetch profile on mount when logged in so manual DB updates (e.g. queries_limit) show in usage
  useEffect(() => {
    if (user?.id) refreshUserProfile();
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = async () => {
    if (!window.confirm('Are you sure you want to log out?')) return;
    try {
      await logout();
    } finally {
      navigate('/', { replace: true });
    }
  };

  // Switch to another project (saves current tabs into current project first)
  const switchProject = useCallback((projectId) => {
    if (projectId === activeProjectId) return;
    setClosedTabs([]);
    const serialized = tabs.map(tabToStorage);
    const nextProjects = projects.map(p =>
      p.id === activeProjectId ? { ...p, tabs: serialized } : p
    );
    const target = nextProjects.find(p => p.id === projectId) || nextProjects[0];
    const newTabs = (target?.tabs || []).map(storageToTab);
    const safeTabs = newTabs.length ? newTabs : [createDefaultTab()];
    const firstTab = safeTabs[0];
    setProjects(nextProjects);
    setActiveProjectId(target?.id ?? projectId);
    setTabs(safeTabs);
    setActiveTabId(firstTab.id);
  }, [projects, activeProjectId, tabs]);

  // Add new project and switch to it
  const addProject = useCallback(() => {
    setClosedTabs([]);
    const newTab = createDefaultTab();
    const newProject = {
      id: generateId(),
      name: `Project ${projects.length + 1}`,
      tabs: [tabToStorage(newTab)],
    };
    const serialized = tabs.map(tabToStorage);
    const nextProjects = projects.map(p =>
      p.id === activeProjectId ? { ...p, tabs: serialized } : p
    );
    setProjects([...nextProjects, newProject]);
    setActiveProjectId(newProject.id);
    setTabs([newTab]);
    setActiveTabId(newTab.id);
  }, [projects, activeProjectId, tabs]);

  // Rename project
  const renameProject = useCallback((projectId, newName) => {
    const trimmed = (newName || '').trim();
    if (!trimmed) return;
    setProjects(prev => prev.map(p => p.id === projectId ? { ...p, name: trimmed } : p));
  }, []);

  const [projectToDelete, setProjectToDelete] = useState(null);

  const deleteProject = useCallback((projectId) => {
    const serializedCurrentTabs = tabs.map(tabToStorage);
    const list = projects.filter(p => p.id !== projectId);
    const nextProjects = list.map(p =>
      p.id === activeProjectId ? { ...p, tabs: serializedCurrentTabs } : p
    );
    if (list.length === 0) {
      const defaultTab = createDefaultTab();
      const defaultProject = { id: generateId(), name: 'Project 1', tabs: [tabToStorage(defaultTab)] };
      setProjects([defaultProject]);
      setActiveProjectId(defaultProject.id);
      setTabs([defaultTab]);
      setActiveTabId(defaultTab.id);
    } else {
      const wasActive = activeProjectId === projectId;
      if (wasActive) {
        const nextActive = nextProjects[0].id;
        const target = nextProjects.find(p => p.id === nextActive) || nextProjects[0];
        const newTabs = (target.tabs || []).map(storageToTab);
        const safeTabs = newTabs.length ? newTabs : [createDefaultTab()];
        setProjects(nextProjects);
        setActiveProjectId(nextActive);
        setTabs(safeTabs);
        setActiveTabId(safeTabs[0].id);
      } else {
        // Keep current active tab state in memory when deleting a different project.
        setProjects(nextProjects);
      }
    }
    setProjectToDelete(null);
  }, [projects, activeProjectId, tabs]);

  const exportProject = useCallback((projectId) => {
    const proj = projects.find(p => p.id === projectId);
    if (!proj) return;
    // projects[id].tabs can be stale between project switches (the persistence effect saves to
    // localStorage/server via nextProjects but does NOT call setProjects to update React state).
    // For the active project, always serialize the current in-memory tabs so the export is fresh.
    const exportTabs = projectId === activeProjectId ? tabs.map(tabToStorage) : proj.tabs;
    const payload = { projectName: proj.name, exportedAt: new Date().toISOString(), tabs: exportTabs };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `project-${(proj.name || 'export').replace(/\s+/g, '-').toLowerCase()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [projects, activeProjectId, tabs]);

  const [editingProjectId, setEditingProjectId] = useState(null);
  const [editingName, setEditingName] = useState('');

  const { theme, darkMode, toggleTheme } = useTheme();

  const updateTabById = useCallback((tabId, updatesOrUpdater) => {
    setTabs(prev => prev.map(tab => {
      if (tab.id !== tabId) return tab;
      const updates = typeof updatesOrUpdater === 'function'
        ? updatesOrUpdater(tab)
        : updatesOrUpdater;
      return updates ? { ...tab, ...updates } : tab;
    }));
  }, []);

  // Update active tab
  const updateActiveTab = useCallback((updates) => {
    updateTabById(activeTabId, updates);
  }, [activeTabId, updateTabById]);

  // Add new tab
  const addTab = useCallback(() => {
    const newTab = createDefaultTab();
    setTabs(prev => [...prev, newTab]);
    setActiveTabId(newTab.id);
  }, []);

  const MAX_CLOSED_TABS = 10;
  const [closedTabs, setClosedTabs] = useState([]);

  // Close tab (store in session for undo)
  const closeTab = useCallback((tabId, e) => {
    e.stopPropagation();
    const tabToClose = tabs.find(t => t.id === tabId);
    if (tabs.length === 1) {
      if (tabToClose) {
        setClosedTabs(prev => [...prev.slice(-(MAX_CLOSED_TABS - 1)), tabToStorage(tabToClose)]);
      }
      const newTab = createDefaultTab();
      setTabs([newTab]);
      setActiveTabId(newTab.id);
      return;
    }
    if (tabToClose) {
      setClosedTabs(prev => [...prev.slice(-(MAX_CLOSED_TABS - 1)), tabToStorage(tabToClose)]);
    }
    const newTabs = tabs.filter(t => t.id !== tabId);
    setTabs(newTabs);
    if (tabId === activeTabId) {
      setActiveTabId(newTabs[0].id);
    }
  }, [tabs, activeTabId]);

  const undoCloseTab = useCallback(() => {
    if (closedTabs.length === 0) return;
    const last = closedTabs[closedTabs.length - 1];
    setClosedTabs(prev => prev.slice(0, -1));
    const restored = storageToTab(last);
    setTabs(prev => [...prev, restored]);
    setActiveTabId(restored.id);
  }, [closedTabs]);

  // Rename tab based on search (auto after research)
  const updateTabTitle = useCallback((product, location) => {
    if (product) {
      const title = location && location !== 'United States' 
        ? `${product} (${location})`
        : product;
      updateActiveTab({ title: title.slice(0, 30) + (title.length > 30 ? '...' : '') });
    }
  }, [updateActiveTab]);

  // User renames a tab manually
  const renameTab = useCallback((tabId, newTitle) => {
    const trimmed = (newTitle || '').trim();
    setTabs(prev => prev.map(t => t.id === tabId ? { ...t, title: trimmed || t.title } : t));
  }, []);

  const [editingTabId, setEditingTabId] = useState(null);
  const [editingTabTitle, setEditingTabTitle] = useState('');

  // Progress bar for initial research (driven by background job; fallback timer)
  const [researchProgress, setResearchProgress] = useState(0);
  const [researchProgressLabel, setResearchProgressLabel] = useState('');
  const researchProgressRef = useRef(null);
  const wasLoadingRef = useRef(false);

  // Sidebar state (projects vs glossary, collapse, resize)
  const [sidebarView, setSidebarView] = useState('projects');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(240);
  const sidebarRef = useRef(null);
  const sidebarIsResizingRef = useRef(false);
  const sidebarResizeStartXRef = useRef(0);
  const sidebarResizeStartWidthRef = useRef(0);
  const makeClientJobId = useCallback(() => {
    if (typeof window !== 'undefined' && window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `job_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }, []);

  // Market trend card interaction: which card is hovered / expanded (showing Analyze button)
  const [hoveredTrendIdx, setHoveredTrendIdx] = useState(null);
  const [expandedTrendIdx, setExpandedTrendIdx] = useState(null);

  // Glossary: persisted in localStorage
  const [glossary, setGlossary] = useState(() => {
    try { return JSON.parse(localStorage.getItem('market_tool_glossary') || '[]'); } catch { return []; }
  });
  const [expandedGlossaryIds, setExpandedGlossaryIds] = useState(() => new Set());
  const [glossarySearch, setGlossarySearch] = useState('');
  const [selectionPopup, setSelectionPopup] = useState(null); // { term, context, x, y }

  // Poll background research job while a tab is loading, so work survives refresh/navigation.
  // Use setInterval so the next poll is never "lost" – we don't schedule it from inside the async
  // callback (which could be cleared by effect cleanup). Interval runs until terminal state or cleanup.
  // Enable verbose logs: localStorage.setItem('DEBUG_RESEARCH_POLL', '1'); then reload or start a research.
  useEffect(() => {
    if (!activeTab?.loading || !activeTab?.researchJobId) return;

    const jobId = activeTab.researchJobId;
    let cancelled = false;
    let pollTimerId = null;
    let pollAbortController = null;
    let pollingStopped = false;
    let wakeRequested = false;
    let pollCount = 0;
    let pollInFlight = false;
    let consecutiveFailures = 0;
    let completeWithoutResultPolls = 0;
    const MAX_FAILURES_BEFORE_ERROR = Number.POSITIVE_INFINITY; // absolute watchdog is authoritative
    const MAX_COMPLETE_WITHOUT_RESULT_POLLS = 4;
    const POLL_MS = 2500;  // Reduced from 1200ms — still very responsive, halves server load
    // Safety net: if the backend job stays non-terminal for > 3 minutes, stop polling and
    // surface a friendly error. The backend expires jobs at RESEARCH_MAX_SECONDS (120s) but a
    // worker crash or DB write failure can leave a job in 'running' state indefinitely.
    const MAX_POLL_DURATION_MS = 180000;
    const pollStartMs = Date.now();

    logPoll('effect started', { jobId, activeTabId });

    const updateTabByJobId = (updates) => {
      setTabs(prev => {
        let matched = 0;
        const next = prev.map(tab => {
          if (tab.researchJobId === jobId) {
            matched += 1;
            return { ...tab, ...updates };
          }
          return tab;
        });
        logPoll('updateTabByJobId', { jobId, keys: Object.keys(updates || {}), matched });
        return next;
      });
    };

    const stopPolling = () => {
      pollingStopped = true;
      if (pollAbortController) {
        pollAbortController.abort();
        pollAbortController = null;
      }
      if (pollTimerId != null) {
        clearTimeout(pollTimerId);
        pollTimerId = null;
        logPoll('poll timer cleared');
      }
    };

    const pausePolling = () => {
      stopPolling();
      updateTabByJobId({
        error: 'We could not reconnect to this research job. Check the results without starting a new query.',
        loading: false,
        researchPollingState: 'paused',
        researchProgressLabel: 'Connection paused',
      });
    };

    const schedulePoll = (delay = POLL_MS) => {
      if (!cancelled && !pollingStopped) pollTimerId = setTimeout(poll, delay);
    };

    const poll = async () => {
      if (cancelled || pollInFlight) return;
      if (Date.now() - pollStartMs >= MAX_POLL_DURATION_MS) {
        pausePolling();
        return;
      }

      pollInFlight = true;
      pollCount += 1;
      const thisPoll = pollCount;
      logPoll('poll start', { poll: thisPoll, jobId, cancelled });

      try {
        const api = await import('../utils/api');
        const { getResearchJob } = api;
        pollAbortController = new AbortController();
        const res = await getResearchJob(jobId, { signal: pollAbortController.signal });

        if (cancelled) return;

        logPoll('poll response', {
          poll: thisPoll,
          success: res?.success,
          hasJob: !!res?.job,
          status: res?.job?.status,
          hasResult: !!res?.job?.result,
          cancelled,
        });

        // Frontend max poll duration guard: if non-terminal for > 3 minutes, give up gracefully.
        if (!cancelled && Date.now() - pollStartMs > MAX_POLL_DURATION_MS) {
          logPoll('max poll duration exceeded, showing timeout error');
          stopPolling();
          updateTabByJobId({
            error: 'Research is taking longer than expected. Please try again — your query limit has not been affected.',
            loading: false,
            researchPollingState: 'paused',
            researchJobId: jobId,
          });
          return;
        }

        // Bad or empty response – retry (interval will fire again).
        if (!res?.success || !res.job) {
          consecutiveFailures += 1;
          logPoll('bad/empty response, will retry', { consecutiveFailures, resKeys: res ? Object.keys(res) : 'no res' });
          if (consecutiveFailures === 2 || consecutiveFailures % 8 === 0) {
            updateTabByJobId({ researchProgressLabel: 'Reconnecting to research job...', researchPollingState: 'reconnecting' });
          }
          if (consecutiveFailures >= MAX_FAILURES_BEFORE_ERROR) {
            logPoll('max failures reached, showing error');
            stopPolling();
            updateTabByJobId({
              error: 'Connection interrupted. We will keep checking for results in the background.',
              loading: false,
              researchJobId: jobId,
              researchProgressLabel: 'Reconnecting…',
            });
          }
          return;
        }

        consecutiveFailures = 0;
        updateTabByJobId({ researchPollingState: 'active', error: null });
        const job = res.job;
        const terminal = job.status === 'complete' || job.status === 'error' || job.status === 'timeout';

        if (!terminal && cancelled) return;

        if (job.progress) {
          const { step, total, label } = job.progress;
          const pct = total > 0 ? Math.round((100 * step) / total) : 0;
          const progressLabel = label || `Step ${step} of ${total}`;
          setResearchProgress(pct);
          setResearchProgressLabel(progressLabel);
          if (!cancelled) updateTabByJobId({ researchProgress: pct, researchProgressLabel: progressLabel });
        }

        if (job.status === 'complete') {
          logPoll('terminal: complete', { hasResult: !!job.result, cancelled });
          if (job.result) {
            stopPolling();
            if (job.usage) {
              setApiCalls(job.usage.queries_used);
              updateUsage(job.usage.queries_used);
            }
            const rawTitle = job.product
              ? (job.location && job.location !== 'United States' ? `${job.product} (${job.location})` : job.product)
              : '';
            const title = rawTitle ? rawTitle.slice(0, 30) + (rawTitle.length > 30 ? '...' : '') : undefined;
            // Flag an immediate server save so a rapid page refresh doesn't revert to stale state.
            forceSaveRef.current = true;
            updateTabByJobId({
              data: job.result,
              error: null,
              loading: false,
              researchJobId: null,
              researchRequestId: null,
              researchPollingState: 'idle',
              researchProgress: 100,
              researchProgressLabel: 'Complete',
              previewCompetitors: null,
              ...(title && { title }),
            });
            logPoll('applied complete with result');
          } else {
            completeWithoutResultPolls += 1;
            logPoll('complete but no result yet', { completeWithoutResultPolls });
            if (completeWithoutResultPolls >= MAX_COMPLETE_WITHOUT_RESULT_POLLS) {
              stopPolling();
              updateTabByJobId({
                error: 'Research finished without a usable result. Please try again; this has been logged for diagnosis.',
                loading: false,
                researchJobId: null,
                researchRequestId: null,
                researchPollingState: 'idle',
                researchProgress: 95,
                researchProgressLabel: 'Result unavailable',
                previewCompetitors: null,
              });
            } else {
              updateTabByJobId({
                researchProgress: 95,
                researchProgressLabel: 'Validating results...',
              });
            }
          }
          return;
        }

        completeWithoutResultPolls = 0;

        if (job.status === 'error' || job.status === 'timeout') {
          logPoll('terminal: error/timeout', { status: job.status, error: job.error });
          stopPolling();
          updateTabByJobId({
            error: job.error || 'Research failed. Please try again.',
            loading: false,
            researchJobId: null,
            researchRequestId: null,
            researchPollingState: 'idle',
            previewCompetitors: null,
          });
          return;
        }

        logPoll('still running', { status: job.status });
      } catch (e) {
        logPoll('poll catch', { poll: thisPoll, error: e?.message, code: e?.code, cancelled });
        if (cancelled) return;
        if (e?.code === 'UNAUTHORIZED') {
          logPoll('UNAUTHORIZED — stopping poll, redirecting to login');
          stopPolling();
          updateTabByJobId({ loading: false, researchJobId: null, researchRequestId: null, researchPollingState: 'idle' });
          logout();
          navigate('/login');
          return;
        }
        consecutiveFailures += 1;
        if (consecutiveFailures === 2 || consecutiveFailures % 8 === 0) {
          updateTabByJobId({ researchProgressLabel: 'Reconnecting to research job...', researchPollingState: 'reconnecting' });
        }
        if (consecutiveFailures >= MAX_FAILURES_BEFORE_ERROR) {
          logPoll('max failures (catch) reached, showing error');
          stopPolling();
          updateTabByJobId({
            error: e.message || 'Connection interrupted. We will keep checking for results in the background.',
            loading: false,
            researchJobId: jobId,
            researchProgressLabel: 'Reconnecting…',
          });
          return;
        }
        // Interval will fire again for retry
      } finally {
        pollAbortController = null;
        pollInFlight = false;
        if (!cancelled && !pollingStopped) {
          if (wakeRequested) {
            wakeRequested = false;
            schedulePoll(0);
          } else {
            const delay = consecutiveFailures > 0 ? Math.min(10000, POLL_MS * (2 ** Math.min(consecutiveFailures, 2))) : POLL_MS;
            schedulePoll(delay);
          }
        }
      }
    };

    // First poll immediately; each completion schedules the next non-overlapping attempt.
    poll();
    const wakePoller = () => {
      if (pollInFlight) {
        wakeRequested = true;
      } else if (!pollingStopped) {
        clearTimeout(pollTimerId);
        schedulePoll(0);
      }
    };
    const handleVisible = () => {
      if (document.visibilityState === 'visible') wakePoller();
    };
    window.addEventListener('online', wakePoller);
    document.addEventListener('visibilitychange', handleVisible);
    logPoll('self-scheduling poller started', { POLL_MS });

    return () => {
      cancelled = true;
      stopPolling();
      window.removeEventListener('online', wakePoller);
      document.removeEventListener('visibilitychange', handleVisible);
      logPoll('effect cleanup', { jobId, totalPolls: pollCount });
    };
  }, [activeTab?.loading, activeTab?.researchJobId]);

  // Fallback reconciler: polls all loading jobs (across all tabs) so completion cannot be missed
  // if active-tab polling is interrupted by tab switches/re-renders.
  useEffect(() => {
    let cancelled = false;
    const RECONCILE_MS = 8000;  // Reduced from 4000ms — reconciler is a fallback, slower is fine

    const reconcile = async () => {
      const currentTabs = tabsRef.current || [];
      // Also pick up "orphaned" jobs: researchJobId set but loading=false and data not yet arrived.
      // This recovers polling if the primary poller stopped early (e.g. tab switch mid-research,
      // auth expiry, or a rapid page refresh before the primary poller re-attached).
      const jobIds = [...new Set(
        currentTabs
          .filter(t => t?.id !== activeTabIdRef.current && t?.researchJobId && (t?.loading || !t?.data) && t.researchPollingState !== 'paused')
          .map(t => t.researchJobId)
      )];
      if (!jobIds.length || cancelled) return;

      try {
        const api = await import('../utils/api');
        const { getResearchJob } = api;

        for (const jobId of jobIds) {
          if (cancelled) return;
          try {
            const res = await getResearchJob(jobId);
            const job = res?.job;
            if (!res?.success || !job) continue;

            if (job.status === 'complete') {
              const rawTitle = job.product
                ? (job.location && job.location !== 'United States' ? `${job.product} (${job.location})` : job.product)
                : '';
              const title = rawTitle ? rawTitle.slice(0, 30) + (rawTitle.length > 30 ? '...' : '') : undefined;
              if (job.result) {
                // Flag an immediate server save so a rapid page refresh doesn't revert to stale state.
                forceSaveRef.current = true;
                setTabs(prev => prev.map(tab =>
                  tab.researchJobId === jobId
                    ? {
                        ...tab,
                        data: job.result,
                        error: null,
                        loading: false,
                        researchJobId: null,
                        researchRequestId: null,
                        researchPollingState: 'idle',
                        researchProgress: 100,
                        researchProgressLabel: 'Complete',
                        ...(title && { title }),
                      }
                    : tab
                ));
                if (job.usage?.queries_used != null) {
                  setApiCalls(job.usage.queries_used);
                  updateUsage(job.usage.queries_used);
                }
                logPoll('reconciler applied complete', { jobId, hasResult: true });
              } else {
                setTabs(prev => prev.map(tab =>
                  tab.researchJobId === jobId
                    ? {
                        ...tab,
                        loading: false,
                        researchProgress: 95,
                        researchProgressLabel: 'Finalizing results…',
                      }
                    : tab
                ));
                logPoll('reconciler saw complete without result', { jobId });
              }
            } else if (job.status === 'error' || job.status === 'timeout') {
              setTabs(prev => prev.map(tab =>
                tab.researchJobId === jobId
                  ? {
                      ...tab,
                      error: job.error || 'Research failed. Please try again.',
                      loading: false,
                      researchJobId: null,
                      researchRequestId: null,
                      researchPollingState: 'idle',
                    }
                  : tab
              ));
              logPoll('reconciler applied terminal error', { jobId, status: job.status });
            } else if (job.status === 'running' || job.status === 'pending') {
              // Job is still in progress but the tab may be orphaned (loading=false after a poller
              // interruption, e.g. tab switch, rapid refresh, or error recovery). Restore loading so
              // the primary poller re-attaches on next render for the active tab, and the reconciler
              // keeps covering inactive tabs every 8 s until completion.
              const progressUpdates = {};
              if (job.progress) {
                const { step, total, label } = job.progress;
                progressUpdates.researchProgress = total > 0 ? Math.round((100 * step) / total) : 0;
                progressUpdates.researchProgressLabel = label || `Step ${step} of ${total}`;
              }
              setTabs(prev => prev.map(tab =>
                tab.researchJobId === jobId
                  ? { ...tab, loading: true, error: null, researchPollingState: 'active', ...progressUpdates }
                  : tab
              ));
            }
          } catch (recErr) {
            if (recErr?.code === 'UNAUTHORIZED') {
              // Session expired — clear loading for this tab and redirect to login.
              cancelled = true;
              setTabs(prev => prev.map(tab =>
                tab.researchJobId === jobId
                  ? { ...tab, loading: false, researchJobId: null, researchRequestId: null, researchPollingState: 'idle' }
                  : tab
              ));
              logout();
              navigate('/login');
              return;
            }
            // Best-effort fallback; active poller handles transient errors.
          }
        }
      } catch {
        // Best-effort fallback; active poller handles transient errors.
      }
    };

    const id = setInterval(reconcile, RECONCILE_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [updateUsage]);

  // Persist glossary to localStorage whenever it changes
  useEffect(() => {
    try { localStorage.setItem('market_tool_glossary', JSON.stringify(glossary)); } catch { /* ignore */ }
  }, [glossary]);

  // Sidebar resize: update DOM directly during drag for performance, commit to state on release
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!sidebarIsResizingRef.current) return;
      const delta = e.clientX - sidebarResizeStartXRef.current;
      const newWidth = Math.max(180, Math.min(440, sidebarResizeStartWidthRef.current + delta));
      if (sidebarRef.current) {
        sidebarRef.current.style.width = `${newWidth}px`;
        sidebarRef.current.style.minWidth = `${newWidth}px`;
      }
    };
    const handleMouseUp = () => {
      if (!sidebarIsResizingRef.current) return;
      sidebarIsResizingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      if (sidebarRef.current) {
        sidebarRef.current.style.transition = '';
        const w = parseInt(sidebarRef.current.style.width, 10);
        if (!isNaN(w)) setSidebarWidth(w);
      }
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  // Text-selection popup: show "Add to Glossary" near any highlighted text
  useEffect(() => {
    const handleMouseUp = () => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed || !selection.rangeCount) return;
      const text = selection.toString().trim();
      if (text.length < 2 || text.length > 120) return;
      const range = selection.getRangeAt(0);
      const context = (range.commonAncestorContainer?.textContent || '').slice(0, 300).trim();
      const rect = range.getBoundingClientRect();
      setSelectionPopup({ term: text, context, x: rect.left + rect.width / 2, y: rect.top });
    };
    const handleMouseDown = (e) => {
      if (!e.target.closest('[data-glossary-popup]')) setSelectionPopup(null);
    };
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, []);

  // Glossary helpers
  const addToGlossary = useCallback((term, context) => {
    const newId = generateId();
    setGlossary(prev => {
      if (prev.some(g => g.term.toLowerCase() === term.toLowerCase())) return prev;
      return [...prev, {
        id: newId,
        term,
        context: (context || '').slice(0, 200),
        definition: null,
        definitionLoading: true,
        note: '',
        addedAt: new Date().toISOString(),
      }];
    });
    setSidebarView('glossary');
    setSidebarCollapsed(false);
    // Fire-and-forget: fetch AI definition via lite model (doesn't count against query limit)
    import('../utils/api').then(({ defineGlossaryTerm }) => {
      defineGlossaryTerm(term, (context || '').slice(0, 300))
        .then(({ definition }) => {
          setGlossary(prev => prev.map(g => g.id === newId ? { ...g, definition, definitionLoading: false } : g));
        })
        .catch(() => {
          setGlossary(prev => prev.map(g => g.id === newId ? { ...g, definitionLoading: false } : g));
        });
    }).catch(() => {
      setGlossary(prev => prev.map(g => g.id === newId ? { ...g, definitionLoading: false } : g));
    });
  }, []);
  const toggleGlossaryExpanded = useCallback((id) => {
    setExpandedGlossaryIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const removeFromGlossary = useCallback((id) => {
    setGlossary(prev => prev.filter(g => g.id !== id));
    setExpandedGlossaryIds(prev => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);
  const updateGlossaryNote = useCallback((id, note) => setGlossary(prev => prev.map(g => g.id === id ? { ...g, note } : g)), []);

  useEffect(() => {
    const loading = !!activeTab?.loading;
    if (!loading) {
      if (researchProgressRef.current) {
        clearInterval(researchProgressRef.current);
        researchProgressRef.current = null;
      }
      if (wasLoadingRef.current) {
        wasLoadingRef.current = false;
        if (activeTab?.data && !activeTab?.error) {
          setResearchProgress(100);
          setResearchProgressLabel('Complete');
          updateActiveTab({ researchProgress: 100, researchProgressLabel: 'Complete', researchRequestId: null });
          const t = setTimeout(() => {
            setResearchProgress(0);
            setResearchProgressLabel('');
            updateActiveTab({ researchProgress: 0, researchProgressLabel: '' });
          }, 800);
          return () => clearTimeout(t);
        }
      }
      return;
    }
    wasLoadingRef.current = true;
    // Only reset bar when we just started (no jobId yet). When returning to page with jobId, keep restored progress.
    if (!activeTab?.researchJobId) {
      setResearchProgress(0);
      setResearchProgressLabel('Starting…');
      updateActiveTab({ researchProgress: 0, researchProgressLabel: 'Starting…' });
      // Animate the bar upward while waiting for the backend job to start.
      // Cap at 15% so real polling progress (33% for step 1/3) always looks like advancement.
      // Must update activeTab.researchProgress (not just local state) because the display
      // uses `activeTab.researchProgress ?? researchProgress` and 0 ?? x === 0 (not a fallthrough).
      let fakeProgress = 0;
      researchProgressRef.current = setInterval(() => {
        if (fakeProgress < 15) {
          fakeProgress = Math.min(15, fakeProgress + 3);
          setResearchProgress(fakeProgress);
          updateActiveTab({ researchProgress: fakeProgress });
        }
      }, 1500);
    }
    return () => {
      if (researchProgressRef.current) clearInterval(researchProgressRef.current);
    };
  }, [activeTab?.loading, activeTab?.researchJobId]);

  // Handle search using background research jobs so work continues across refreshes.
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!activeTab.product.trim() || activeTab.researchJobId) return;

    const tabId = activeTab.id;
    const product = activeTab.product;
    const location = activeTab.location;
    const yearsBack = activeTab.yearsBack;
    const requestId = activeTab.researchRequestId || makeClientJobId();

    updateTabById(tabId, {
      loading: true,
      error: null,
      data: null,
      selectedCompetitors: new Set(),
      // Attach immediately. The backend accepts this client id, so even if the
      // start response times out the poller/reconciler still has a handle.
      researchJobId: requestId,
      researchRequestId: requestId,
      researchPollingState: 'active',
      researchProgress: 0,
      researchProgressLabel: 'Starting...',
      previewCompetitors: null,
    });

    try {
      const api = await import('../utils/api');
      const { startResearchJob, getResearchPreview } = api;

      // Fire preview concurrently - non-blocking, populates skeleton UI in ~5s
      getResearchPreview(product, location).then((prev) => {
        if (prev?.competitors?.length) {
          updateTabById(tabId, { previewCompetitors: prev.competitors });
        }
      }).catch(() => {});

      const response = await startResearchJob(product, location, yearsBack, requestId);
      if (!response?.success || !response.job?.id) {
        throw new Error(response?.error || 'Failed to start research job');
      }
      updateTabById(tabId, {
        researchJobId: response.job.id,
        researchRequestId: response.job.id,
        researchProgressLabel: response.job.progress?.label || 'Research started',
      });
      logRefresh('research job attached to tab', {
        jobId: response.job.id,
        progress: response.job.progress || null,
      });
    } catch (err) {
      if (researchProgressRef.current) {
        clearInterval(researchProgressRef.current);
        researchProgressRef.current = null;
      }
      if (err.code === 'USAGE_LIMIT_EXCEEDED') {
        setApiCalls(err.queriesUsed || apiCalls);
        updateTabById(tabId, {
          error: `${err.message} You've used ${err.queriesUsed}/${err.queriesLimit} queries this month.`,
          loading: false,
          researchJobId: null,
          researchRequestId: null,
          researchPollingState: 'idle',
        });
      } else if (err.code === 'UNAUTHORIZED') {
        updateTabById(tabId, { loading: false, researchJobId: null, researchRequestId: null, researchPollingState: 'idle' });
        logout();
        navigate('/login');
      } else if (err.code === 'TIMEOUT') {
        // The start request may have timed out after the backend created the job.
        // Keep polling by client job id so results can appear without a refresh.
        try {
          const { getResearchJob } = await import('../utils/api');
          const status = await getResearchJob(requestId);
          const label = status?.job?.progress?.label || 'Research is still running...';
          updateTabById(tabId, {
            loading: true,
            error: null,
            researchJobId: requestId,
            researchRequestId: requestId,
            researchPollingState: 'active',
            researchProgressLabel: label,
          });
        } catch {
          updateTabById(tabId, {
            loading: true,
            error: null,
            researchJobId: requestId,
            researchRequestId: requestId,
            researchPollingState: 'reconnecting',
            researchProgressLabel: 'Still connecting to the research job...',
          });
        }
      } else {
        updateTabById(tabId, {
          error: err.message,
          loading: false,
          researchJobId: null,
          researchRequestId: requestId,
          researchPollingState: 'idle',
        });
      }
    }
  };

  // Cancel an in-flight research job: stop polling and clear loading state. The backend job
  // continues to completion (no cancel endpoint) but the result is silently discarded.
  const handleCancelResearch = useCallback(() => {
    if (researchProgressRef.current) {
      clearInterval(researchProgressRef.current);
      researchProgressRef.current = null;
    }
    updateActiveTab({
      loading: false,
      researchJobId: null,
      researchRequestId: null,
      researchPollingState: 'idle',
      researchProgress: 0,
      researchProgressLabel: '',
      error: null,
      previewCompetitors: null,
    });
  }, [updateActiveTab]);

  const handleCheckResearchResults = useCallback(() => {
    if (!activeTab?.researchJobId) return;
    updateActiveTab({
      loading: true,
      error: null,
      researchPollingState: 'active',
      researchProgressLabel: 'Checking research status...',
    });
  }, [activeTab?.researchJobId, updateActiveTab]);


  // Apply filters to competitors
  const filteredCompetitors = useMemo(() => {
    if (!activeTab?.data?.competitors) return [];

    let competitors = [...activeTab.data.competitors];

    // Filter by price range
    if (activeTab.filters.minPrice) {
      competitors = competitors.filter(c => 
        c.current_price >= parseFloat(activeTab.filters.minPrice)
      );
    }
    if (activeTab.filters.maxPrice) {
      competitors = competitors.filter(c => 
        c.current_price <= parseFloat(activeTab.filters.maxPrice)
      );
    }

    // Filter by sentiment
    if (activeTab.filters.minSentiment > 0) {
      competitors = competitors.filter(c => 
        (c.sentiment_score || 0) >= activeTab.filters.minSentiment
      );
    }

    // Filter to show only selected competitors
    if (activeTab.filters.showOnlySelected && activeTab.selectedCompetitors.size > 0) {
      competitors = competitors.filter(c => 
        activeTab.selectedCompetitors.has(c.name)
      );
    }

    // Stable sort - use original index as tiebreaker to prevent reordering
    competitors.sort((a, b) => {
      let comparison = 0;
      switch (activeTab.filters.sortBy) {
        case 'price':
          comparison = (a.current_price || 0) - (b.current_price || 0);
          break;
        case 'priceDesc':
          comparison = (b.current_price || 0) - (a.current_price || 0);
          break;
        case 'sentiment':
          comparison = (b.sentiment_score || 0) - (a.sentiment_score || 0);
          break;
        case 'sentimentAsc':
          comparison = (a.sentiment_score || 0) - (b.sentiment_score || 0);
          break;
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        default:
          comparison = 0;
      }
      // Use original index as tiebreaker for stable sorting
      if (comparison === 0) {
        const indexA = activeTab.data.competitors.findIndex(c => c.name === a.name);
        const indexB = activeTab.data.competitors.findIndex(c => c.name === b.name);
        return indexA - indexB;
      }
      return comparison;
    });

    return competitors;
  }, [activeTab?.data, activeTab?.filters, activeTab?.selectedCompetitors]);

  // Calculate average price for reference line
  const averagePrice = useMemo(() => {
    if (!filteredCompetitors.length) return null;
    const prices = filteredCompetitors
      .map(c => c.current_price)
      .filter(p => p !== null && p !== undefined);
    if (!prices.length) return null;
    return prices.reduce((a, b) => a + b, 0) / prices.length;
  }, [filteredCompetitors]);

  const toggleCompetitor = (competitor) => {
    const newSet = new Set(activeTab.selectedCompetitors);
    if (newSet.has(competitor.name)) {
      newSet.delete(competitor.name);
    } else {
      newSet.add(competitor.name);
    }
    updateActiveTab({ selectedCompetitors: newSet });
  };

  const removeCompetitor = useCallback((competitorName) => {
    setTabs(prev => prev.map(tab => {
      if (tab.id !== activeTabId) return tab;

      const competitors = Array.isArray(tab.data?.competitors) ? tab.data.competitors : [];
      if (!competitors.some(c => c.name === competitorName)) return tab;

      const nextSelectedCompetitors = new Set(tab.selectedCompetitors || []);
      nextSelectedCompetitors.delete(competitorName);

      const deepDives = { ...(tab.deepDives || {}) };
      delete deepDives[competitorName];
      const deepDiveNames = Object.keys(deepDives);

      const events = { ...(tab.events || {}) };
      delete events[competitorName];
      const eventNames = Object.keys(events);

      const nextData = {
        ...tab.data,
        competitors: competitors.filter(c => c.name !== competitorName),
      };

      return {
        ...tab,
        data: nextData,
        selectedCompetitors: nextSelectedCompetitors,
        showComparison: nextSelectedCompetitors.size > 0 ? tab.showComparison : false,
        deepDives,
        activeDeepDiveName: tab.activeDeepDiveName === competitorName
          ? (deepDiveNames[0] || null)
          : tab.activeDeepDiveName,
        events,
        activeEventName: tab.activeEventName === competitorName
          ? (eventNames[0] || null)
          : tab.activeEventName,
        seoAnalysis: null,
        seoAnalysisLoading: false,
        seoAnalysisError: null,
        seoWarning: null,
        marketShare: null,
        marketShareLoading: false,
        marketShareError: null,
        marketShareWarning: null,
        furtherReadingSources: null,
        furtherReadingLoading: false,
        furtherReadingError: null,
      };
    }));
  }, [activeTabId]);

  // Find additional competitors
  const handleExpandCompetitors = async () => {
    if (!activeTab.data || !activeTab.product) return;
    
    updateActiveTab({ expandingCompetitors: true, error: null });
    
    try {
      const { findAdditionalCompetitors } = await import('../utils/api');
      const existingNames = activeTab.data.competitors.map(c => c.name);
      const result = await findAdditionalCompetitors(
        activeTab.product, 
        activeTab.location, 
        existingNames
      );
      
      // Merge new competitors with existing
      const newData = {
        ...activeTab.data,
        competitors: [...activeTab.data.competitors, ...result.additional_competitors]
      };
      
      updateActiveTab({ 
        data: newData, 
        expandingCompetitors: false 
      });
    } catch (err) {
      updateActiveTab({ 
        error: 'Failed to find additional competitors: ' + err.message,
        expandingCompetitors: false 
      });
    }
  };

  // Search for specific competitor
  const handleSearchCompetitor = async (e) => {
    e.preventDefault();
    if (!activeTab.competitorSearchQuery.trim() || !activeTab.product) return;
    
    updateActiveTab({ searchingCompetitor: true, error: null });
    
    try {
      const { searchSpecificCompetitor } = await import('../utils/api');
      const result = await searchSpecificCompetitor(
        activeTab.product,
        activeTab.location,
        activeTab.competitorSearchQuery.trim()
      );
      
      if (result.data.found) {
        // Check if competitor already exists
        const exists = activeTab.data.competitors.some(
          c => c.name.toLowerCase() === result.data.name.toLowerCase()
        );
        
        if (exists) {
          updateActiveTab({ 
            error: `"${result.data.name}" is already in your competitor list`,
            searchingCompetitor: false,
            competitorSearchQuery: ''
          });
          return;
        }
        
        // Add new competitor
        const newData = {
          ...activeTab.data,
          competitors: [...activeTab.data.competitors, result.data]
        };
        
        updateActiveTab({ 
          data: newData, 
          searchingCompetitor: false,
          competitorSearchQuery: ''
        });
      } else {
        updateActiveTab({ 
          error: `Could not find competitor "${activeTab.competitorSearchQuery}"`,
          searchingCompetitor: false 
        });
      }
    } catch (err) {
      updateActiveTab({ 
        error: 'Failed to search competitor: ' + err.message,
        searchingCompetitor: false 
      });
    }
  };

  // Scroll the deep dive section into view (after a short delay so DOM has updated)
  const scrollToDeepDiveSection = useCallback(() => {
    setTimeout(() => {
      deepDiveSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, []);

  const scrollToTrendAnalysisSection = useCallback(() => {
    setTimeout(() => {
      trendAnalysisSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, []);

  // Deep dive: generate or open detailed analysis for a competitor (multiple deep dives kept in tabs)
  const handleDeepDive = async (competitor) => {
    const name = competitor.name;
    const product = activeTab.product;
    const location = activeTab.location;
    const existing = (activeTab.deepDives || {})[name];

    if (existing?.data) {
      updateActiveTab({ activeDeepDiveName: name });
      scrollToDeepDiveSection();
      return;
    }

    setTabs(prev => prev.map(tab =>
      tab.id !== activeTabId ? tab : {
        ...tab,
        deepDives: { ...(tab.deepDives || {}), [name]: { loading: true } },
        activeDeepDiveName: name,
      }
    ));
    scrollToDeepDiveSection();

    try {
      const { getCompetitorDeepDive } = await import('../utils/api');
      const result = await getCompetitorDeepDive(name, product, location, competitor);
      if (result.usage) {
        setApiCalls(result.usage.queries_used);
        updateUsage(result.queries_used);
      }
      setTabs(prev => prev.map(tab =>
        tab.id !== activeTabId ? tab : {
          ...tab,
          deepDives: { ...(tab.deepDives || {}), [name]: { data: result.data } },
        }
      ));
    } catch (err) {
      if (err.code === 'USAGE_LIMIT_EXCEEDED') {
        setApiCalls(err.queriesUsed ?? apiCalls);
      }
      setTabs(prev => prev.map(tab =>
        tab.id !== activeTabId ? tab : {
          ...tab,
          deepDives: { ...(tab.deepDives || {}), [name]: { error: err.message || 'Deep dive failed' } },
        }
      ));
    }
  };

  const closeDeepDive = (competitorName) => {
    setTabs(prev => prev.map(tab => {
      if (tab.id !== activeTabId) return tab;
      const deepDives = { ...(tab.deepDives || {}) };
      delete deepDives[competitorName];
      const names = Object.keys(deepDives);
      const activeDeepDiveName = tab.activeDeepDiveName === competitorName
        ? (names[0] || null)
        : tab.activeDeepDiveName;
      return { ...tab, deepDives, activeDeepDiveName };
    }));
  };

  // Market trend analysis: generate or open detailed analysis for a specific trend
  const handleTrendAnalysis = async (trend) => {
    const name = trend.trend;
    const existing = (activeTab.trendAnalyses || {})[name];

    if (existing?.data) {
      updateActiveTab({ activeTrendName: name });
      scrollToTrendAnalysisSection();
      return;
    }

    setTabs(prev => prev.map(tab =>
      tab.id !== activeTabId ? tab : {
        ...tab,
        trendAnalyses: { ...(tab.trendAnalyses || {}), [name]: { loading: true } },
        activeTrendName: name,
      }
    ));
    scrollToTrendAnalysisSection();

    try {
      const { getMarketTrendAnalysis } = await import('../utils/api');
      const result = await getMarketTrendAnalysis(
        name,
        trend.impact,
        activeTab.product,
        activeTab.location,
        activeTab.data?.market_trends || [],
        activeTab.yearsBack
      );
      if (result.usage) {
        setApiCalls(result.usage.queries_used);
        updateUsage(result.usage.queries_used);
      }
      setTabs(prev => prev.map(tab =>
        tab.id !== activeTabId ? tab : {
          ...tab,
          trendAnalyses: { ...(tab.trendAnalyses || {}), [name]: { data: result.data } },
        }
      ));
    } catch (err) {
      if (err.code === 'USAGE_LIMIT_EXCEEDED') {
        setApiCalls(err.queriesUsed ?? apiCalls);
      }
      setTabs(prev => prev.map(tab =>
        tab.id !== activeTabId ? tab : {
          ...tab,
          trendAnalyses: { ...(tab.trendAnalyses || {}), [name]: { error: err.message || 'Trend analysis failed. Please try again.' } },
        }
      ));
    }
  };

  const closeTrendAnalysis = (trendName) => {
    setTabs(prev => prev.map(tab => {
      if (tab.id !== activeTabId) return tab;
      const trendAnalyses = { ...(tab.trendAnalyses || {}) };
      delete trendAnalyses[trendName];
      const names = Object.keys(trendAnalyses);
      const activeTrendName = tab.activeTrendName === trendName
        ? (names[0] || null)
        : tab.activeTrendName;
      return { ...tab, trendAnalyses, activeTrendName };
    }));
  };

  // SEO Analysis handler
  const handleSEOAnalysis = async () => {
    if (!activeTab.data || !activeTab.product) return;
    
    updateActiveTab({ seoAnalysisLoading: true, seoAnalysisError: null });
    
    try {
      const { analyzeSEO } = await import('../utils/api');
      const result = await analyzeSEO(
        activeTab.product,
        activeTab.location,
        activeTab.data.competitors
      );
      
      if (result.usage) {
        setApiCalls(result.usage.queries_used);
        updateUsage(result.usage.queries_used);
      }
      
      updateActiveTab({
        seoAnalysis: result.data,
        seoAnalysisLoading: false,
        seoWarning: result.warning || null,
      });
    } catch (err) {
      if (err.code === 'USAGE_LIMIT_EXCEEDED') {
        setApiCalls(err.queriesUsed ?? apiCalls);
      }
      updateActiveTab({
        seoAnalysisError: err.message || 'SEO analysis failed',
        seoAnalysisLoading: false,
      });
    }
  };

  // Market Share Analysis handler
  const handleMarketShare = async () => {
    if (!activeTab.data || !activeTab.product) return;

    updateActiveTab({ marketShareLoading: true, marketShareError: null });

    try {
      const { analyzeMarketShare } = await import('../utils/api');
      const result = await analyzeMarketShare(
        activeTab.product,
        activeTab.location,
        activeTab.data.competitors,
        activeTab.yearsBack || 3
      );

      if (result.usage) {
        setApiCalls(result.usage.queries_used);
        updateUsage(result.usage.queries_used);
      }

      updateActiveTab({
        marketShare: result.data,
        marketShareLoading: false,
        marketShareWarning: result.warning || null,
      });
    } catch (err) {
      if (err.code === 'USAGE_LIMIT_EXCEEDED') {
        setApiCalls(err.queriesUsed ?? apiCalls);
      }
      updateActiveTab({
        marketShareError: err.message || 'Market share analysis failed',
        marketShareLoading: false,
      });
    }
  };

  // Further Reading handler: lazy-loaded after research completes, free (no usage charge)
  const handleFurtherReading = async () => {
    if (!activeTab.data || !activeTab.product) return;
    const isOldFormat = activeTab.furtherReadingSources && (activeTab.furtherReadingSources.search_links || activeTab.furtherReadingSources.industry_resources);
    if (activeTab.furtherReadingSources && !isOldFormat) return; // already loaded with new format
    updateActiveTab({ furtherReadingLoading: true, furtherReadingError: null });
    try {
      const { getResearchSources } = await import('../utils/api');
      const result = await getResearchSources(
        activeTab.product,
        activeTab.location,
        (activeTab.data.competitors || []).map(c => ({ name: c.name, website: c.website || null })),
        buildResearchSourcesContext(activeTab.data)
      );
      updateActiveTab({
        furtherReadingSources: result.data,
        furtherReadingLoading: false,
      });
    } catch (err) {
      updateActiveTab({
        furtherReadingError: err.message || 'Could not load further reading',
        furtherReadingLoading: false,
      });
    }
  };

  // Auto-trigger further reading when research completes
  useEffect(() => {
    if (!activeTab.data || activeTab.loading) return;
    // Detect old-format localStorage data (has search_links/industry_resources) — treat as needing refetch
    const isOldFormat = activeTab.furtherReadingSources && (activeTab.furtherReadingSources.search_links || activeTab.furtherReadingSources.industry_resources);
    if (activeTab.furtherReadingSources && !isOldFormat) return; // already loaded with new format
    if (activeTab.furtherReadingLoading || activeTab.furtherReadingError) return;
    handleFurtherReading();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab.data, activeTab.loading, activeTab.furtherReadingSources, activeTab.furtherReadingLoading, activeTab.furtherReadingError]);

  // Events Analysis handler (get recent headlines for a competitor)
  const handleEventsAnalysis = async (competitor) => {
    const name = competitor.name;
    const product = activeTab.product;
    const location = activeTab.location;
    const existing = (activeTab.events || {})[name];

    if (existing?.data) {
      updateActiveTab({ activeEventName: name });
      return;
    }

    setTabs(prev => prev.map(tab =>
      tab.id !== activeTabId ? tab : {
        ...tab,
        events: { ...(tab.events || {}), [name]: { loading: true } },
        activeEventName: name,
      }
    ));

    const monthsBack = activeTab.eventsMonthsBack ?? 1;
    try {
      const { getCompetitorEvents } = await import('../utils/api');
      const result = await getCompetitorEvents(name, product, location, competitor, undefined, monthsBack);
      if (result.usage) {
        setApiCalls(result.usage.queries_used);
        updateUsage(result.usage.queries_used);
      }
      setTabs(prev => prev.map(tab =>
        tab.id !== activeTabId ? tab : {
          ...tab,
          events: { ...(tab.events || {}), [name]: { data: result.data, warning: result.warning || null } },
        }
      ));
    } catch (err) {
      if (err.code === 'USAGE_LIMIT_EXCEEDED') {
        setApiCalls(err.queriesUsed ?? apiCalls);
      }
      setTabs(prev => prev.map(tab =>
        tab.id !== activeTabId ? tab : {
          ...tab,
          events: { ...(tab.events || {}), [name]: { error: err.message || 'Events analysis failed' } },
        }
      ));
    }
  };

  // Load more headlines for a competitor
  const handleLoadMoreEvents = async (competitorName) => {
    const product = activeTab.product;
    const location = activeTab.location;
    const competitor = activeTab.data?.competitors?.find(c => c.name === competitorName);
    const existingEntry = (activeTab.events || {})[competitorName];
    const existingHeadlines = existingEntry?.data?.headlines?.map(h => h.title) || [];

    setTabs(prev => prev.map(tab =>
      tab.id !== activeTabId ? tab : {
        ...tab,
        events: { ...(tab.events || {}), [competitorName]: { ...existingEntry, loadingMore: true } },
      }
    ));

    const monthsBack = activeTab.eventsMonthsBack ?? 1;
    try {
      const { getCompetitorEvents } = await import('../utils/api');
      const result = await getCompetitorEvents(competitorName, product, location, competitor, existingHeadlines, monthsBack);
      if (result.usage) {
        setApiCalls(result.usage.queries_used);
        updateUsage(result.usage.queries_used);
      }
      
      // Merge new headlines with existing
      const newHeadlines = result.data?.headlines || [];
      const mergedHeadlines = [...(existingEntry?.data?.headlines || []), ...newHeadlines];
      
      setTabs(prev => prev.map(tab =>
        tab.id !== activeTabId ? tab : {
          ...tab,
          events: { 
            ...(tab.events || {}), 
            [competitorName]: { 
              data: { ...result.data, headlines: mergedHeadlines },
              loadingMore: false 
            } 
          },
        }
      ));
    } catch (err) {
      setTabs(prev => prev.map(tab =>
        tab.id !== activeTabId ? tab : {
          ...tab,
          events: { 
            ...(tab.events || {}), 
            [competitorName]: { ...existingEntry, error: err.message || 'Failed to load more', loadingMore: false } 
          },
        }
      ));
    }
  };

  const closeEvents = (competitorName) => {
    setTabs(prev => prev.map(tab => {
      if (tab.id !== activeTabId) return tab;
      const events = { ...(tab.events || {}) };
      delete events[competitorName];
      const names = Object.keys(events);
      const activeEventName = tab.activeEventName === competitorName
        ? (names[0] || null)
        : tab.activeEventName;
      return { ...tab, events, activeEventName };
    }));
  };

  // Export functions
  const exportToJSON = () => {
    if (!activeTab.data) return;

    const competitors = activeTab.data?.competitors || [];
    const selectedNames = activeTab.selectedCompetitors instanceof Set
      ? Array.from(activeTab.selectedCompetitors)
      : Array.isArray(activeTab.selectedCompetitors)
        ? activeTab.selectedCompetitors
        : [];
    const selectedCompetitors = competitors.filter((c) => selectedNames.includes(c.name));
    const generatedDeepDives = Object.entries(activeTab.deepDives || {}).filter(
      ([, entry]) => entry?.data
    );
    const generatedTrendAnalyses = Object.entries(activeTab.trendAnalyses || {}).filter(
      ([, entry]) => entry?.data
    );

    const exportData = {
      exportDate: new Date().toISOString(),
      product: activeTab.product,
      location: activeTab.location,
      yearsBack: activeTab.yearsBack,
      ...activeTab.data,
      sentiment_analysis: competitors.length > 0 ? {
        competitor_count: competitors.length,
        average_sentiment: Number(
          (
            competitors.reduce((sum, c) => sum + (c.sentiment_score || 0), 0) / competitors.length
          ).toFixed(4)
        ),
      } : undefined,
      selected_competitor_comparison: selectedCompetitors.length > 0 ? {
        selected_names: selectedNames,
        selected_count: selectedCompetitors.length,
        competitors: selectedCompetitors,
      } : undefined,
      seo_landscape: activeTab.seoAnalysis || undefined,
      deep_dive_tabs: generatedDeepDives.length > 0
        ? Object.fromEntries(generatedDeepDives.map(([name, entry]) => [name, entry.data]))
        : undefined,
      trend_analysis_tabs: generatedTrendAnalyses.length > 0
        ? Object.fromEntries(generatedTrendAnalyses.map(([name, entry]) => [name, entry.data]))
        : undefined,
    };

    // Keep exports clean: only include sections that were actually generated.
    Object.keys(exportData).forEach((k) => exportData[k] === undefined && delete exportData[k]);

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `market-research-${activeTab.product.replace(/\s+/g, '-').toLowerCase()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Load dashboard (current tab) from exported JSON file. If file is a project export (has tabs), imports as new project instead.
  // Uses refs for activeTab/activeTabId so the callback is stable (deps=[]) and never captures stale closures.
  const importDashboardFromJSON = useCallback((e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const json = JSON.parse(reader.result);
        // ── Project export (has a tabs array) → add as new project ──────────────────
        const isProjectExport = Array.isArray(json.tabs) && json.tabs.length > 0;
        if (isProjectExport) {
          const projectName = json.projectName ?? 'Imported Project';
          const newTabs = json.tabs.map(t => storageToTab({ ...t, id: generateId() }));
          const newProject = {
            id: generateId(),
            name: String(projectName).trim() || 'Imported Project',
            tabs: newTabs.map(tabToStorage),
          };
          setProjects(prev => [...prev, newProject]);
          setActiveProjectId(newProject.id);
          setTabs(newTabs);
          setActiveTabId(newTabs[0].id);
          e.target.value = '';
          return;
        }
        // ── Single-tab research export → load into current tab ───────────────────────
        // Read current tab via refs so we never capture stale closure values.
        const curTab = activeTabRef.current;
        const curTabId = activeTabIdRef.current;
        const topLevel = ['exportDate', 'product', 'location', 'yearsBack', 'sentiment_analysis', 'selected_competitor_comparison', 'seo_landscape', 'deep_dive_tabs', 'trend_analysis_tabs'];
        const dataKeys = Object.keys(json).filter(k => !topLevel.includes(k));
        const data = dataKeys.length ? Object.fromEntries(dataKeys.map(k => [k, json[k]])) : null;
        if (!data?.competitors && !json.competitors) {
          console.warn('[importDashboardFromJSON] JSON has no competitors data — nothing to import.');
          e.target.value = '';
          return;
        }
        const product = json.product ?? '';
        const location = json.location ?? 'United States';
        const yearsBack = json.yearsBack ?? 5;
        const selectedNames = json.selected_competitor_comparison?.selected_names ?? [];
        const seoAnalysis = json.seo_landscape ?? null;
        const deepDiveEntries = json.deep_dive_tabs && typeof json.deep_dive_tabs === 'object'
          ? Object.entries(json.deep_dive_tabs).map(([name, d]) => [name, { data: d }])
          : [];
        const deepDives = Object.fromEntries(deepDiveEntries);
        const trendAnalysisEntries = json.trend_analysis_tabs && typeof json.trend_analysis_tabs === 'object'
          ? Object.entries(json.trend_analysis_tabs).map(([name, d]) => [name, { data: d }])
          : [];
        const trendAnalyses = Object.fromEntries(trendAnalysisEntries);
        const title = (product ? product + (location && location !== 'United States' ? ` (${location})` : '') : 'New Search').slice(0, 33);
        const loadedTab = {
          ...curTab,
          product,
          location,
          yearsBack,
          data: data || { competitors: json.competitors || [] },
          selectedCompetitors: new Set(selectedNames),
          seoAnalysis,
          deepDives,
          trendAnalyses,
          activeTrendName: Object.keys(trendAnalyses)[0] || null,
          title,
          loading: false,
          error: null,
        };
        setTabs(prev => prev.map(t => t.id === curTabId ? loadedTab : t));
      } catch (err) {
        console.warn('[importDashboardFromJSON] Failed to parse or apply JSON:', err);
      }
      e.target.value = '';
    };
    reader.readAsText(file);
  }, []); // stable — reads live values through refs

  // Load project from exported JSON file (adds new project and switches to it)
  const importProjectFromJSON = useCallback((e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const json = JSON.parse(reader.result);
        const rawTabs = Array.isArray(json.tabs) ? json.tabs : null;
        if (!rawTabs?.length) {
          console.warn('[importProjectFromJSON] JSON has no tabs array — nothing to import. Make sure you exported via "Export project".');
          e.target.value = '';
          return;
        }
        const projectName = json.projectName != null ? json.projectName : 'Imported Project';
        const newTabs = rawTabs.map(t => storageToTab({ ...t, id: generateId() }));
        const newProject = {
          id: generateId(),
          name: String(projectName).trim() || 'Imported Project',
          tabs: newTabs.map(tabToStorage),
        };
        setProjects(prev => [...prev, newProject]);
        setActiveProjectId(newProject.id);
        setTabs(newTabs);
        setActiveTabId(newTabs[0].id);
      } catch (err) {
        console.warn('[importProjectFromJSON] Failed to parse or apply JSON:', err);
      }
      e.target.value = '';
    };
    reader.readAsText(file);
  }, []);

  const downloadHtml = (htmlContent, filename) => {
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportCurrentTabToInteractiveHTML = () => {
    if (!activeTab.data) return;
    const htmlContent = generateTabHtml(activeTab);
    downloadHtml(htmlContent, `market-research-${safeFilename(activeTab.product || activeTab.title)}.html`);
    setHtmlExportMenuOpen(false);
  };

  const exportActiveProjectToInteractiveHTML = () => {
    const proj = projects.find(p => p.id === activeProjectId);
    if (!proj || !tabs.some(t => t?.data)) return;
    const htmlContent = generateProjectHtml({
      projectName: proj.name,
      tabs,
      activeTabId,
    });
    downloadHtml(htmlContent, `project-${safeFilename(proj.name || 'export')}.html`);
    setHtmlExportMenuOpen(false);
  };

  const activeProjectHasHtmlData = tabs.some(t => t?.data);

  // Show loading until we have valid state and (when logged in) until we've pulled latest from DB
  const waitingForHydration = userId && !stateHydrated;
  if (tabs.length === 0 || !activeTab || waitingForHydration) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: theme.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
        <div style={{ width: 40, height: 40, border: `3px solid ${theme.border}`, borderTop: `3px solid ${theme.primary}`, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        {waitingForHydration && (
          <p style={{ margin: 0, fontSize: 14, color: theme.textMuted }}>Loading your dashboard…</p>
        )}
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: theme.bg, display: 'flex', flexDirection: 'column', color: theme.text }}>
      {/* Keyframe for the saving spinner */}
      <style>{`@keyframes dashboard-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      {/* Fixed error banner */}
      {activeTab.error && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '12px 20px',
          backgroundColor: theme.errorBg,
          borderBottom: `1px solid ${theme.errorBorder}`,
          color: theme.errorText,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        }}>
          <AlertCircle size={18} style={{ flexShrink: 0 }} />
          <span style={{ flex: 1, fontSize: '14px' }}>{activeTab.error}</span>
          {activeTab.researchPollingState === 'paused' && activeTab.researchJobId && (
            <button
              type="button"
              onClick={handleCheckResearchResults}
              style={{
                padding: '6px 10px', borderRadius: '6px', border: `1px solid ${theme.errorText}`,
                background: 'transparent', color: theme.errorText, cursor: 'pointer', fontWeight: 600,
              }}
            >
              Check results
            </button>
          )}
          {activeTab.researchPollingState !== 'paused' && (
          <button
            type="button"
            onClick={() => updateActiveTab({ error: null })}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.errorText, display: 'flex', alignItems: 'center', padding: '2px' }}
          >
            <X size={18} />
          </button>
          )}
        </div>
      )}
      {/* Header */}
      <header style={{
        backgroundColor: theme.card,
        borderBottom: `1px solid ${theme.border}`,
        padding: '16px 0',
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
                <img src="/logo.png" alt="Market Scan" style={{ width: 128, height: 128, objectFit: 'contain', borderRadius: '12px', display: 'block', backgroundColor: 'transparent' }} />
              </div>
            </div>
            
            {/* Usage Bar, Theme Toggle & User Menu */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
              {/* Theme toggle */}
              <button
                type="button"
                onClick={toggleTheme}
                style={{
                  padding: '8px',
                  backgroundColor: theme.primaryLight,
                  color: theme.primary,
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {darkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>
              {/* Save-status indicator (Google-Docs-style) */}
              {saveStatus !== 'idle' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {saveStatus === 'saving' && (
                    <>
                      <RotateCw
                        size={14}
                        style={{
                          color: theme.textMuted,
                          animation: 'dashboard-spin 1s linear infinite',
                        }}
                      />
                      <span style={{ fontSize: '12px', color: theme.textMuted, whiteSpace: 'nowrap' }}>
                        Saving…
                      </span>
                    </>
                  )}
                  {saveStatus === 'saved' && (
                    <>
                      <CheckCircle2 size={14} style={{ color: '#16a34a' }} />
                      <span style={{ fontSize: '12px', color: '#16a34a', whiteSpace: 'nowrap' }}>
                        Saved
                      </span>
                    </>
                  )}
                  {saveStatus === 'error' && (
                    <button
                      onClick={handleRetrySave}
                      title={saveError || 'Save failed — click to retry'}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        padding: '4px 8px',
                        backgroundColor: theme.errorBg,
                        border: `1px solid ${theme.errorBorder}`,
                        borderRadius: '6px',
                        cursor: 'pointer',
                        color: theme.errorText,
                        fontSize: '12px',
                        fontWeight: '500',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <CloudOff size={13} />
                      Save failed — retry
                      <RotateCw size={11} />
                    </button>
                  )}
                </div>
              )}
              {/* Usage Indicator */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ margin: '0', fontSize: '12px', color: theme.textMuted }}>
                    Monthly Usage
                  </p>
                  <div style={{
                    width: '150px',
                    height: '6px',
                    backgroundColor: theme.border,
                    borderRadius: '3px',
                    marginTop: '4px',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      width: `${Math.min((apiCalls / apiLimit) * 100, 100)}%`,
                      height: '100%',
                      backgroundColor: apiCalls / apiLimit > 0.8 ? theme.usageError : apiCalls / apiLimit > 0.5 ? theme.usageWarn : theme.usageOk,
                      borderRadius: '3px',
                      transition: 'width 0.3s',
                    }} />
                  </div>
                  <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: theme.textMuted }}>
                    {apiCalls} / {apiLimit} queries
                  </p>
                </div>
              </div>
              
              {/* User Menu */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <button
                  type="button"
                  onClick={() => navigate('/profile')}
                  style={{
                    width: '36px',
                    height: '36px',
                    padding: 0,
                    backgroundColor: theme.primaryLight,
                    borderRadius: '50%',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                  title="Profile"
                >
                  <User size={18} color={theme.primary} />
                </button>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ margin: '0', fontSize: '14px', fontWeight: '600', color: theme.text }}>
                    {(user?.name || 'User').replace(/\b\w/g, c => c.toUpperCase())}
                  </p>
                  <p style={{ margin: '0', fontSize: '12px', color: theme.textMuted }}>
                    {user?.email}
                  </p>
                </div>
                <button
                  onClick={() => navigate('/profile')}
                  style={{
                    padding: '6px 12px',
                    fontSize: '13px',
                    fontWeight: '500',
                    color: theme.textMuted,
                    backgroundColor: 'transparent',
                    border: `1px solid ${theme.border}`,
                    borderRadius: '6px',
                    cursor: 'pointer',
                  }}
                >
                  Profile
                </button>
                <button
                  onClick={() => navigate('/dashboard/intel')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 12px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: theme.textMuted,
                    backgroundColor: 'transparent',
                    border: `1px solid ${theme.border}`,
                    borderRadius: '6px',
                    cursor: 'pointer',
                  }}
                  title="Competitive intel is under construction"
                >
                  <Radar size={15} />
                  Intel Soon
                </button>
                <button
                  onClick={handleLogout}
                  style={{
                    padding: '8px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: theme.textMuted,
                  }}
                  title="Log out"
                >
                  <LogOut size={18} />
                </button>
              </div>
            </div>
            
            {/* Export / Import buttons */}
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="file"
                ref={dashboardJsonInputRef}
                accept=".json,application/json"
                style={{ display: 'none' }}
                onChange={importDashboardFromJSON}
              />
              <button
                type="button"
                onClick={() => dashboardJsonInputRef.current?.click()}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 14px',
                  fontSize: '13px',
                  fontWeight: '500',
                  backgroundColor: theme.hoverBg,
                  color: theme.text,
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                }}
                title="Load dashboard from exported JSON"
              >
                <Upload size={16} />
                Import JSON
              </button>
              {activeTab.data && (
                <button
                  onClick={exportToJSON}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 14px',
                    fontSize: '13px',
                    fontWeight: '500',
                    backgroundColor: theme.hoverBg,
                    color: theme.text,
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                  }}
                  title="Export current tab as JSON"
                >
                  <FileJson size={16} />
                  JSON
                </button>
              )}
              {(activeTab.data || activeProjectHasHtmlData) && (
                <div ref={htmlExportMenuRef} style={{ position: 'relative' }}>
                  <button
                    type="button"
                    onClick={() => setHtmlExportMenuOpen(open => !open)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 14px',
                      fontSize: '13px',
                      fontWeight: '500',
                      backgroundColor: theme.primaryLight,
                      color: theme.primary,
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                    }}
                    title="Export interactive HTML report"
                    aria-haspopup="menu"
                    aria-expanded={htmlExportMenuOpen}
                  >
                    <FileCode size={16} />
                    HTML
                    <ChevronDown size={14} />
                  </button>
                  {htmlExportMenuOpen && (
                    <div
                      role="menu"
                      style={{
                        position: 'absolute',
                        right: 0,
                        top: 'calc(100% + 6px)',
                        minWidth: '220px',
                        padding: '6px',
                        backgroundColor: theme.card,
                        border: `1px solid ${theme.border}`,
                        borderRadius: '8px',
                        boxShadow: theme.shadow,
                        zIndex: 100,
                      }}
                    >
                      <button
                        type="button"
                        role="menuitem"
                        onClick={exportCurrentTabToInteractiveHTML}
                        disabled={!activeTab.data}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '9px 10px',
                          fontSize: '13px',
                          color: activeTab.data ? theme.text : theme.textMuted,
                          backgroundColor: 'transparent',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: activeTab.data ? 'pointer' : 'not-allowed',
                          textAlign: 'left',
                        }}
                      >
                        <FileCode size={15} />
                        Current tab as HTML
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={exportActiveProjectToInteractiveHTML}
                        disabled={!activeProjectHasHtmlData}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '9px 10px',
                          fontSize: '13px',
                          color: activeProjectHasHtmlData ? theme.text : theme.textMuted,
                          backgroundColor: 'transparent',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: activeProjectHasHtmlData ? 'pointer' : 'not-allowed',
                          textAlign: 'left',
                        }}
                      >
                        <FolderOpen size={15} />
                        Active project as HTML
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* Sidebar: sticky to viewport so Export/Import/New buttons follow the screen when scrolling */}
        {/* Expand button shown only when sidebar is hidden */}
        {sidebarCollapsed && (
          <button
            type="button"
            onClick={() => setSidebarCollapsed(false)}
            title="Show sidebar"
            style={{
              position: 'sticky', top: '50vh', alignSelf: 'flex-start', zIndex: 50,
              padding: '10px 3px', backgroundColor: theme.card,
              border: `1px solid ${theme.border}`, borderLeft: 'none',
              borderRadius: '0 6px 6px 0', cursor: 'pointer', color: theme.textMuted,
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              boxShadow: '2px 0 6px rgba(0,0,0,0.08)',
            }}
          >
            <ChevronRight size={16} />
          </button>
        )}

        <aside
          ref={sidebarRef}
          style={{
            position: 'sticky', top: 0, alignSelf: 'flex-start',
            width: sidebarCollapsed ? '0' : `${sidebarWidth}px`,
            minWidth: sidebarCollapsed ? '0' : `${sidebarWidth}px`,
            height: '100vh', flexShrink: 0, overflow: 'hidden',
            backgroundColor: theme.card, borderRight: `1px solid ${theme.border}`,
            display: 'flex', flexDirection: 'column',
            transition: 'width 0.15s ease, min-width 0.15s ease',
          }}
        >
          {/* Tab bar: Projects | Glossary + collapse button */}
          <div style={{ flexShrink: 0, display: 'flex', alignItems: 'stretch', borderBottom: `1px solid ${theme.border}` }}>
            {[
              { id: 'projects', label: 'Projects', icon: null },
              { id: 'glossary', label: `Glossary${glossary.length ? ` (${glossary.length})` : ''}`, icon: <BookOpen size={11} /> },
            ].map(({ id, label, icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setSidebarView(id)}
                style={{
                  flex: 1, padding: '8px 4px', fontSize: '10px', fontWeight: '600',
                  color: sidebarView === id ? theme.primary : theme.textMuted,
                  backgroundColor: 'transparent', border: 'none', cursor: 'pointer',
                  borderBottom: sidebarView === id ? `2px solid ${theme.primary}` : '2px solid transparent',
                  textTransform: 'uppercase', letterSpacing: '0.5px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                  whiteSpace: 'nowrap',
                }}
              >
                {icon}{label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setSidebarCollapsed(true)}
              title="Hide sidebar"
              style={{
                padding: '8px 6px', backgroundColor: 'transparent', border: 'none',
                cursor: 'pointer', color: theme.textMuted, display: 'flex', alignItems: 'center', flexShrink: 0,
              }}
            >
              <ChevronLeft size={15} />
            </button>
          </div>

          {/* ── Projects view ── */}
          {sidebarView === 'projects' && (
            <>
              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', padding: '6px 8px' }}>
                {projects.map((proj) => (
                  <div
                    key={proj.id}
                    style={{
                      marginBottom: '2px', borderRadius: '8px',
                      backgroundColor: proj.id === activeProjectId ? theme.primaryLight : 'transparent',
                      display: 'flex', alignItems: 'center', gap: '4px',
                    }}
                  >
                    {editingProjectId === proj.id ? (
                      <input
                        type="text"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onBlur={() => { renameProject(proj.id, editingName); setEditingProjectId(null); }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { renameProject(proj.id, editingName); setEditingProjectId(null); }
                          if (e.key === 'Escape') setEditingProjectId(null);
                        }}
                        autoFocus
                        style={{
                          flex: 1, minWidth: 0, boxSizing: 'border-box', padding: '8px 10px',
                          fontSize: '13px', border: `1px solid ${theme.primary}`, borderRadius: '8px',
                          outline: 'none', backgroundColor: theme.inputBg, color: theme.text,
                        }}
                      />
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => switchProject(proj.id)}
                          onDoubleClick={(e) => { e.preventDefault(); setEditingProjectId(proj.id); setEditingName(proj.name); }}
                          style={{
                            flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '6px',
                            padding: '8px 8px 8px 10px', fontSize: '13px',
                            fontWeight: proj.id === activeProjectId ? '600' : '500',
                            color: proj.id === activeProjectId ? theme.primary : theme.text,
                            backgroundColor: 'transparent', border: 'none', borderRadius: '8px',
                            cursor: 'pointer', textAlign: 'left',
                          }}
                        >
                          <FolderOpen size={16} style={{ flexShrink: 0 }} />
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{proj.name}</span>
                        </button>
                        <button type="button" onClick={(e) => { e.stopPropagation(); setEditingProjectId(proj.id); setEditingName(proj.name); }} style={{ padding: '4px', backgroundColor: 'transparent', border: 'none', borderRadius: '4px', cursor: 'pointer', color: theme.textMuted, flexShrink: 0 }} title="Rename project"><Pencil size={14} /></button>
                        <button type="button" onClick={(e) => { e.stopPropagation(); exportProject(proj.id); }} style={{ padding: '4px', backgroundColor: 'transparent', border: 'none', borderRadius: '4px', cursor: 'pointer', color: theme.textMuted, flexShrink: 0 }} title="Export project as JSON"><Download size={14} /></button>
                        <button type="button" onClick={(e) => { e.stopPropagation(); setProjectToDelete({ id: proj.id, name: proj.name }); }} style={{ padding: '4px', backgroundColor: 'transparent', border: 'none', borderRadius: '4px', cursor: 'pointer', color: theme.textMuted, flexShrink: 0 }} title="Delete project"><Trash2 size={14} /></button>
                      </>
                    )}
                  </div>
                ))}
              </div>
              <div style={{ flexShrink: 0, marginTop: 'auto', padding: '10px 8px 12px', borderTop: `1px solid ${theme.border}`, display: 'flex', flexDirection: 'column', gap: '8px', backgroundColor: theme.card }}>
                <input type="file" ref={projectJsonInputRef} accept=".json,application/json" style={{ display: 'none' }} onChange={importProjectFromJSON} />
                {[
                  { label: 'Export project', icon: <Download size={18} />, onClick: () => exportProject(activeProjectId), title: 'Export this project (all tabs) as JSON' },
                  { label: 'Import project', icon: <Upload size={18} />, onClick: () => projectJsonInputRef.current?.click(), title: 'Load project from exported JSON' },
                  { label: 'New project',    icon: <FolderPlus size={18} />, onClick: addProject, title: 'New project' },
                ].map(({ label, icon, onClick, title }) => (
                  <button key={label} type="button" onClick={onClick} title={title}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '10px 14px', fontSize: '13px', fontWeight: '500', color: theme.textMuted, backgroundColor: 'transparent', border: `1px dashed ${theme.border}`, borderRadius: '8px', cursor: 'pointer' }}
                  >{icon}{label}</button>
                ))}
              </div>
            </>
          )}

          {/* ── Glossary view ── */}
          {sidebarView === 'glossary' && (() => {
            const searchLower = glossarySearch.trim().toLowerCase();
            const filtered = searchLower
              ? glossary.filter(g => g.term.toLowerCase().includes(searchLower))
              : glossary;
            return (
              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                {/* Search bar */}
                {glossary.length > 0 && (
                  <div style={{ padding: '6px 8px', borderBottom: `1px solid ${theme.border}`, flexShrink: 0 }}>
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                      <Search size={11} style={{ position: 'absolute', left: '7px', color: theme.textMuted, pointerEvents: 'none', flexShrink: 0 }} />
                      <input
                        type="text"
                        placeholder="Search terms…"
                        value={glossarySearch}
                        onChange={(e) => setGlossarySearch(e.target.value)}
                        style={{
                          width: '100%', boxSizing: 'border-box',
                          padding: '4px 24px 4px 24px',
                          fontSize: '11px',
                          border: `1px solid ${theme.inputBorder}`,
                          borderRadius: '6px',
                          backgroundColor: theme.inputBg,
                          color: theme.text,
                          outline: 'none',
                          fontFamily: 'inherit',
                        }}
                      />
                      {glossarySearch && (
                        <button
                          onClick={() => setGlossarySearch('')}
                          style={{ position: 'absolute', right: '6px', background: 'none', border: 'none', cursor: 'pointer', color: theme.textMuted, padding: 0, display: 'flex' }}
                        >
                          <X size={11} />
                        </button>
                      )}
                    </div>
                    {searchLower && (
                      <p style={{ margin: '4px 0 0', fontSize: '10px', color: theme.textMuted }}>
                        {filtered.length} of {glossary.length} term{glossary.length !== 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                )}

                {/* Term list */}
                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '8px' }}>
                  {glossary.length === 0 ? (
                    <div style={{ textAlign: 'center', color: theme.textMuted, padding: '28px 12px' }}>
                      <BookOpen size={28} style={{ opacity: 0.35, display: 'block', margin: '0 auto 10px' }} />
                      <p style={{ margin: 0, fontSize: '12px', lineHeight: '1.5' }}>
                        Highlight any term on the dashboard to save its definition here.
                      </p>
                    </div>
                  ) : filtered.length === 0 ? (
                    <p style={{ margin: '20px 0', textAlign: 'center', fontSize: '11px', color: theme.textMuted }}>
                      No terms match "{glossarySearch}"
                    </p>
                  ) : (
                    filtered.map(g => (
                      <div key={g.id} style={{ marginBottom: '6px', borderRadius: '8px', border: `1px solid ${theme.border}`, overflow: 'hidden' }}>
                        {/* Header row */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 8px', backgroundColor: theme.hoverBg }}>
                          <button
                            type="button"
                            onClick={() => toggleGlossaryExpanded(g.id)}
                            style={{ flex: 1, background: 'none', border: 'none', cursor: 'pointer', color: theme.text, textAlign: 'left', padding: 0, fontSize: '12px', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            title={g.term}
                          >
                            {g.term}
                          </button>
                          {/* Loading dot when definition is being fetched */}
                          {g.definitionLoading && (
                            <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: theme.primary, opacity: 0.7, flexShrink: 0, animation: 'spin 1s linear infinite' }} title="Fetching definition…" />
                          )}
                          <button
                            type="button"
                            onClick={() => removeFromGlossary(g.id)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.textMuted, padding: '2px', display: 'flex', flexShrink: 0 }}
                            title="Remove"
                          >
                            <X size={12} />
                          </button>
                        </div>

                        {/* Expanded detail */}
                        {expandedGlossaryIds.has(g.id) && (
                          <div style={{ padding: '10px', backgroundColor: theme.card }}>
                            {/* Context snippet (italic, muted) */}
                            {g.context && (
                              <p style={{ margin: '0 0 8px 0', fontSize: '11px', color: theme.textMuted, fontStyle: 'italic', lineHeight: '1.55', borderLeft: `3px solid ${theme.border}`, paddingLeft: '8px' }}>
                                {g.context.slice(0, 160)}{g.context.length > 160 ? '…' : ''}
                              </p>
                            )}

                            {/* AI-generated definition */}
                            {g.definitionLoading ? (
                              <div style={{ margin: '0 0 8px 0', padding: '8px', borderRadius: '6px', backgroundColor: theme.hoverBg, border: `1px solid ${theme.border}` }}>
                                <p style={{ margin: 0, fontSize: '11px', color: theme.textMuted, fontStyle: 'italic' }}>
                                  Generating definition…
                                </p>
                              </div>
                            ) : g.definition ? (
                              <div style={{ margin: '0 0 8px 0', padding: '8px', borderRadius: '6px', backgroundColor: theme.accentLight || theme.hoverBg, border: `1px solid ${theme.accent || theme.border}` }}>
                                <p style={{ margin: '0 0 4px 0', fontSize: '10px', fontWeight: '600', color: theme.primary, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                                  AI Definition
                                </p>
                                <p style={{ margin: 0, fontSize: '11px', color: theme.text, lineHeight: '1.6' }}>
                                  {g.definition}
                                </p>
                              </div>
                            ) : null}

                            {/* User note */}
                            <textarea
                              placeholder="Add your own note…"
                              value={g.note}
                              onChange={(e) => updateGlossaryNote(g.id, e.target.value)}
                              style={{
                                width: '100%', boxSizing: 'border-box', padding: '6px 8px',
                                fontSize: '11px', border: `1px solid ${theme.inputBorder}`,
                                borderRadius: '6px', resize: 'vertical', minHeight: '56px',
                                backgroundColor: theme.inputBg, color: theme.text, outline: 'none', fontFamily: 'inherit',
                              }}
                            />
                            <p style={{ margin: '4px 0 0', fontSize: '10px', color: theme.textMuted }}>
                              Added {new Date(g.addedAt).toLocaleDateString()}
                            </p>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })()}

          {/* Drag-to-resize handle on the right edge */}
          <div
            onMouseDown={(e) => {
              sidebarIsResizingRef.current = true;
              sidebarResizeStartXRef.current = e.clientX;
              sidebarResizeStartWidthRef.current = sidebarWidth;
              document.body.style.cursor = 'col-resize';
              document.body.style.userSelect = 'none';
              if (sidebarRef.current) sidebarRef.current.style.transition = 'none';
              e.preventDefault();
            }}
            style={{
              position: 'absolute', top: 0, right: 0, width: '4px', height: '100%',
              cursor: 'col-resize', backgroundColor: 'transparent', zIndex: 10,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = theme.primary + '50'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            title="Drag to resize"
          />
        </aside>

        {/* Delete project confirmation dialog */}
        {projectToDelete && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
            }}
            onClick={() => setProjectToDelete(null)}
          >
            <div
              style={{
                backgroundColor: theme.card,
                borderRadius: '12px',
                padding: '24px',
                maxWidth: '360px',
                width: '90%',
                boxShadow: theme.shadow,
                border: `1px solid ${theme.border}`,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: theme.text }}>
                Delete project?
              </h3>
              <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: theme.textMuted, lineHeight: 1.5 }}>
                Are you sure you want to delete &quot;{projectToDelete.name}&quot;? This cannot be undone.
              </p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setProjectToDelete(null)}
                  style={{
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: theme.text,
                    backgroundColor: theme.hoverBg,
                    border: `1px solid ${theme.border}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => deleteProject(projectToDelete.id)}
                  style={{
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: theme.primaryContrast,
                    backgroundColor: theme.usageError,
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main: Tab bar + content */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      {/* Tab Bar */}
      <div style={{
        backgroundColor: theme.tabBarBg,
        borderBottom: `1px solid ${theme.border}`,
        padding: '8px 24px 0',
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 24px', display: 'flex', gap: '4px', overflowX: 'auto' }}>
          {tabs.map(tab => (
            <div
              key={tab.id}
              onClick={() => editingTabId !== tab.id && setActiveTabId(tab.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 10px 10px 12px',
                fontSize: '14px',
                fontWeight: '500',
                backgroundColor: tab.id === activeTabId ? theme.tabActiveBg : 'transparent',
                color: tab.id === activeTabId ? theme.text : theme.tabInactiveText,
                borderTop: tab.id === activeTabId ? `2px solid ${theme.tabActiveBorder}` : '2px solid transparent',
                borderLeft: `1px solid ${theme.border}`,
                borderRight: `1px solid ${theme.border}`,
                borderTopLeftRadius: '6px',
                borderTopRightRadius: '6px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s',
              }}
            >
              {tab.data && <span style={{ color: theme.accent, flexShrink: 0 }}>●</span>}
              {editingTabId === tab.id ? (
                <input
                  type="text"
                  value={editingTabTitle}
                  onChange={(e) => setEditingTabTitle(e.target.value)}
                  onBlur={() => {
                    renameTab(tab.id, editingTabTitle);
                    setEditingTabId(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      renameTab(tab.id, editingTabTitle);
                      setEditingTabId(null);
                    }
                    if (e.key === 'Escape') setEditingTabId(null);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                  style={{
                    width: '100%',
                    minWidth: '60px',
                    maxWidth: '160px',
                    padding: '2px 6px',
                    fontSize: '13px',
                    border: `1px solid ${theme.primary}`,
                    borderRadius: '4px',
                    outline: 'none',
                    backgroundColor: theme.inputBg,
                    color: theme.text,
                    boxSizing: 'border-box',
                  }}
                />
              ) : (
                <>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{tab.title}</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingTabId(tab.id);
                      setEditingTabTitle(tab.title || '');
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '18px',
                      height: '18px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      color: theme.textMuted,
                      flexShrink: 0,
                    }}
                    title="Rename tab"
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = theme.hoverBg; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    <Pencil size={12} />
                  </button>
                </>
              )}
              <button
                onClick={(e) => closeTab(tab.id, e)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '18px',
                  height: '18px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  color: theme.textMuted,
                  flexShrink: 0,
                }}
                title="Close tab"
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = theme.hoverBg; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <X size={14} />
              </button>
            </div>
          ))}
          
          {/* Add Tab Button */}
          <button
            onClick={addTab}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '10px 14px',
              backgroundColor: 'transparent',
              border: `1px dashed ${theme.border}`,
              borderRadius: '6px',
              cursor: 'pointer',
              color: theme.textMuted,
            }}
            title="New search"
          >
            <Plus size={18} />
          </button>

          {/* Undo close tab - re-open last closed tab in this session */}
          {closedTabs.length > 0 && (
            <button
              type="button"
              onClick={undoCloseTab}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 14px',
                fontSize: '13px',
                fontWeight: '500',
                backgroundColor: theme.primaryLight,
                color: theme.primary,
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
              }}
              title={`Re-open closed tab${closedTabs.length > 1 ? ` (${closedTabs.length} closed)` : ''}`}
            >
              <Undo2 size={16} />
              Undo
            </button>
          )}
        </div>
      </div>

      <main style={{ maxWidth: '1400px', margin: '0 auto', padding: '24px' }}>
        {/* Search Form */}
        <div style={{
          backgroundColor: theme.card,
          borderRadius: '12px',
          padding: '24px',
          boxShadow: theme.shadow,
          marginBottom: '24px',
        }}>
          <form onSubmit={handleSearch} style={{ minWidth: 0 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.6fr) 200px 120px auto', gap: '16px', alignItems: 'end', minWidth: 0 }}>
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, justifyContent: 'flex-end' }}>
                <label htmlFor="product" style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: theme.text,
                  marginBottom: '6px',
                  height: '20px',
                  flexShrink: 0,
                }}>
                  Product or Service
                </label>
                <input
                  id="product"
                  type="text"
                  placeholder="e.g., CRM software, wireless earbuds..."
                  value={activeTab.product}
                  onChange={(e) => updateActiveTab({ product: e.target.value })}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    minWidth: 0,
                    padding: '12px 16px',
                    fontSize: '15px',
                    border: `1px solid ${theme.inputBorder}`,
                    borderRadius: '8px',
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                    height: '46px',
                    backgroundColor: theme.inputBg,
                    color: theme.text,
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = theme.primary;
                    e.target.style.boxShadow = `0 0 0 3px ${theme.focusRing}`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = theme.inputBorder;
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <div style={{ height: '20px', flexShrink: 0 }} aria-hidden="true" />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, justifyContent: 'flex-end' }}>
                <LocationSelector 
                  value={activeTab.location} 
                  onChange={(loc) => updateActiveTab({ location: loc })} 
                  theme={theme}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, justifyContent: 'flex-end' }}>
                <label htmlFor="yearsBack" style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: theme.text,
                  marginBottom: '6px',
                  height: '20px',
                  flexShrink: 0,
                }}>
                  History
                </label>
                <select
                  id="yearsBack"
                  value={activeTab.yearsBack}
                  onChange={(e) => updateActiveTab({ yearsBack: parseInt(e.target.value) })}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    minWidth: 0,
                    padding: '12px 10px',
                    fontSize: '15px',
                    border: `1px solid ${theme.inputBorder}`,
                    borderRadius: '8px',
                    backgroundColor: theme.inputBg,
                    color: theme.text,
                    height: '46px',
                    cursor: 'pointer',
                  }}
                >
                  <option value={3}>3 years</option>
                  <option value={5}>5 years</option>
                  <option value={7}>7 years</option>
                  <option value={10}>10 years</option>
                </select>
                <div style={{ height: '20px', flexShrink: 0 }} aria-hidden="true" />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', minWidth: 0 }}>
                <button
                  type="submit"
                  disabled={activeTab.loading || !!activeTab.researchJobId || !activeTab.product.trim()}
                  style={{
                    padding: '12px 24px',
                    fontSize: '15px',
                    fontWeight: '600',
                    backgroundColor: activeTab.loading || activeTab.researchJobId || !activeTab.product.trim() ? 'transparent' : theme.primary,
                    color: activeTab.loading || activeTab.researchJobId || !activeTab.product.trim() ? theme.primary : theme.primaryContrast,
                    border: activeTab.loading || activeTab.researchJobId || !activeTab.product.trim() ? `1px solid ${theme.primary}` : 'none',
                    borderRadius: '8px',
                    cursor: activeTab.loading || activeTab.researchJobId || !activeTab.product.trim() ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'background-color 0.2s',
                    height: '46px',
                    boxSizing: 'border-box',
                    flexShrink: 0,
                  }}
                >
                {activeTab.loading ? (
                  <>
                    <RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} />
                    Researching...
                  </>
                ) : activeTab.researchJobId ? (
                  <>
                    <RefreshCw size={18} />
                    Research pending
                  </>
                ) : (
                  <>
                    <Search size={18} />
                    Research
                  </>
                )}
                </button>
                <div style={{ height: '20px', flexShrink: 0 }} aria-hidden="true" />
              </div>
            </div>
          </form>

          {showInitialResearchNotice && (
            <div
              style={{
                marginTop: '12px',
                padding: '10px 12px',
                borderRadius: '8px',
                border: `1px solid ${theme.primary}`,
                backgroundColor: theme.primaryLight,
                color: theme.text,
                fontSize: '13px',
                lineHeight: 1.45,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px', gap: 8 }}>
                <div>Initial research can take up to a minute while we gather and process market data.</div>
                <button
                  type="button"
                  onClick={handleCancelResearch}
                  title="Cancel research"
                  style={{
                    flexShrink: 0,
                    padding: '2px 10px',
                    fontSize: '12px',
                    fontWeight: 500,
                    color: theme.textMuted,
                    backgroundColor: 'transparent',
                    border: `1px solid ${theme.border}`,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Cancel
                </button>
              </div>
              {(activeTab.researchProgressLabel ?? researchProgressLabel) && (
                <div style={{ marginBottom: '6px', fontWeight: '500', color: theme.primary }}>
                  {activeTab.researchProgressLabel ?? researchProgressLabel}
                </div>
              )}
              <div
                style={{
                  height: '8px',
                  borderRadius: '4px',
                  backgroundColor: theme.border,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${activeTab.researchProgress ?? researchProgress}%`,
                    backgroundColor: theme.primary,
                    borderRadius: '4px',
                    transition: 'width 0.3s ease',
                  }}
                />
              </div>
              <div style={{ marginTop: '4px', fontSize: '12px', color: theme.textMuted }}>
                {activeTab.researchProgress ?? researchProgress}% complete
              </div>
            </div>
          )}

        </div>

        {/* Skeleton loading UI — shown while research job is running and no data yet */}
        {showInitialResearchNotice && (
          <>
            <style>{`
              @keyframes shimmer {
                0% { background-position: -600px 0; }
                100% { background-position: 600px 0; }
              }
              .sk-shimmer {
                background: linear-gradient(90deg, var(--sk-base) 25%, var(--sk-shine) 50%, var(--sk-base) 75%);
                background-size: 600px 100%;
                animation: shimmer 1.6s infinite linear;
                border-radius: 6px;
              }
            `}</style>

            {/* Market summary skeleton */}
            <div style={{
              '--sk-base': theme.border,
              '--sk-shine': theme.card,
              border: `1px solid ${theme.border}`,
              borderRadius: '12px',
              padding: '16px 20px',
              marginBottom: '24px',
              backgroundColor: theme.card,
            }}>
              <div className="sk-shimmer" style={{ height: 14, width: '30%', marginBottom: 10 }} />
              <div className="sk-shimmer" style={{ height: 12, width: '90%', marginBottom: 6 }} />
              <div className="sk-shimmer" style={{ height: 12, width: '75%', marginBottom: 6 }} />
              <div className="sk-shimmer" style={{ height: 12, width: '55%' }} />
            </div>

            {/* Competitor skeleton cards */}
            {(() => {
              const previews = activeTab.previewCompetitors;
              const slots = previews?.length
                ? previews
                : [null, null, null, null];
              return (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                  {slots.map((p, i) => (
                    <div key={i} style={{
                      '--sk-base': theme.border,
                      '--sk-shine': theme.card,
                      backgroundColor: theme.card,
                      border: `1px solid ${theme.border}`,
                      borderRadius: '12px',
                      padding: '16px',
                      boxShadow: theme.shadow,
                    }}>
                      {/* Name row */}
                      {p?.name ? (
                        <div style={{ fontWeight: 600, fontSize: 14, color: theme.text, marginBottom: 8 }}>
                          {p.name}
                        </div>
                      ) : (
                        <div className="sk-shimmer" style={{ height: 14, width: '60%', marginBottom: 8 }} />
                      )}
                      {/* Price row */}
                      {p?.current_price != null ? (
                        <div style={{ fontSize: 13, color: theme.primary, fontWeight: 500, marginBottom: 10 }}>
                          {p.currency || ''} {typeof p.current_price === 'number' ? p.current_price.toFixed(2) : p.current_price}
                        </div>
                      ) : (
                        <div className="sk-shimmer" style={{ height: 12, width: '30%', marginBottom: 10 }} />
                      )}
                      {/* Tagline */}
                      {p?.tagline ? (
                        <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 12, fontStyle: 'italic' }}>
                          {p.tagline}
                        </div>
                      ) : (
                        <div className="sk-shimmer" style={{ height: 12, width: '80%', marginBottom: 12 }} />
                      )}
                      {/* Shimmer placeholder lines for sentiment / review / features */}
                      <div className="sk-shimmer" style={{ height: 10, width: '100%', marginBottom: 6 }} />
                      <div className="sk-shimmer" style={{ height: 10, width: '85%', marginBottom: 6 }} />
                      <div className="sk-shimmer" style={{ height: 10, width: '70%' }} />
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Market trends skeleton */}
            <div style={{
              '--sk-base': theme.border,
              '--sk-shine': theme.card,
              backgroundColor: theme.card,
              border: `1px solid ${theme.border}`,
              borderRadius: '12px',
              padding: '16px 20px',
              marginBottom: '24px',
              boxShadow: theme.shadow,
            }}>
              <div className="sk-shimmer" style={{ height: 14, width: '25%', marginBottom: 14 }} />
              {[0, 1, 2, 3].map((i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <div className="sk-shimmer" style={{ height: 10, width: '45%' }} />
                  <div className="sk-shimmer" style={{ height: 10, flex: 1 }} />
                </div>
              ))}
            </div>
          </>
        )}

        {/* Results */}
        {activeTab.data && (
          <>
            {/* Market Summary */}
            {activeTab.data.market_summary && (
              <div style={{
                backgroundColor: theme.primaryLight,
                border: `1px solid ${theme.primary}`,
                borderRadius: '12px',
                padding: '16px 20px',
                marginBottom: '24px',
              }}>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: theme.primary }}>
                  Market Summary
                </h3>
                <p style={{ margin: 0, fontSize: '14px', color: theme.text, lineHeight: '1.6' }}>
                  {activeTab.data.market_summary}
                </p>
              </div>
            )}

            {/* Filters */}
            <FilterPanel
              competitors={activeTab.data.competitors}
              filters={activeTab.filters}
              onFilterChange={(filters) => updateActiveTab({ filters })}
              selectedCount={activeTab.selectedCompetitors.size}
              theme={theme}
            />

            {/* Main Content Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
              {/* Price Chart */}
              <div style={{ gridColumn: 'span 2' }}>
                <PriceChart
                  competitors={filteredCompetitors}
                />
              </div>

              {/* Sentiment Comparison */}
              <SentimentComparison competitors={filteredCompetitors} />

              {/* Competitor Grid */}
              <div>
                <div style={{
                  backgroundColor: theme.card,
                  borderRadius: '12px',
                  padding: '20px',
                  boxShadow: theme.shadow,
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px',
                    flexWrap: 'wrap',
                    gap: '8px',
                  }}>
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: theme.text }}>
                      Competitors ({filteredCompetitors.length})
                    </h3>
                    
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      {/* Sort Dropdown for Competitors */}
                      <select
                        value={activeTab.filters.sortBy}
                        onChange={(e) => updateActiveTab({ 
                          filters: { ...activeTab.filters, sortBy: e.target.value }
                        })}
                        style={{
                          padding: '6px 10px',
                          fontSize: '12px',
                          border: `1px solid ${theme.inputBorder}`,
                          borderRadius: '6px',
                          backgroundColor: theme.inputBg,
                          color: theme.text,
                          cursor: 'pointer',
                        }}
                      >
                        <option value="price">Price: Low→High</option>
                        <option value="priceDesc">Price: High→Low</option>
                        <option value="sentiment">Sentiment: High→Low</option>
                        <option value="sentimentAsc">Sentiment: Low→High</option>
                        <option value="name">Name: A-Z</option>
                      </select>

                      {activeTab.selectedCompetitors.size > 0 && (
                        <>
                          <button
                            onClick={() => updateActiveTab({ showComparison: true })}
                            style={{
                              fontSize: '12px',
                              padding: '4px 10px',
                              backgroundColor: theme.primaryLight,
                              color: theme.primary,
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}
                          >
                            <BarChart3 size={14} />
                            Compare ({activeTab.selectedCompetitors.size})
                          </button>
                          <button
                            onClick={() => updateActiveTab({ selectedCompetitors: new Set() })}
                            style={{
                              fontSize: '12px',
                              padding: '4px 10px',
                              backgroundColor: theme.errorBg,
                              color: theme.errorText,
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                            }}
                          >
                            Clear
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Search specific competitor */}
                  <form onSubmit={handleSearchCompetitor} style={{ marginBottom: '16px', minWidth: 0 }}>
                    <div style={{ display: 'flex', gap: '8px', minWidth: 0 }}>
                      <input
                        type="text"
                        placeholder="Search for a competitor..."
                        value={activeTab.competitorSearchQuery || ''}
                        onChange={(e) => updateActiveTab({ competitorSearchQuery: e.target.value })}
                        style={{
                          flex: 1,
                          minWidth: 0,
                          boxSizing: 'border-box',
                          padding: '8px 12px',
                          fontSize: '13px',
                          border: `1px solid ${theme.inputBorder}`,
                          borderRadius: '6px',
                          backgroundColor: theme.inputBg,
                          color: theme.text,
                        }}
                      />
                      <button
                        type="submit"
                        disabled={activeTab.searchingCompetitor || !activeTab.competitorSearchQuery?.trim()}
                        style={{
                          padding: '8px 12px',
                          fontSize: '13px',
                          backgroundColor: activeTab.searchingCompetitor || !activeTab.competitorSearchQuery?.trim() ? 'transparent' : theme.primary,
                          color: activeTab.searchingCompetitor || !activeTab.competitorSearchQuery?.trim() ? theme.primary : theme.primaryContrast,
                          border: activeTab.searchingCompetitor || !activeTab.competitorSearchQuery?.trim() ? `1px solid ${theme.primary}` : 'none',
                          borderRadius: '6px',
                          cursor: activeTab.searchingCompetitor || !activeTab.competitorSearchQuery?.trim() ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        {activeTab.searchingCompetitor ? (
                          <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} />
                        ) : (
                          <Search size={14} />
                        )}
                        Add
                      </button>
                    </div>
                  </form>

                  {/* Expand competitors button */}
                  <button
                    onClick={handleExpandCompetitors}
                    disabled={activeTab.expandingCompetitors}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      marginBottom: '16px',
                      fontSize: '13px',
                      fontWeight: '500',
                      backgroundColor: activeTab.expandingCompetitors ? theme.hoverBg : theme.accentLight,
                      color: activeTab.expandingCompetitors ? theme.textMuted : theme.accent,
                      border: `1px dashed ${activeTab.expandingCompetitors ? theme.border : theme.accent}`,
                      borderRadius: '8px',
                      cursor: activeTab.expandingCompetitors ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      transition: 'all 0.2s',
                    }}
                  >
                    {activeTab.expandingCompetitors ? (
                      <>
                        <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                        Finding more competitors...
                      </>
                    ) : (
                      <>
                        <Sparkles size={16} />
                        Find More Competitors
                      </>
                    )}
                  </button>
                  {activeTab.expandingCompetitors && (
                    <p style={{ margin: '0 0 16px 0', fontSize: '12px', color: theme.textMuted }}>
                      Research may take up to a minute.
                    </p>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {filteredCompetitors.map((competitor) => (
                      <CompetitorCard
                        key={competitor.name}
                        competitor={competitor}
                        product={activeTab.product}
                        isSelected={activeTab.selectedCompetitors.has(competitor.name)}
                        onSelect={toggleCompetitor}
                        onCompare={() => {}}
                        onDeepDive={handleDeepDive}
                        onRemove={removeCompetitor}
                      />
                    ))}
                  </div>

                  {filteredCompetitors.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '40px', color: theme.textMuted }}>
                      <p>No competitors match the current filters</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Market Trends */}
            {activeTab.data.market_trends && activeTab.data.market_trends.length > 0 && (
              <div style={{
                backgroundColor: theme.card,
                borderRadius: '12px',
                padding: '20px',
                boxShadow: theme.shadow,
                marginBottom: '24px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px', flexWrap: 'wrap', gap: '8px' }}>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: theme.text }}>
                    Market Trends
                  </h3>
                  <div style={{ display: 'flex', gap: '12px', flexShrink: 0 }}>
                    {[
                      { label: 'Positive', bg: theme.accentLight, border: theme.accent },
                      { label: 'Neutral',  bg: theme.hoverBg,    border: theme.textMuted },
                      { label: 'Negative', bg: theme.errorBg,    border: theme.usageError },
                    ].map(({ label, bg, border }) => (
                      <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <div style={{ width: '10px', height: '10px', backgroundColor: bg, border: `2px solid ${border}`, borderRadius: '2px', flexShrink: 0 }} />
                        <span style={{ fontSize: '11px', color: theme.textMuted }}>{label}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <p style={{ margin: '0 0 14px 0', fontSize: '11px', color: theme.textMuted }}>
                  Click any trend to analyze it in depth.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '12px' }}>
                  {activeTab.data.market_trends.map((trend, idx) => {
                    const isHovered = hoveredTrendIdx === idx;
                    const isExpanded = expandedTrendIdx === idx;
                    const hasAnalysis = !!(activeTab.trendAnalyses || {})[trend.trend]?.data;
                    const isAnalyzing = !!(activeTab.trendAnalyses || {})[trend.trend]?.loading;
                    const accentColor = trend.impact === 'positive' ? theme.accent
                      : trend.impact === 'negative' ? theme.usageError : theme.textMuted;
                    return (
                      <div
                        key={idx}
                        role="button"
                        tabIndex={0}
                        aria-expanded={isExpanded}
                        onClick={() => setExpandedTrendIdx(isExpanded ? null : idx)}
                        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setExpandedTrendIdx(isExpanded ? null : idx)}
                        onMouseEnter={() => setHoveredTrendIdx(idx)}
                        onMouseLeave={() => setHoveredTrendIdx(null)}
                        style={{
                          padding: '12px',
                          backgroundColor: trend.impact === 'positive' ? theme.accentLight :
                                          trend.impact === 'negative' ? theme.errorBg : theme.hoverBg,
                          borderRadius: '8px',
                          borderLeft: `4px solid ${accentColor}`,
                          cursor: 'pointer',
                          transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                          transform: isHovered || isExpanded ? 'translateY(-2px)' : 'none',
                          boxShadow: isHovered || isExpanded
                            ? `0 4px 12px ${accentColor}33`
                            : 'none',
                          outline: 'none',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
                          <p style={{ margin: 0, fontSize: '13px', color: theme.text, flex: 1 }}>
                            {trend.trend}
                          </p>
                          <span style={{
                            fontSize: '10px',
                            color: accentColor,
                            flexShrink: 0,
                            marginTop: '2px',
                            transition: 'transform 0.15s ease',
                            transform: isExpanded ? 'rotate(180deg)' : 'none',
                            display: 'inline-block',
                          }}>▾</span>
                        </div>
                        {isExpanded && (
                          <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: `1px solid ${accentColor}44` }}>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleTrendAnalysis(trend); setExpandedTrendIdx(null); }}
                              disabled={isAnalyzing}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                width: '100%',
                                padding: '7px 12px',
                                backgroundColor: hasAnalysis ? theme.hoverBg : theme.accent,
                                color: hasAnalysis ? theme.text : '#fff',
                                border: hasAnalysis ? `1px solid ${theme.border}` : 'none',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: '500',
                                cursor: isAnalyzing ? 'wait' : 'pointer',
                                opacity: isAnalyzing ? 0.7 : 1,
                              }}
                            >
                              {isAnalyzing ? (
                                <>
                                  <RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} />
                                  Analyzing…
                                </>
                              ) : hasAnalysis ? (
                                <>
                                  <BarChart3 size={12} />
                                  View Analysis
                                </>
                              ) : (
                                <>
                                  <BarChart3 size={12} />
                                  Analyze Market Trend
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Trend Analysis Panel — shown below Market Trends when analyses exist */}
            <div ref={trendAnalysisSectionRef}>
              <TrendAnalysisPanel
                theme={theme}
                activeTab={activeTab}
                onAnalyze={handleTrendAnalysis}
                onClose={closeTrendAnalysis}
                updateActiveTab={updateActiveTab}
              />
            </div>

            {/* Market Share Panel */}
            <MarketSharePanel
              theme={theme}
              activeTab={activeTab}
              onAnalyze={handleMarketShare}
              onClear={() => updateActiveTab({ marketShare: null, marketShareError: null })}
            />

            {/* SEO Analysis Panel */}
            <SEOAnalysisPanel
              theme={theme}
              activeTab={activeTab}
              onAnalyze={handleSEOAnalysis}
              onClear={() => updateActiveTab({ seoAnalysis: null, seoAnalysisError: null })}
            />

            {/* Events & Headlines Panel - multiple competitors, tabbed */}
            <EventsPanel
              theme={theme}
              activeTab={activeTab}
              filteredCompetitors={filteredCompetitors}
              onEvents={handleEventsAnalysis}
              onLoadMore={handleLoadMoreEvents}
              onClose={closeEvents}
              updateActiveTab={updateActiveTab}
            />

            {/* Deep Dive Panel - multiple competitors, tabbed */}
            <div ref={deepDiveSectionRef}>
              <DeepDivePanel
                theme={theme}
                activeTab={activeTab}
                filteredCompetitors={filteredCompetitors}
                onDeepDive={handleDeepDive}
                onCloseDeepDive={closeDeepDive}
                updateActiveTab={updateActiveTab}
              />
            </div>

            {/* Sources & Further Reading Panel */}
            <FurtherReadingPanel
              theme={theme}
              activeTab={activeTab}
            />

            {/* Selected Competitors Comparison Panel - At the Bottom */}
            {activeTab.selectedCompetitors.size > 0 && activeTab.showComparison && (
              <SelectedCompetitorsComparison
                competitors={activeTab.data.competitors.filter(c => 
                  activeTab.selectedCompetitors.has(c.name)
                )}
                onClose={() => updateActiveTab({ showComparison: false })}
                onRemove={(competitorName) => {
                  const newSet = new Set(activeTab.selectedCompetitors);
                  newSet.delete(competitorName);
                  updateActiveTab({ selectedCompetitors: newSet });
                }}
              />
            )}
          </>
        )}

        {/* Empty State */}
        {!activeTab.data && !activeTab.loading && !activeTab.error && (
          <div style={{
            textAlign: 'center',
            padding: '80px 20px',
            color: theme.textMuted,
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              margin: '0 auto 24px',
              backgroundColor: theme.border,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Search size={32} color={theme.textMuted} />
            </div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: theme.text }}>
              Start Your Research
            </h3>
            <p style={{ margin: 0, fontSize: '14px', maxWidth: '400px', marginLeft: 'auto', marginRight: 'auto' }}>
              Enter a product or service name above to analyze competitors, 
              compare prices, and review market sentiment.
            </p>
            {tabs.length === 1 && (
              <p style={{ marginTop: '16px', fontSize: '13px', color: theme.textMuted }}>
                Tip: Click the + button to open multiple search tabs
              </p>
            )}
          </div>
        )}
      </main>
        </div>
      </div>

      {/* Footer */}
      <footer style={{
        borderTop: `1px solid ${theme.border}`,
        backgroundColor: theme.card,
        padding: '20px 0',
        marginTop: '40px',
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 24px', textAlign: 'center' }}>
          <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: theme.textMuted }}>
            Market Scan • Powered by Google Gemini
          </p>
          <p style={{ margin: 0, fontSize: '11px', color: theme.textMuted }}>
            Multi-tab research • Interactive exports • Real-time analysis
          </p>
          <p style={{ margin: '8px 0 0 0', fontSize: '11px', color: theme.textMuted }}>
            <a href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer" style={{ color: theme.primary, textDecoration: 'none' }}>Privacy Policy</a>
            {' • '}
            <a href={TERMS_OF_SERVICE_URL} target="_blank" rel="noopener noreferrer" style={{ color: theme.primary, textDecoration: 'none' }}>Terms of Service</a>
          </p>
          <p style={{ margin: '6px 0 0 0', fontSize: '11px', color: theme.textMuted }}>
            By using our services you agree to our Terms of Service and Privacy Policy.
          </p>
          <p style={{ margin: '8px 0 0 0', fontSize: '11px', color: theme.textMuted }}>
            Developed, maintained, and owned by{' '}
            <a href="https://pdxintelligence.com" target="_blank" rel="noopener noreferrer" style={{ color: theme.primary, textDecoration: 'none' }}>PDX Intelligence</a>
          </p>
        </div>
      </footer>

      {/* CSS Animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Text-selection glossary popup */}
      {selectionPopup && (
        <div
          data-glossary-popup="true"
          style={{
            position: 'fixed',
            left: Math.max(10, Math.min(selectionPopup.x - 100, window.innerWidth - 220)),
            top: Math.max(10, selectionPopup.y - 52),
            zIndex: 9000,
            backgroundColor: theme.card,
            border: `1px solid ${theme.border}`,
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
            padding: '7px 10px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <BookOpen size={14} style={{ color: theme.primary, flexShrink: 0 }} />
          <span style={{
            fontSize: '12px',
            color: theme.text,
            maxWidth: '130px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            "{selectionPopup.term}"
          </span>
          <button
            onClick={() => {
              addToGlossary(selectionPopup.term, selectionPopup.context);
              setSelectionPopup(null);
              window.getSelection()?.removeAllRanges();
            }}
            style={{
              padding: '4px 10px',
              fontSize: '11px',
              fontWeight: '600',
              color: theme.primaryContrast,
              backgroundColor: theme.primary,
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            + Glossary
          </button>
          <button
            onClick={() => setSelectionPopup(null)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: theme.textMuted,
              padding: '2px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Shared UI Helpers ─────────────────────────────────────────────────────────

/**
 * Map raw backend error messages to user-friendly strings.
 */
function friendlyMessage(msg = '') {
  const m = msg.toLowerCase();
  if (m.includes('json') || m.includes('parse') || m.includes('non-json')) {
    return 'The AI returned an unexpected format. Please try again.';
  }
  if (m.includes('quota') || m.includes('429') || m.includes('rate limit')) {
    return 'Rate limit reached. Please wait a moment and try again.';
  }
  if (m.includes('timeout') || m.includes('timed out')) {
    return 'The request timed out. Please try again.';
  }
  if (m.includes('usage limit')) {
    return msg; // already user-friendly from the API layer
  }
  return msg || 'Something went wrong. Please try again.';
}

/**
 * Amber warning banner for "no data" / low-confidence states.
 */
function WarningBanner({ message, theme }) {
  return (
    <div style={{
      padding: '10px 14px',
      backgroundColor: theme.isDark ? '#78350f22' : '#fef3c722',
      border: '1px solid #f59e0b66',
      borderRadius: '8px',
      color: theme.isDark ? '#fbbf24' : '#92400e',
      fontSize: '13px',
      lineHeight: '1.5',
      marginBottom: '14px',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '8px',
    }}>
      <span style={{ flexShrink: 0, marginTop: '1px' }}>⚠️</span>
      <span>{message}</span>
    </div>
  );
}

// ─── Market Share Panel ────────────────────────────────────────────────────────

// ─── Trend Analysis Panel ──────────────────────────────────────────────────────
// Tabbed panel shown below Market Trends when one or more analyses have been run.
// Category labels and icons for FurtherReadingPanel search links
const SEARCH_LINK_META = {
  reports:  { label: 'Research Reports', icon: '📊' },
  data:     { label: 'Market Data',       icon: '📈' },
  news:     { label: 'Industry News',     icon: '📰' },
  reviews:  { label: 'Reviews & Compare', icon: '⭐' },
};

function buildResearchSourcesContext(researchData) {
  if (!researchData) return {};

  return {
    market_summary: researchData.market_summary || '',
    market_trends: (researchData.market_trends || [])
      .slice(0, 5)
      .map((trend) => ({
        trend: trend?.trend || '',
        impact: trend?.impact || '',
      }))
      .filter((trend) => trend.trend),
    competitors: (researchData.competitors || [])
      .slice(0, 6)
      .map((competitor) => ({
        name: competitor?.name || '',
        website: competitor?.website || null,
        current_price: competitor?.current_price ?? null,
        currency: competitor?.currency || null,
        review_summary: competitor?.review_summary || '',
        key_features: Array.isArray(competitor?.key_features) ? competitor.key_features.slice(0, 3) : [],
      }))
      .filter((competitor) => competitor.name),
    grounding_sources: Array.isArray(researchData.grounding_sources) ? researchData.grounding_sources.slice(0, 10) : [],
  };
}

function buildFallbackSearchLink(query, title, domainLabel = 'google.com') {
  return {
    title,
    url: `https://www.google.com/search?q=${encodeURIComponent(query)}`,
    domain: domainLabel,
  };
}

function FurtherReadingPanel({ theme, activeTab }) {
  const { data: researchData } = activeTab;
  const { furtherReadingSources, furtherReadingLoading, furtherReadingError } = activeTab;
  if (!researchData) return null;

  // Detect old-format data (has search_links/industry_resources keys) — treat as not loaded
  const isOldFormat = furtherReadingSources && (furtherReadingSources.search_links || furtherReadingSources.industry_resources);
  const sources = isOldFormat ? null : furtherReadingSources;

  // Competitor sites are derived directly from existing research data — no extra API call
  const competitorSites = (researchData.competitors || [])
    .filter(c => c.website)
    .map(c => ({ name: c.name, url: c.website }));

  const initialResearchSources = Array.isArray(researchData?.grounding_sources) ? researchData.grounding_sources : [];
  const normalizedResearchSources = initialResearchSources.map((item) => ({
    title: item?.title || item?.uri || '',
    url: item?.uri || item?.url || '',
    domain: (() => {
      try {
        return new URL(item?.uri || item?.url || '').hostname.replace(/^www\./, '');
      } catch {
        return '';
      }
    })(),
  })).filter((item) => item.url);

  const citations = Array.isArray(sources?.citations) ? sources.citations : [];
  const categoryKeys = ['reports', 'news', 'reviews', 'data'];
  const fallbackCategoryLinks = {
    reports: [
      buildFallbackSearchLink(`${activeTab.product} market research report ${activeTab.location}`, `Search research reports for ${activeTab.product}`),
    ],
    news: [
      buildFallbackSearchLink(`${activeTab.product} market news latest developments ${activeTab.location}`, `Search news about ${activeTab.product}`),
    ],
    reviews: [
      buildFallbackSearchLink(`${activeTab.product} reviews comparisons ${activeTab.location}`, `Search reviews and comparisons for ${activeTab.product}`),
    ],
    data: [
      buildFallbackSearchLink(`${activeTab.product} market size statistics market share ${activeTab.location}`, `Search market data for ${activeTab.product}`),
    ],
  };
  const mergedCategorySources = Object.fromEntries(categoryKeys.map((key) => {
    const apiItems = Array.isArray(sources?.[key]) ? sources[key] : [];
    return [key, apiItems.length > 0 ? apiItems : fallbackCategoryLinks[key]];
  }));
  const hasCategories = categoryKeys.some(k => mergedCategorySources[k]?.length > 0);

  return (
    <div style={{
      backgroundColor: theme.card,
      borderRadius: '12px',
      padding: '20px',
      boxShadow: theme.shadow,
      marginBottom: '24px',
      border: `1px solid ${theme.border}`,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '18px' }}>📚</span>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: theme.text }}>
            Sources &amp; Further Reading
          </h3>
        </div>
      </div>
      <p style={{ margin: '0 0 16px 0', fontSize: '12px', color: theme.textMuted }}>
        Curated resources from live web search across research reports, news, reviews, and market data.
      </p>

      {furtherReadingLoading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: theme.textMuted, fontSize: '13px' }}>
          <div style={{
            width: '14px', height: '14px', borderRadius: '50%',
            border: `2px solid ${theme.border}`, borderTopColor: theme.accent,
            animation: 'spin 0.8s linear infinite',
          }} />
          Finding industry resources…
        </div>
      )}

      {furtherReadingError && !furtherReadingLoading && (
        <p style={{ margin: 0, fontSize: '13px', color: theme.errorText || '#ef4444' }}>
          {furtherReadingError}
        </p>
      )}

      {citations.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 10px 0', fontSize: '12px', fontWeight: '700', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Citations
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {citations.map((item, i) => (
              <a
                key={`${item.url}-${i}`}
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'block',
                  padding: '10px 12px',
                  borderRadius: '10px',
                  backgroundColor: theme.hoverBg,
                  border: `1px solid ${theme.border}`,
                  color: theme.text,
                  textDecoration: 'none',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = theme.primary; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = theme.border; }}
              >
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', marginBottom: item.reason ? '4px' : 0 }}>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: theme.primary || '#3b82f6' }}>
                    {item.title || item.url}
                  </span>
                  {item.domain && (
                    <span style={{ fontSize: '11px', color: theme.textMuted }}>
                      {item.domain}
                    </span>
                  )}
                  {item.category && (
                    <span style={{
                      fontSize: '10px',
                      color: theme.textMuted,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      padding: '2px 6px',
                      borderRadius: '999px',
                      backgroundColor: theme.card,
                      border: `1px solid ${theme.border}`,
                    }}>
                      {item.category}
                    </span>
                  )}
                </div>
                {item.reason && (
                  <div style={{ fontSize: '12px', color: theme.textMuted, lineHeight: '1.5' }}>
                    {item.reason}
                  </div>
                )}
              </a>
            ))}
          </div>
        </div>
      )}

      {normalizedResearchSources.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 10px 0', fontSize: '12px', fontWeight: '700', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Sources Used In Initial Research
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {normalizedResearchSources.map((item, i) => (
              <a
                key={`${item.url}-${i}`}
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex', alignItems: 'baseline', gap: '6px',
                  fontSize: '13px', color: theme.primary || '#3b82f6', textDecoration: 'none',
                }}
                onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
                onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
              >
                <span style={{ flexShrink: 0 }}>-</span>
                <span>{item.title || item.url}</span>
                {item.domain && (
                  <span style={{ fontSize: '11px', color: theme.textMuted, flexShrink: 0 }}>
                    ({item.domain})
                  </span>
                )}
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Competitor Sites — always shown when research data exists */}
      {competitorSites.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 10px 0', fontSize: '12px', fontWeight: '700', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            🏢 Competitor Sites
          </h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {competitorSites.map((site, i) => (
              <a
                key={i}
                href={site.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                  padding: '5px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '500',
                  backgroundColor: theme.hoverBg, color: theme.primary || '#3b82f6',
                  border: `1px solid ${theme.border}`, textDecoration: 'none',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = theme.primaryLight || '#eff6ff'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = theme.hoverBg; }}
              >
                {site.name} ↗
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Category-based resources from grounded search */}
      {hasCategories && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
          {categoryKeys.map(cat => {
            const items = mergedCategorySources[cat];
            if (!items || items.length === 0) return null;
            const meta = SEARCH_LINK_META[cat] || { label: cat, icon: '🔗' };
            return (
              <div key={cat} style={{ minWidth: '200px', flex: '1 1 200px' }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: '700', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {meta.icon} {meta.label}
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {items.map((item, i) => (
                    <a
                      key={i}
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'flex', alignItems: 'baseline', gap: '6px',
                        fontSize: '13px', color: theme.primary || '#3b82f6', textDecoration: 'none',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
                      onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
                    >
                      <span style={{ flexShrink: 0 }}>•</span>
                      <span>{item.title || item.url}</span>
                      {item.domain && (
                        <span style={{ fontSize: '11px', color: theme.textMuted, flexShrink: 0 }}>
                          ({item.domain})
                        </span>
                      )}
                    </a>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Empty state while waiting for auto-trigger */}
      {!furtherReadingLoading && citations.length === 0 && normalizedResearchSources.length === 0 && competitorSites.length === 0 && !furtherReadingError && (
        <p style={{ margin: 0, fontSize: '13px', color: theme.textMuted }}>
          Sources will be loaded automatically once research completes.
        </p>
      )}

      <p style={{ margin: '16px 0 0 0', fontSize: '10px', color: theme.textMuted, fontStyle: 'italic' }}>
        Resources found via live web search. Verify data independently.
      </p>
    </div>
  );
}

function TrendAnalysisPanel({ theme, activeTab, onAnalyze, onClose, updateActiveTab }) {
  const trendAnalyses = activeTab.trendAnalyses || {};
  const names = Object.keys(trendAnalyses);
  if (!names.length) return null;

  const activeName = activeTab.activeTrendName && trendAnalyses[activeTab.activeTrendName]
    ? activeTab.activeTrendName
    : names[0];
  const activeEntry = trendAnalyses[activeName] || {};

  const severityColor = (severity, theme) => {
    if (severity === 'high')   return theme.usageError;
    if (severity === 'medium') return theme.accent;
    return theme.textMuted;
  };

  const confidenceLabel = (level) => {
    const map = { high: { label: 'High Confidence', color: '#10b981' }, medium: { label: 'Medium Confidence', color: '#f59e0b' }, low: { label: 'Low Confidence', color: '#ef4444' } };
    return map[level] || map.medium;
  };

  return (
    <div style={{
      backgroundColor: theme.card,
      borderRadius: '12px',
      padding: '20px',
      boxShadow: theme.shadow,
      marginBottom: '24px',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
        <BarChart3 size={20} color={theme.accent} />
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: theme.text }}>
          Market Trend Analysis
        </h3>
      </div>
      <p style={{ margin: '0 0 14px 0', fontSize: '12px', color: theme.textMuted }}>
        In-depth breakdown of selected market trends.
      </p>

      {/* Tab bar */}
      <div style={{
        display: 'flex',
        gap: '6px',
        flexWrap: 'wrap',
        marginBottom: '16px',
        borderBottom: `1px solid ${theme.border}`,
        paddingBottom: '12px',
      }}>
        {names.map((name) => {
          const isActive = name === activeName;
          const entry = trendAnalyses[name];
          return (
            <div
              key={name}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '5px 10px',
                backgroundColor: isActive ? theme.accent : theme.hoverBg,
                color: isActive ? '#fff' : theme.text,
                borderRadius: '6px',
                fontSize: '12px',
                cursor: 'pointer',
                maxWidth: '220px',
                border: `1px solid ${isActive ? theme.accent : theme.border}`,
              }}
              onClick={() => updateActiveTab({ activeTrendName: name })}
            >
              {entry?.loading && (
                <RefreshCw size={10} style={{ animation: 'spin 1s linear infinite', flexShrink: 0 }} />
              )}
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {name.length > 38 ? name.slice(0, 36) + '…' : name}
              </span>
              <button
                onClick={(e) => { e.stopPropagation(); onClose(name); }}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px',
                  color: isActive ? '#ffffffcc' : theme.textMuted, lineHeight: 1, flexShrink: 0,
                }}
                title="Close this analysis"
              >
                <X size={12} />
              </button>
            </div>
          );
        })}
      </div>

      {/* Loading state */}
      {activeEntry.loading && (
        <div style={{ padding: '32px 0', textAlign: 'center', color: theme.textMuted }}>
          <RefreshCw size={28} style={{ animation: 'spin 1s linear infinite', color: theme.accent }} />
          <p style={{ margin: '12px 0 4px 0', fontSize: '14px', fontWeight: '500', color: theme.text }}>
            Analyzing trend…
          </p>
          <p style={{ margin: 0, fontSize: '12px', color: theme.textMuted }}>
            Researching drivers, impacts, and opportunities. This may take up to a minute.
          </p>
        </div>
      )}

      {/* Error state */}
      {activeEntry.error && !activeEntry.loading && (
        <div style={{
          padding: '14px 16px',
          backgroundColor: theme.errorBg,
          borderRadius: '8px',
          borderLeft: `4px solid ${theme.usageError}`,
        }}>
          <p style={{ margin: 0, fontSize: '13px', color: theme.usageError, fontWeight: '500' }}>
            Analysis failed
          </p>
          <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: theme.text }}>
            {activeEntry.error}
          </p>
          <button
            onClick={() => {
              const trend = (activeTab.data?.market_trends || []).find(t => t.trend === activeName);
              if (trend) onAnalyze(trend);
            }}
            style={{
              marginTop: '10px', padding: '6px 12px', fontSize: '12px', fontWeight: '500',
              backgroundColor: theme.accent, color: '#fff', border: 'none',
              borderRadius: '6px', cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Analysis content */}
      {activeEntry.data && !activeEntry.loading && (() => {
        const d = activeEntry.data;
        const conf = confidenceLabel(d.confidence_level);
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

            {/* Trend name + confidence badge */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
              <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: theme.text, flex: 1 }}>
                {activeName}
              </h4>
              <span style={{
                padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600',
                backgroundColor: conf.color + '22', color: conf.color, flexShrink: 0,
              }}>
                {conf.label}
              </span>
            </div>

            {/* Executive Summary */}
            {d.executive_summary && (
              <div>
                <h5 style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: '600', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Executive Summary
                </h5>
                <p style={{ margin: 0, fontSize: '13px', color: theme.text, lineHeight: '1.65' }}>
                  {d.executive_summary}
                </p>
              </div>
            )}

            {/* Drivers */}
            {d.drivers?.length > 0 && (
              <div>
                <h5 style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: '600', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Key Drivers
                </h5>
                <ul style={{ margin: 0, paddingLeft: '18px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  {d.drivers.map((driver, i) => (
                    <li key={i} style={{ fontSize: '13px', color: theme.text, lineHeight: '1.5' }}>{driver}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Market Implications */}
            {d.market_implications && (
              <div>
                <h5 style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: '600', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Market Implications
                </h5>
                <p style={{ margin: 0, fontSize: '13px', color: theme.text, lineHeight: '1.65' }}>
                  {d.market_implications}
                </p>
              </div>
            )}

            {/* Impact by Segment */}
            {d.impact_by_segment?.length > 0 && (
              <div>
                <h5 style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: '600', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Impact by Segment
                </h5>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {d.impact_by_segment.map((seg, i) => (
                    <div key={i} style={{
                      display: 'flex', gap: '10px', alignItems: 'flex-start',
                      padding: '10px 12px', borderRadius: '8px', backgroundColor: theme.hoverBg,
                      borderLeft: `3px solid ${severityColor(seg.severity, theme)}`,
                    }}>
                      <div style={{ flex: 1 }}>
                        <span style={{ fontSize: '12px', fontWeight: '600', color: theme.text }}>{seg.segment}</span>
                        <p style={{ margin: '3px 0 0 0', fontSize: '12px', color: theme.textMuted, lineHeight: '1.5' }}>{seg.impact}</p>
                      </div>
                      <span style={{
                        padding: '2px 8px', borderRadius: '12px', fontSize: '10px', fontWeight: '600', flexShrink: 0,
                        backgroundColor: severityColor(seg.severity, theme) + '22',
                        color: severityColor(seg.severity, theme),
                        textTransform: 'capitalize',
                      }}>
                        {seg.severity}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Opportunities & Risks side by side */}
            {(d.opportunities?.length > 0 || d.risks?.length > 0) && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                {d.opportunities?.length > 0 && (
                  <div>
                    <h5 style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: '600', color: '#10b981', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Opportunities
                    </h5>
                    <ul style={{ margin: 0, paddingLeft: '16px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                      {d.opportunities.map((o, i) => (
                        <li key={i} style={{ fontSize: '12px', color: theme.text, lineHeight: '1.5' }}>{o}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {d.risks?.length > 0 && (
                  <div>
                    <h5 style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: '600', color: theme.usageError, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Risks
                    </h5>
                    <ul style={{ margin: 0, paddingLeft: '16px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                      {d.risks.map((r, i) => (
                        <li key={i} style={{ fontSize: '12px', color: theme.text, lineHeight: '1.5' }}>{r}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Timeline */}
            {d.timeline && (
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: '10px',
                padding: '12px 14px', borderRadius: '8px',
                backgroundColor: theme.accentLight, borderLeft: `3px solid ${theme.accent}`,
              }}>
                <span style={{ fontSize: '16px', flexShrink: 0 }}>🕐</span>
                <div>
                  <span style={{ fontSize: '11px', fontWeight: '600', color: theme.accent, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '2px' }}>
                    Timeline
                  </span>
                  <p style={{ margin: 0, fontSize: '13px', color: theme.text }}>{d.timeline}</p>
                </div>
              </div>
            )}

            {/* Competitive Positioning */}
            {d.competitive_positioning && (
              <div>
                <h5 style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: '600', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Competitive Positioning
                </h5>
                <p style={{ margin: 0, fontSize: '13px', color: theme.text, lineHeight: '1.65' }}>
                  {d.competitive_positioning}
                </p>
              </div>
            )}

            {/* Key Players Affected */}
            {d.key_players_affected?.length > 0 && (
              <div>
                <h5 style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: '600', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Key Players Affected
                </h5>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {d.key_players_affected.map((p, i) => (
                    <span key={i} style={{
                      padding: '4px 10px', borderRadius: '20px', fontSize: '12px',
                      backgroundColor: theme.hoverBg, color: theme.text,
                      border: `1px solid ${theme.border}`,
                    }}>
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Strategic Recommendations */}
            {d.strategic_recommendations?.length > 0 && (
              <div style={{
                padding: '14px 16px',
                backgroundColor: theme.accentLight,
                borderRadius: '8px',
                borderLeft: `4px solid ${theme.accent}`,
              }}>
                <h5 style={{ margin: '0 0 10px 0', fontSize: '12px', fontWeight: '600', color: theme.accent, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Strategic Recommendations
                </h5>
                <ol style={{ margin: 0, paddingLeft: '18px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {d.strategic_recommendations.map((rec, i) => (
                    <li key={i} style={{ fontSize: '13px', color: theme.text, lineHeight: '1.55' }}>{rec}</li>
                  ))}
                </ol>
              </div>
            )}

            <SourcesSection sources={d.grounding_sources} theme={theme} />

          </div>
        );
      })()}
    </div>
  );
}

const MARKET_SHARE_COLORS = [
  '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#14b8a6',
];

function MarketSharePanel({ theme, activeTab, onAnalyze, onClear }) {
  const { marketShare, marketShareLoading, marketShareError, marketShareWarning } = activeTab;
  const hasCompetitors = activeTab.data?.competitors?.length > 0;

  // Chart type: 'bar' | 'pie'
  const [chartType, setChartType] = useState('bar');

  // Selected competitors filter — default = all visible
  const allNames = useMemo(() => (marketShare?.competitors || []).map(c => c.name), [marketShare]);
  const [hiddenNames, setHiddenNames] = useState(new Set());

  // When new data arrives reset hidden set
  useEffect(() => { setHiddenNames(new Set()); }, [marketShare]);

  const visibleCompetitors = useMemo(() => {
    if (!marketShare) return [];
    // Intersect with selectedCompetitors filter if active
    const selected = activeTab.selectedCompetitors;
    const base = (marketShare.competitors || []).filter(c => !hiddenNames.has(c.name));
    if (selected && selected.size > 0) {
      return base.filter(c => selected.has(c.name));
    }
    return base;
  }, [marketShare, hiddenNames, activeTab.selectedCompetitors]);

  // Build stacked bar data  [{year, CompA: 25, CompB: 40, ...}]
  const barData = useMemo(() => {
    if (!marketShare) return [];
    const years = new Set();
    visibleCompetitors.forEach(c => Object.keys(c.shares_by_year || {}).forEach(y => years.add(y)));
    const sortedYears = Array.from(years).sort();
    return sortedYears.map(year => {
      const row = { year };
      let total = 0;
      visibleCompetitors.forEach(c => {
        const raw = c.shares_by_year?.[year] ?? 0;
        row[c.name] = raw;
        total += raw;
      });
      // Normalise to 100%
      if (total > 0 && Math.abs(total - 100) > 1) {
        visibleCompetitors.forEach(c => { row[c.name] = +((row[c.name] / total) * 100).toFixed(1); });
      }
      return row;
    });
  }, [visibleCompetitors, marketShare]);

  // Build pie data for latest year
  const pieData = useMemo(() => {
    if (!barData.length) return [];
    const latestRow = barData[barData.length - 1];
    return visibleCompetitors.map(c => ({
      name: c.name,
      value: latestRow[c.name] ?? 0,
    })).filter(d => d.value > 0);
  }, [barData, visibleCompetitors]);

  const latestYear = barData.length ? barData[barData.length - 1].year : '';

  const [hoveredCompetitor, setHoveredCompetitor] = useState(null);

  const toggleHidden = (name) => {
    setHiddenNames(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  // Custom tooltip: when hovering a competitor, show that competitor's value across ALL years
  const BarTooltip = useCallback(({ active, label }) => {
    if (!active || !hoveredCompetitor) return null;
    const colorIdx = allNames.indexOf(hoveredCompetitor) % MARKET_SHARE_COLORS.length;
    const color = MARKET_SHARE_COLORS[colorIdx < 0 ? 0 : colorIdx];
    return (
      <div style={{
        backgroundColor: theme.card,
        border: `1px solid ${theme.border}`,
        borderRadius: '8px',
        padding: '10px 14px',
        fontSize: '12px',
        boxShadow: theme.shadow,
        minWidth: 140,
      }}>
        <div style={{ fontWeight: 700, color, marginBottom: 8, fontSize: 13 }}>{hoveredCompetitor}</div>
        {barData.map(row => (
          <div key={row.year} style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 16,
            marginBottom: 3,
            fontWeight: row.year === label ? 700 : 400,
            color: row.year === label ? theme.text : theme.textMuted,
          }}>
            <span>{row.year}</span>
            <span>{(row[hoveredCompetitor] ?? 0).toFixed(1)}%</span>
          </div>
        ))}
      </div>
    );
  }, [hoveredCompetitor, barData, allNames, theme]);

  const confidenceColor = {
    high: '#10b981',
    moderate: '#f59e0b',
    low: '#ef4444',
  };

  const cardStyle = {
    backgroundColor: theme.card,
    borderRadius: '12px',
    padding: '20px',
    boxShadow: theme.shadow,
    marginBottom: '24px',
    border: `1px solid ${theme.border}`,
  };

  const btnBase = {
    padding: '10px 18px',
    fontSize: '14px',
    fontWeight: '500',
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'background-color 0.2s',
  };

  return (
    <div style={cardStyle}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <BarChart3 size={20} color={theme.accent} />
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: theme.text }}>
          Market Share Breakdown
        </h3>
      </div>
      <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: theme.textMuted }}>
        Estimated market share distribution across competitors over time. Switch between stacked bar and pie views.
      </p>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {!marketShare && !marketShareLoading && (
          <button
            type="button"
            onClick={onAnalyze}
            disabled={!hasCompetitors}
            style={{
              ...btnBase,
              backgroundColor: hasCompetitors ? theme.primary : 'transparent',
              color: hasCompetitors ? theme.primaryContrast : theme.primary,
              border: hasCompetitors ? 'none' : `1px solid ${theme.primary}`,
              cursor: hasCompetitors ? 'pointer' : 'not-allowed',
            }}
          >
            <BarChart3 size={16} />
            Analyze Market Share
          </button>
        )}

        {marketShare && !marketShareLoading && (
          <>
            <button
              type="button"
              onClick={onAnalyze}
              style={{ ...btnBase, backgroundColor: theme.primary, color: theme.primaryContrast, border: 'none' }}
            >
              <RefreshCw size={16} />
              Refresh Analysis
            </button>
            <button
              type="button"
              onClick={onClear}
              style={{ ...btnBase, backgroundColor: theme.hoverBg, color: theme.text, border: `1px solid ${theme.border}` }}
            >
              Clear Results
            </button>
          </>
        )}
      </div>

      {/* Loading */}
      {marketShareLoading && (
        <div style={{ padding: '32px', textAlign: 'center', color: theme.textMuted }}>
          <RefreshCw size={28} style={{ animation: 'spin 1s linear infinite', display: 'inline-block', marginBottom: '12px' }} />
          <p style={{ margin: 0, fontSize: '14px' }}>Analyzing market share...</p>
          <p style={{ margin: '8px 0 0 0', fontSize: '12px', opacity: 0.7 }}>Research may take up to a minute.</p>
        </div>
      )}

      {/* Error */}
      {marketShareError && !marketShareLoading && (
        <div style={{
          padding: '16px', backgroundColor: theme.errorBg,
          border: `1px solid ${theme.errorBorder}`, borderRadius: '8px',
          color: theme.errorText, marginBottom: '16px',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px',
        }}>
          <span><strong>Error:</strong> {friendlyMessage(marketShareError)}</span>
          <button
            type="button"
            onClick={onAnalyze}
            style={{ flexShrink: 0, padding: '4px 12px', fontSize: '12px', fontWeight: '500', backgroundColor: 'transparent', color: theme.errorText, border: `1px solid ${theme.errorBorder}`, borderRadius: '6px', cursor: 'pointer' }}
          >
            Try Again
          </button>
        </div>
      )}

      {/* Results */}
      {marketShare && !marketShareLoading && (
        <>
          {/* Warning banner — shown when data is estimated/low-confidence */}
          {marketShareWarning && <WarningBanner message={marketShareWarning} theme={theme} />}

          {/* Low-confidence inline notice when all shares are zero */}
          {marketShare.data_confidence === 'low' && !marketShareWarning && (
            <WarningBanner message="These figures are estimates based on limited public data and should be treated as indicative only." theme={theme} />
          )}

          {/* No-data empty state */}
          {(!marketShare.competitors || marketShare.competitors.length === 0) && (
            <div style={{ padding: '24px', textAlign: 'center', backgroundColor: theme.hoverBg, borderRadius: '8px', border: `1px solid ${theme.border}` }}>
              <p style={{ margin: '0 0 6px 0', fontSize: '14px', fontWeight: '500', color: theme.text }}>No market share data available</p>
              <p style={{ margin: 0, fontSize: '13px', color: theme.textMuted }}>Market share data is not publicly available for these competitors. This is common for niche or private companies.</p>
            </div>
          )}

          {/* Summary */}
          {marketShare.summary && marketShare.competitors?.length > 0 && (
            <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: theme.text, lineHeight: '1.6' }}>
              {marketShare.summary}
            </p>
          )}

          {/* Chart Type Toggle + Competitor Filter — only shown when there is data */}
          {marketShare.competitors?.length > 0 && <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
            {/* Chart toggle */}
            <div style={{ display: 'flex', gap: '4px', backgroundColor: theme.hoverBg, borderRadius: '8px', padding: '4px' }}>
              {[['bar', 'Stacked Bar'], ['pie', 'Pie Chart']].map(([type, label]) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setChartType(type)}
                  style={{
                    padding: '6px 14px',
                    fontSize: '12px',
                    fontWeight: chartType === type ? '600' : '400',
                    backgroundColor: chartType === type ? theme.primary : 'transparent',
                    color: chartType === type ? theme.primaryContrast : theme.textMuted,
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Competitor filter pills */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {allNames.map((name, i) => {
                const isHidden = hiddenNames.has(name);
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => toggleHidden(name)}
                    style={{
                      padding: '3px 10px',
                      fontSize: '11px',
                      fontWeight: '500',
                      borderRadius: '20px',
                      border: `1px solid ${MARKET_SHARE_COLORS[i % MARKET_SHARE_COLORS.length]}`,
                      backgroundColor: isHidden ? 'transparent' : MARKET_SHARE_COLORS[i % MARKET_SHARE_COLORS.length] + '22',
                      color: isHidden ? theme.textMuted : MARKET_SHARE_COLORS[i % MARKET_SHARE_COLORS.length],
                      cursor: 'pointer',
                      opacity: isHidden ? 0.5 : 1,
                      textDecoration: isHidden ? 'line-through' : 'none',
                    }}
                  >
                    {name}
                  </button>
                );
              })}
            </div>
          </div>}

          {/* Stacked Bar Chart */}
          {chartType === 'bar' && barData.length > 0 && (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={barData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
                <XAxis dataKey="year" tick={{ fill: theme.textMuted, fontSize: 12 }} />
                <YAxis
                  tickFormatter={v => `${v}%`}
                  domain={[0, 100]}
                  tick={{ fill: theme.textMuted, fontSize: 11 }}
                  width={42}
                />
                <RechartsTooltip
                  shared={false}
                  cursor={false}
                  content={<BarTooltip />}
                />
                <Legend wrapperStyle={{ fontSize: '12px', color: theme.text }} />
                {visibleCompetitors.map((c, i) => {
                  const color = MARKET_SHARE_COLORS[allNames.indexOf(c.name) % MARKET_SHARE_COLORS.length];
                  const dimmed = hoveredCompetitor && hoveredCompetitor !== c.name;
                  return (
                    <Bar
                      key={c.name}
                      dataKey={c.name}
                      stackId="a"
                      radius={i === visibleCompetitors.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                      activeBar={false}
                      onMouseEnter={() => setHoveredCompetitor(c.name)}
                      onMouseLeave={() => setHoveredCompetitor(null)}
                    >
                      {barData.map((_, idx) => (
                        <Cell
                          key={idx}
                          fill={color}
                          opacity={dimmed ? 0.2 : 1}
                        />
                      ))}
                    </Bar>
                  );
                })}
              </BarChart>
            </ResponsiveContainer>
          )}

          {/* Pie Chart */}
          {chartType === 'pie' && pieData.length > 0 && (
            <div>
              <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: theme.textMuted, textAlign: 'center' }}>
                Latest year: <strong style={{ color: theme.text }}>{latestYear}</strong>
              </p>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    outerRadius={110}
                    dataKey="value"
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    labelLine={true}
                  >
                    {pieData.map((entry, i) => (
                      <Cell
                        key={entry.name}
                        fill={MARKET_SHARE_COLORS[allNames.indexOf(entry.name) % MARKET_SHARE_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <RechartsTooltip
                    formatter={(value, name) => [`${value.toFixed(1)}%`, name]}
                    contentStyle={{
                      backgroundColor: theme.isDark ? '#22c55e' : theme.card,
                      border: `1px solid ${theme.isDark ? '#16a34a' : theme.border}`,
                      borderRadius: '8px',
                      fontSize: '12px',
                      color: theme.isDark ? '#000000' : undefined,
                    }}
                    labelStyle={{ color: theme.isDark ? '#000000' : theme.text, fontWeight: '600' }}
                    itemStyle={{ color: theme.isDark ? '#000000' : theme.text }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Positioning / Trend Table */}
          {marketShare.competitors?.length > 0 && (
            <div style={{ marginTop: '20px', overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                    {['Competitor', 'Trend', 'Positioning'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: theme.textMuted, fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {marketShare.competitors.map((c, i) => (
                    <tr key={c.name} style={{ borderBottom: `1px solid ${theme.border}` }}>
                      <td style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: MARKET_SHARE_COLORS[i % MARKET_SHARE_COLORS.length], flexShrink: 0, display: 'inline-block' }} />
                        <span style={{ color: theme.text, fontWeight: '500' }}>{c.name}</span>
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        <span style={{
                          padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '500',
                          backgroundColor: c.trend === 'growing' ? '#10b98122' : c.trend === 'declining' ? '#ef444422' : '#6b728022',
                          color: c.trend === 'growing' ? '#10b981' : c.trend === 'declining' ? '#ef4444' : theme.textMuted,
                        }}>
                          {c.trend || '—'}
                        </span>
                      </td>
                      <td style={{ padding: '8px 12px', color: theme.textMuted, textTransform: 'capitalize' }}>
                        {c.positioning || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Market Dynamics */}
          {marketShare.market_dynamics && (
            <div style={{ marginTop: '20px', padding: '14px 16px', backgroundColor: theme.hoverBg, borderRadius: '8px', border: `1px solid ${theme.border}` }}>
              <p style={{ margin: '0 0 4px 0', fontSize: '11px', fontWeight: '600', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Market Dynamics
              </p>
              <p style={{ margin: 0, fontSize: '13px', color: theme.text, lineHeight: '1.6' }}>
                {marketShare.market_dynamics}
              </p>
            </div>
          )}

          {/* Confidence + Methodology footer */}
          {(marketShare.data_confidence || marketShare.methodology) && (
            <div style={{ marginTop: '14px', display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
              {marketShare.data_confidence && (
                <span style={{ fontSize: '11px', color: theme.textMuted }}>
                  Confidence:{' '}
                  <span style={{ fontWeight: '600', color: confidenceColor[marketShare.data_confidence] || theme.textMuted, textTransform: 'capitalize' }}>
                    {marketShare.data_confidence}
                  </span>
                </span>
              )}
              {marketShare.methodology && (
                <span style={{ fontSize: '11px', color: theme.textMuted, fontStyle: 'italic' }}>
                  {marketShare.methodology}
                </span>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// SEO Analysis Panel
function SEOAnalysisPanel({ theme, activeTab, onAnalyze, onClear }) {
  const { seoAnalysis, seoAnalysisLoading, seoAnalysisError, seoWarning } = activeTab;
  const hasCompetitors = activeTab.data?.competitors?.length > 0;

  return (
    <div style={{
      backgroundColor: theme.card,
      borderRadius: '12px',
      padding: '20px',
      boxShadow: theme.shadow,
      marginBottom: '24px',
      border: `1px solid ${theme.border}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <Globe size={20} color={theme.accent} />
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: theme.text }}>
          Competitor SEO Analysis
        </h3>
      </div>
      <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: theme.textMuted }}>
        Analyze keywords, content strategies, backlink opportunities, and content gaps across your competitive landscape.
      </p>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {!seoAnalysis && !seoAnalysisLoading && (
          <button
            type="button"
            onClick={onAnalyze}
            disabled={!hasCompetitors}
            style={{
              padding: '10px 18px',
              fontSize: '14px',
              fontWeight: '500',
              backgroundColor: hasCompetitors ? theme.primary : 'transparent',
              color: hasCompetitors ? theme.primaryContrast : theme.primary,
              border: hasCompetitors ? 'none' : `1px solid ${theme.primary}`,
              borderRadius: '8px',
              cursor: hasCompetitors ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'background-color 0.2s',
            }}
          >
            <Search size={16} />
            Analyze SEO Landscape
          </button>
        )}
        
        {seoAnalysis && !seoAnalysisLoading && (
          <>
            <button
              type="button"
              onClick={onAnalyze}
              style={{
                padding: '10px 18px',
                fontSize: '14px',
                fontWeight: '500',
                backgroundColor: theme.primary,
                color: theme.primaryContrast,
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <RefreshCw size={16} />
              Refresh Analysis
            </button>
            <button
              type="button"
              onClick={onClear}
              style={{
                padding: '10px 18px',
                fontSize: '14px',
                fontWeight: '500',
                backgroundColor: theme.hoverBg,
                color: theme.text,
                border: `1px solid ${theme.border}`,
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              Clear Results
            </button>
          </>
        )}
      </div>

      {/* Loading State */}
      {seoAnalysisLoading && (
        <div style={{ padding: '32px', textAlign: 'center', color: theme.textMuted }}>
          <RefreshCw size={28} style={{ animation: 'spin 1s linear infinite', display: 'inline-block', marginBottom: '12px' }} />
          <p style={{ margin: 0, fontSize: '14px' }}>Analyzing SEO landscape...</p>
          <p style={{ margin: '8px 0 0 0', fontSize: '12px', opacity: 0.7 }}>Research may take up to a minute.</p>
        </div>
      )}

      {/* Error State */}
      {seoAnalysisError && !seoAnalysisLoading && (
        <div style={{
          padding: '16px',
          backgroundColor: theme.errorBg,
          border: `1px solid ${theme.errorBorder}`,
          borderRadius: '8px',
          color: theme.errorText,
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '12px',
        }}>
          <span><strong>Error:</strong> {friendlyMessage(seoAnalysisError)}</span>
          <button
            type="button"
            onClick={onAnalyze}
            style={{ flexShrink: 0, padding: '4px 12px', fontSize: '12px', fontWeight: '500', backgroundColor: 'transparent', color: theme.errorText, border: `1px solid ${theme.errorBorder}`, borderRadius: '6px', cursor: 'pointer' }}
          >
            Try Again
          </button>
        </div>
      )}

      {/* Results */}
      {seoAnalysis && !seoAnalysisLoading && (
        <>
          {seoWarning && <WarningBanner message={seoWarning} theme={theme} />}
          <SEOAnalysisContent data={seoAnalysis} theme={theme} />
        </>
      )}

      {/* Empty State */}
      {!seoAnalysis && !seoAnalysisLoading && !seoAnalysisError && (
        <div style={{
          padding: '32px',
          textAlign: 'center',
          backgroundColor: theme.hoverBg,
          borderRadius: '8px',
          color: theme.textMuted,
        }}>
          <Globe size={40} style={{ marginBottom: '12px', opacity: 0.5 }} />
          <p style={{ margin: 0, fontSize: '14px' }}>Click "Analyze SEO Landscape" to uncover keyword opportunities and content strategies</p>
        </div>
      )}
    </div>
  );
}

// SEO Analysis Content Component
function SEOAnalysisContent({ data, theme }) {
  if (!data) return null;

  const { market_keywords, content_themes, domain_authority_landscape, backlink_strategies, 
          content_gaps, technical_seo_trends, local_seo_factors, competitive_summary } = data;

  const getDifficultyColor = (difficulty) => {
    const d = (difficulty || '').toLowerCase();
    if (d.includes('hard')) return theme.usageError || '#ef4444';
    if (d.includes('medium')) return theme.warning || '#f59e0b';
    if (d.includes('easy')) return theme.accent || '#10b981';
    return theme.textMuted;
  };

  const getImpactColor = (impact) => {
    const i = (impact || '').toLowerCase();
    if (i.includes('high')) return theme.accent || '#10b981';
    if (i.includes('medium')) return theme.warning || '#f59e0b';
    if (i.includes('low')) return theme.textMuted;
    return theme.textMuted;
  };

  return (
    <div style={{ marginTop: '16px' }}>
      {/* Competitive Summary */}
      {competitive_summary && (
        <div style={{
          backgroundColor: theme.primaryLight,
          border: `1px solid ${theme.primary}`,
          borderRadius: '10px',
          padding: '16px',
          marginBottom: '20px',
        }}>
          <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', fontWeight: '700', color: theme.primary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Executive Summary
          </h4>
          <p style={{ margin: 0, fontSize: '14px', color: theme.text, lineHeight: '1.7' }}>
            {competitive_summary}
          </p>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
        {/* Keywords Section */}
        {market_keywords && (
          <div style={{
            backgroundColor: theme.card,
            border: `1px solid ${theme.border}`,
            borderRadius: '10px',
            padding: '16px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <span style={{ fontSize: '18px' }}>🔑</span>
              <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: theme.text }}>Target Keywords</h4>
              {market_keywords.keyword_difficulty && (
                <span style={{
                  marginLeft: 'auto',
                  fontSize: '11px',
                  padding: '3px 10px',
                  backgroundColor: getDifficultyColor(market_keywords.keyword_difficulty) + '20',
                  color: getDifficultyColor(market_keywords.keyword_difficulty),
                  borderRadius: '12px',
                  fontWeight: '600',
                }}>
                  {market_keywords.keyword_difficulty}
                </span>
              )}
            </div>
            
            {market_keywords.primary_keywords?.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <p style={{ margin: '0 0 6px 0', fontSize: '11px', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Primary</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {market_keywords.primary_keywords.map((kw, i) => (
                    <span key={i} style={{
                      fontSize: '12px',
                      padding: '4px 10px',
                      backgroundColor: theme.primaryLight,
                      color: theme.primary,
                      borderRadius: '4px',
                      fontWeight: '500',
                    }}>{kw}</span>
                  ))}
                </div>
              </div>
            )}
            
            {market_keywords.long_tail_keywords?.length > 0 && (
              <div>
                <p style={{ margin: '0 0 6px 0', fontSize: '11px', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Long-tail</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {market_keywords.long_tail_keywords.map((kw, i) => (
                    <span key={i} style={{
                      fontSize: '11px',
                      padding: '3px 8px',
                      backgroundColor: theme.hoverBg,
                      color: theme.text,
                      borderRadius: '4px',
                    }}>{kw}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Domain Authority */}
        {domain_authority_landscape && (
          <div style={{
            backgroundColor: theme.card,
            border: `1px solid ${theme.border}`,
            borderRadius: '10px',
            padding: '16px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <span style={{ fontSize: '18px' }}>📊</span>
              <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: theme.text }}>Domain Authority</h4>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', color: theme.textMuted }}>Estimated Range</span>
                <span style={{ fontSize: '13px', fontWeight: '600', color: theme.text }}>
                  {domain_authority_landscape.estimated_range}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', color: theme.textMuted }}>Market Maturity</span>
                <span style={{ fontSize: '13px', fontWeight: '600', color: theme.text }}>
                  {domain_authority_landscape.market_maturity}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', color: theme.textMuted }}>Newcomer Feasibility</span>
                <span style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: getDifficultyColor(domain_authority_landscape.newcomer_feasibility) === theme.usageError ? theme.usageError :
                         getDifficultyColor(domain_authority_landscape.newcomer_feasibility) === theme.accent ? theme.accent : theme.text
                }}>
                  {domain_authority_landscape.newcomer_feasibility}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Content Themes */}
        {content_themes?.length > 0 && (
          <div style={{
            backgroundColor: theme.card,
            border: `1px solid ${theme.border}`,
            borderRadius: '10px',
            padding: '16px',
            gridColumn: 'span 1',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <span style={{ fontSize: '18px' }}>📝</span>
              <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: theme.text }}>Content Themes</h4>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {content_themes.map((theme, i) => (
                <div key={i} style={{
                  padding: '10px',
                  backgroundColor: theme.hoverBg,
                  borderRadius: '6px',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: theme.text }}>{theme.theme}</span>
                    <span style={{
                      fontSize: '10px',
                      padding: '2px 8px',
                      backgroundColor: theme.primaryLight,
                      color: theme.primary,
                      borderRadius: '10px',
                    }}>{theme.frequency}</span>
                  </div>
                  <p style={{ margin: 0, fontSize: '12px', color: theme.textMuted, lineHeight: '1.5' }}>
                    {theme.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Backlink Strategies */}
        {backlink_strategies?.length > 0 && (
          <div style={{
            backgroundColor: theme.card,
            border: `1px solid ${theme.border}`,
            borderRadius: '10px',
            padding: '16px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <span style={{ fontSize: '18px' }}>🔗</span>
              <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: theme.text }}>Backlink Strategies</h4>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {backlink_strategies.map((strategy, i) => (
                <div key={i} style={{
                  padding: '10px',
                  backgroundColor: theme.hoverBg,
                  borderRadius: '6px',
                  borderLeft: `3px solid ${getImpactColor(strategy.effectiveness)}`,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: theme.text }}>{strategy.strategy}</span>
                    <span style={{
                      fontSize: '10px',
                      padding: '2px 8px',
                      backgroundColor: getImpactColor(strategy.effectiveness) + '20',
                      color: getImpactColor(strategy.effectiveness),
                      borderRadius: '10px',
                    }}>{strategy.effectiveness}</span>
                  </div>
                  <p style={{ margin: 0, fontSize: '12px', color: theme.textMuted, lineHeight: '1.5' }}>
                    {strategy.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Content Gaps */}
        {content_gaps?.length > 0 && (
          <div style={{
            backgroundColor: theme.card,
            border: `1px solid ${theme.border}`,
            borderRadius: '10px',
            padding: '16px',
            gridColumn: 'span 1',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <span style={{ fontSize: '18px' }}>💡</span>
              <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: theme.text }}>Content Gaps & Opportunities</h4>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {content_gaps.map((gap, i) => (
                <div key={i} style={{
                  padding: '12px',
                  backgroundColor: theme.accentLight || '#f0fdf4',
                  borderRadius: '6px',
                  border: `1px solid ${(theme.accent || '#10b981')}30`,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: theme.text }}>{gap.opportunity}</span>
                    <span style={{
                      fontSize: '10px',
                      padding: '2px 8px',
                      backgroundColor: getImpactColor(gap.potential_impact) + '20',
                      color: getImpactColor(gap.potential_impact),
                      borderRadius: '10px',
                    }}>{gap.potential_impact} impact</span>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span style={{ fontSize: '11px', color: theme.textMuted }}>Difficulty:</span>
                    <span style={{
                      fontSize: '11px',
                      fontWeight: '500',
                      color: getDifficultyColor(gap.difficulty),
                    }}>{gap.difficulty}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Technical SEO */}
        {technical_seo_trends?.length > 0 && (
          <div style={{
            backgroundColor: theme.card,
            border: `1px solid ${theme.border}`,
            borderRadius: '10px',
            padding: '16px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <span style={{ fontSize: '18px' }}>⚙️</span>
              <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: theme.text }}>Technical SEO Trends</h4>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {technical_seo_trends.map((trend, i) => (
                <div key={i} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px',
                  backgroundColor: theme.hoverBg,
                  borderRadius: '6px',
                }}>
                  <div>
                    <span style={{ fontSize: '13px', fontWeight: '500', color: theme.text }}>{trend.aspect}</span>
                    <span style={{ 
                      fontSize: '11px', 
                      color: theme.textMuted,
                      marginLeft: '8px',
                      padding: '2px 6px',
                      backgroundColor: theme.border,
                      borderRadius: '4px',
                    }}>{trend.market_adoption}</span>
                  </div>
                  <span style={{
                    fontSize: '11px',
                    padding: '3px 8px',
                    backgroundColor: trend.importance === 'critical' ? (theme.errorBg || '#fee2e2') :
                                   trend.importance === 'important' ? (theme.accentLight || '#f0fdf4') : theme.hoverBg,
                    color: trend.importance === 'critical' ? (theme.usageError || '#ef4444') :
                          trend.importance === 'important' ? (theme.accent || '#10b981') : theme.textMuted,
                    borderRadius: '10px',
                    fontWeight: '500',
                  }}>{trend.importance}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Local SEO */}
        {local_seo_factors?.relevant && (
          <div style={{
            backgroundColor: theme.card,
            border: `1px solid ${theme.border}`,
            borderRadius: '10px',
            padding: '16px',
            gridColumn: 'span 1',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <span style={{ fontSize: '18px' }}>📍</span>
              <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: theme.text }}>Local SEO Factors</h4>
            </div>
            
            {local_seo_factors.factors?.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <p style={{ margin: '0 0 6px 0', fontSize: '11px', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Key Factors</p>
                <ul style={{ margin: 0, padding: '0 0 0 16px' }}>
                  {local_seo_factors.factors.map((factor, i) => (
                    <li key={i} style={{ fontSize: '12px', color: theme.text, marginBottom: '4px', lineHeight: '1.5' }}>
                      {factor}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {local_seo_factors.optimization_tips?.length > 0 && (
              <div>
                <p style={{ margin: '0 0 6px 0', fontSize: '11px', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Optimization Tips</p>
                <ul style={{ margin: 0, padding: '0 0 0 16px' }}>
                  {local_seo_factors.optimization_tips.map((tip, i) => (
                    <li key={i} style={{ fontSize: '12px', color: theme.text, marginBottom: '4px', lineHeight: '1.5' }}>
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Events Panel: Recent headlines and news for competitors
function EventsPanel({ theme, activeTab, filteredCompetitors, onEvents, onLoadMore, onClose, updateActiveTab }) {
  const events = activeTab.events || {};
  const names = Object.keys(events);
  const activeName = activeTab.activeEventName || names[0] || null;
  const activeEntry = activeName ? events[activeName] : null;
  const candidates = filteredCompetitors.filter(c => !events[c.name]);
  const [addSelect, setAddSelect] = useState('');

  const getSentimentColor = (sentiment) => {
    const s = (sentiment || '').toLowerCase();
    if (s === 'positive') return theme.accent || '#10b981';
    if (s === 'negative') return theme.usageError || '#ef4444';
    return theme.textMuted || '#6b7280';
  };

  const getCategoryIcon = (category) => {
    const c = (category || '').toLowerCase();
    if (c.includes('product')) return '🚀';
    if (c.includes('business')) return '💼';
    if (c.includes('market')) return '📈';
    if (c.includes('award')) return '🏆';
    if (c.includes('controversy')) return '⚠️';
    if (c.includes('leadership')) return '👤';
    return '📰';
  };

  const getSignificanceColor = (significance) => {
    const s = (significance || '').toLowerCase();
    if (s === 'high') return theme.usageError || '#ef4444';
    if (s === 'medium') return theme.warning || '#f59e0b';
    return theme.accent || '#10b981';
  };

  return (
    <div style={{
      backgroundColor: theme.card,
      borderRadius: '12px',
      padding: '20px',
      boxShadow: theme.shadow,
      marginBottom: '24px',
      border: `1px solid ${theme.border}`,
    }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
        <Newspaper size={20} color={theme.primary} />
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: theme.text }}>
          Recent Events & Headlines
        </h3>
        <label style={{ fontSize: '13px', fontWeight: '500', color: theme.textMuted, marginLeft: 'auto' }} htmlFor="events-months">Search window:</label>
        <select
          id="events-months"
          value={String(activeTab.eventsMonthsBack ?? 1)}
          onChange={(e) => updateActiveTab({ eventsMonthsBack: parseInt(e.target.value, 10) })}
          style={{
            padding: '6px 10px',
            fontSize: '13px',
            border: `1px solid ${theme.inputBorder}`,
            borderRadius: '6px',
            backgroundColor: theme.inputBg,
            color: theme.text,
            minWidth: '140px',
          }}
        >
          <option value="1">Last 1 month</option>
          <option value="3">Last 3 months</option>
          <option value="6">Last 6 months</option>
          <option value="12">Last 1 year</option>
          <option value="24">Last 2 years</option>
        </select>
      </div>
      <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: theme.textMuted }}>
        Track recent news, product launches, and market developments for competitors. Headlines are limited to the search window above (default: last 1 month). New requests use the selected range.
      </p>

      {/* Tab bar for multiple event analyses */}
      {names.length > 0 && (
        <div style={{
          display: 'flex',
          gap: '4px',
          flexWrap: 'wrap',
          marginBottom: '16px',
          borderBottom: `1px solid ${theme.border}`,
          paddingBottom: '12px',
        }}>
          {names.map((name) => (
            <div
              key={name}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 12px',
                fontSize: '13px',
                fontWeight: '500',
                backgroundColor: activeName === name ? theme.tabActiveBg : 'transparent',
                color: activeName === name ? theme.text : theme.tabInactiveText,
                border: `1px solid ${activeName === name ? theme.tabActiveBorder : theme.border}`,
                borderRadius: '6px',
                cursor: 'pointer',
              }}
              onClick={() => updateActiveTab({ activeEventName: name })}
            >
              {name}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onClose(name); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '18px',
                  height: '18px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  color: theme.textMuted,
                }}
                title="Close"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add new competitor selector */}
      {names.length === 0 && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          alignItems: 'center',
          marginBottom: '16px',
        }}>
          <select
            value={addSelect}
            onChange={(e) => setAddSelect(e.target.value)}
            style={{
              padding: '8px 12px',
              fontSize: '13px',
              border: `1px solid ${theme.inputBorder}`,
              borderRadius: '6px',
              backgroundColor: theme.inputBg,
              color: theme.text,
              minWidth: '180px',
            }}
          >
            <option value="">Choose a competitor...</option>
            {filteredCompetitors.map((c) => (
              <option key={c.name} value={c.name}>{c.name}</option>
            ))}
          </select>
          <button
            type="button"
            disabled={!addSelect}
            onClick={() => {
              const comp = filteredCompetitors.find(c => c.name === addSelect);
              if (comp) onEvents(comp);
              setAddSelect('');
            }}
            style={{
              padding: '12px 24px',
              fontSize: '15px',
              fontWeight: '600',
              backgroundColor: addSelect ? theme.primary : 'transparent',
              color: addSelect ? theme.primaryContrast : theme.primary,
              border: addSelect ? 'none' : `1px solid ${theme.primary}`,
              borderRadius: '8px',
              cursor: addSelect ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'background-color 0.2s',
            }}
          >
            <Newspaper size={14} />
            Get Headlines
          </button>
        </div>
      )}

      {/* Add more selector when tabs exist */}
      {names.length > 0 && candidates.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: '16px' }}>
          <span style={{ fontSize: '12px', color: theme.textMuted }}>Add competitor:</span>
          <select
            value={addSelect}
            onChange={(e) => setAddSelect(e.target.value)}
            style={{
              padding: '6px 10px',
              fontSize: '12px',
              border: `1px solid ${theme.inputBorder}`,
              borderRadius: '6px',
              backgroundColor: theme.inputBg,
              color: theme.text,
              minWidth: '160px',
            }}
          >
            <option value="">Choose...</option>
            {candidates.map((c) => (
              <option key={c.name} value={c.name}>{c.name}</option>
            ))}
          </select>
          <button
            type="button"
            disabled={!addSelect}
            onClick={() => {
              const comp = filteredCompetitors.find(c => c.name === addSelect);
              if (comp) onEvents(comp);
              setAddSelect('');
            }}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              backgroundColor: addSelect ? theme.primary : 'transparent',
              color: addSelect ? theme.primaryContrast : theme.primary,
              border: addSelect ? 'none' : `1px solid ${theme.primary}`,
              borderRadius: '6px',
              cursor: addSelect ? 'pointer' : 'not-allowed',
            }}
          >
            Add
          </button>
        </div>
      )}

      {/* Loading state */}
      {activeEntry?.loading && (
        <div style={{ padding: '24px', textAlign: 'center', color: theme.textMuted }}>
          <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite', display: 'inline-block', marginBottom: '8px' }} />
          <p style={{ margin: 0 }}>Loading headlines for {activeName}...</p>
          <p style={{ margin: '8px 0 0 0', fontSize: '12px', opacity: 0.7 }}>Research may take up to a minute.</p>
        </div>
      )}

      {/* Error state */}
      {activeEntry?.error && !activeEntry?.loading && (
        <div style={{
          padding: '16px',
          backgroundColor: theme.errorBg,
          border: `1px solid ${theme.errorBorder}`,
          borderRadius: '8px',
          color: theme.errorText,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '12px',
        }}>
          <span><strong>{activeName}</strong>: {friendlyMessage(activeEntry.error)}</span>
          <button
            type="button"
            onClick={() => { const comp = filteredCompetitors.find(c => c.name === activeName); if (comp) onEvents(comp); }}
            style={{ flexShrink: 0, padding: '4px 12px', fontSize: '12px', fontWeight: '500', backgroundColor: 'transparent', color: theme.errorText, border: `1px solid ${theme.errorBorder}`, borderRadius: '6px', cursor: 'pointer' }}
          >
            Try Again
          </button>
        </div>
      )}

      {/* Content */}
      {activeEntry?.data && (
        <>
          {activeEntry.warning && <WarningBanner message={activeEntry.warning} theme={theme} />}
          <EventsContent
            data={activeEntry.data}
            theme={theme}
            name={activeName}
            onLoadMore={() => onLoadMore(activeName)}
            loadingMore={activeEntry?.loadingMore || false}
          />
        </>
      )}

      {/* Empty state */}
      {names.length === 0 && !activeEntry && (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          backgroundColor: theme.hoverBg,
          borderRadius: '8px',
          color: theme.textMuted,
        }}>
          <Newspaper size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
          <p style={{ margin: 0, fontSize: '14px' }}>Select a competitor above to view their recent headlines and events</p>
        </div>
      )}
    </div>
  );
}

// Events Content Component
function EventsContent({ data, theme, name, onLoadMore, loadingMore }) {
  const { headlines, market_narrative, trending_themes, competitive_implications } = data;

  const getSentimentColor = (sentiment) => {
    const s = (sentiment || '').toLowerCase();
    if (s === 'positive') return theme.accent || '#10b981';
    if (s === 'negative') return theme.usageError || '#ef4444';
    return theme.textMuted || '#6b7280';
  };

  const getCategoryIcon = (category) => {
    const c = (category || '').toLowerCase();
    if (c.includes('product')) return '🚀';
    if (c.includes('business')) return '💼';
    if (c.includes('market')) return '📈';
    if (c.includes('award')) return '🏆';
    if (c.includes('controversy')) return '⚠️';
    if (c.includes('leadership')) return '👤';
    return '📰';
  };

  const getSignificanceColor = (significance) => {
    const s = (significance || '').toLowerCase();
    if (s === 'high') return theme.usageError || '#ef4444';
    if (s === 'medium') return theme.warning || '#f59e0b';
    return theme.accent || '#10b981';
  };

  return (
    <div style={{ marginTop: '8px' }}>
      {/* Market Narrative */}
      {market_narrative && (
        <div style={{
          backgroundColor: theme.primaryLight,
          border: `1px solid ${theme.primary}`,
          borderRadius: '10px',
          padding: '16px',
          marginBottom: '20px',
        }}>
          <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', fontWeight: '700', color: theme.primary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Market Narrative
          </h4>
          <p style={{ margin: 0, fontSize: '14px', color: theme.text, lineHeight: '1.7' }}>
            {market_narrative}
          </p>
        </div>
      )}

      {/* Headlines List */}
      {headlines && headlines.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: theme.text }}>
              Recent Headlines ({headlines.length})
            </h4>
            <button
              type="button"
              onClick={onLoadMore}
              disabled={loadingMore}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: '500',
                backgroundColor: loadingMore ? theme.hoverBg : theme.accentLight,
                color: loadingMore ? theme.textMuted : theme.accent,
                border: `1px dashed ${loadingMore ? theme.border : theme.accent}`,
                borderRadius: '6px',
                cursor: loadingMore ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              {loadingMore ? (
                <><RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> Loading... (may take up to a minute)</>
              ) : (
                <><Plus size={14} /> Load More Headlines</>
              )}
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {headlines.map((headline, i) => (
              <div key={i} style={{
                padding: '14px',
                backgroundColor: theme.card,
                border: `1px solid ${theme.border}`,
                borderRadius: '8px',
                borderLeft: `4px solid ${getSentimentColor(headline.sentiment)}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <span style={{ fontSize: '20px', flexShrink: 0 }}>{getCategoryIcon(headline.category)}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: '6px' }}>
                      <span style={{ 
                        fontSize: '11px', 
                        padding: '2px 8px', 
                        backgroundColor: theme.hoverBg,
                        color: theme.textMuted,
                        borderRadius: '10px',
                      }}>
                        {headline.date}
                      </span>
                      <span style={{ 
                        fontSize: '11px', 
                        padding: '2px 8px', 
                        backgroundColor: getSignificanceColor(headline.significance) + '20',
                        color: getSignificanceColor(headline.significance),
                        borderRadius: '10px',
                        fontWeight: '600',
                      }}>
                        {headline.significance} impact
                      </span>
                      <span style={{ 
                        fontSize: '11px', 
                        padding: '2px 8px', 
                        backgroundColor: getSentimentColor(headline.sentiment) + '20',
                        color: getSentimentColor(headline.sentiment),
                        borderRadius: '10px',
                        fontWeight: '500',
                        textTransform: 'capitalize',
                      }}>
                        {headline.sentiment}
                      </span>
                    </div>
                    <h5 style={{ margin: '0 0 6px 0', fontSize: '14px', fontWeight: '600', color: theme.text }}>
                      {headline.title}
                    </h5>
                    <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: theme.textMuted, lineHeight: '1.5' }}>
                      {headline.summary}
                    </p>
                    {headline.sources_hint && (
                      <p style={{ margin: 0, fontSize: '11px', color: theme.textMuted, fontStyle: 'italic' }}>
                        Sources: {headline.sources_hint}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {headlines && headlines.length === 0 && (
        <div style={{
          padding: '24px',
          textAlign: 'center',
          backgroundColor: theme.hoverBg,
          borderRadius: '8px',
          color: theme.textMuted,
          marginBottom: '20px',
        }}>
          <p style={{ margin: 0 }}>No recent headlines found for this competitor.</p>
        </div>
      )}

      {/* Trending Themes */}
      {trending_themes && trending_themes.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: theme.text }}>
            Trending Themes
          </h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {trending_themes.map((theme, i) => (
              <div key={i} style={{
                padding: '12px',
                backgroundColor: theme.hoverBg,
                borderRadius: '8px',
                flex: '1 1 200px',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: theme.text }}>{theme.theme}</span>
                  <span style={{ 
                    fontSize: '10px', 
                    padding: '2px 8px', 
                    backgroundColor: theme.primaryLight,
                    color: theme.primary,
                    borderRadius: '10px',
                  }}>
                    {theme.frequency}
                  </span>
                </div>
                <p style={{ margin: 0, fontSize: '12px', color: theme.textMuted, lineHeight: '1.4' }}>
                  {theme.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Competitive Implications */}
      {competitive_implications && (
        <div style={{
          backgroundColor: theme.card,
          border: `1px solid ${theme.border}`,
          borderRadius: '10px',
          padding: '16px',
        }}>
          <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', fontWeight: '700', color: theme.text, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            🎯 Competitive Implications
          </h4>
          <p style={{ margin: 0, fontSize: '14px', color: theme.text, lineHeight: '1.7' }}>
            {competitive_implications}
          </p>
        </div>
      )}

      <SourcesSection sources={data.grounding_sources} theme={theme} />
    </div>
  );
}

/**
 * Renders a collapsible list of web sources consulted by the AI during grounded research.
 * sources: [{uri: string, title: string}]
 */
function SourcesSection({ sources, theme }) {
  const [open, setOpen] = React.useState(false);
  if (!sources || sources.length === 0) return null;
  return (
    <div style={{
      marginTop: '16px',
      backgroundColor: theme.hoverBg,
      borderRadius: '8px',
      padding: '12px 14px',
      border: `1px solid ${theme.border}`,
    }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          color: theme.text,
          fontSize: '13px',
          fontWeight: '600',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        <span style={{ fontSize: '14px' }}>🔗</span>
        <span>{open ? '▾' : '▸'}</span>
        Web sources consulted ({sources.length})
      </button>
      {open && (
        <ul style={{
          margin: '8px 0 0 0',
          paddingLeft: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
        }}>
          {sources.map((s, i) => (
            <li key={i} style={{ fontSize: '12px' }}>
              <a
                href={s.uri}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: theme.primary || '#3b82f6', textDecoration: 'none' }}
                onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
                onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
              >
                {s.title || s.uri}
              </a>
            </li>
          ))}
        </ul>
      )}
      <p style={{ margin: '6px 0 0 0', fontSize: '11px', color: theme.textMuted, fontStyle: 'italic' }}>
        These pages were used to ground the AI's research. Verify claims independently.
      </p>
    </div>
  );
}

// Deep Dive panel: tabbed view of per-competitor deep dives (multiple kept without losing previous)
function DeepDivePanel({ theme, activeTab, filteredCompetitors, onDeepDive, onCloseDeepDive, updateActiveTab }) {
  const deepDives = activeTab.deepDives || {};
  const names = Object.keys(deepDives);
  const activeName = activeTab.activeDeepDiveName || names[0] || null;
  const activeEntry = activeName ? deepDives[activeName] : null;
  const candidates = filteredCompetitors.filter(c => !deepDives[c.name]);
  const [addSelect, setAddSelect] = useState('');

  return (
    <div style={{
      backgroundColor: theme.card,
      borderRadius: '12px',
      padding: '20px',
      boxShadow: theme.shadow,
      marginBottom: '24px',
      border: `1px solid ${theme.border}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <BookOpen size={20} color={theme.accent} />
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: theme.text }}>
          Deep Dive
        </h3>
      </div>
      <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: theme.textMuted }}>
        Detailed business summary, strengths/weaknesses, metrics, and strategic takeaway per competitor. You can open multiple deep dives; each is kept in its own tab below.
      </p>

      {names.length > 0 && (
        <div style={{
          display: 'flex',
          gap: '4px',
          flexWrap: 'wrap',
          marginBottom: '16px',
          borderBottom: `1px solid ${theme.border}`,
          paddingBottom: '12px',
        }}>
          {names.map((name) => (
            <div
              key={name}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 12px',
                fontSize: '13px',
                fontWeight: '500',
                backgroundColor: activeName === name ? theme.tabActiveBg : 'transparent',
                color: activeName === name ? theme.text : theme.tabInactiveText,
                border: `1px solid ${activeName === name ? theme.tabActiveBorder : theme.border}`,
                borderRadius: '6px',
                cursor: 'pointer',
              }}
              onClick={() => updateActiveTab({ activeDeepDiveName: name })}
            >
              {name}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onCloseDeepDive(name); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '18px',
                  height: '18px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  color: theme.textMuted,
                }}
                title="Close this deep dive"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {names.length === 0 && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          alignItems: 'center',
          marginBottom: '16px',
        }}>
          <select
            value={addSelect}
            onChange={(e) => setAddSelect(e.target.value)}
            style={{
              padding: '8px 12px',
              fontSize: '13px',
              border: `1px solid ${theme.inputBorder}`,
              borderRadius: '6px',
              backgroundColor: theme.inputBg,
              color: theme.text,
              minWidth: '180px',
            }}
          >
            <option value="">Choose a competitor...</option>
            {filteredCompetitors.map((c) => (
              <option key={c.name} value={c.name}>{c.name}</option>
            ))}
          </select>
          <button
            type="button"
            disabled={!addSelect}
            onClick={() => {
              const comp = filteredCompetitors.find(c => c.name === addSelect);
              if (comp) onDeepDive(comp);
              setAddSelect('');
            }}
            style={{
              padding: '12px 24px',
              fontSize: '15px',
              fontWeight: '600',
              backgroundColor: addSelect ? theme.primary : 'transparent',
              color: addSelect ? theme.primaryContrast : theme.primary,
              border: addSelect ? 'none' : `1px solid ${theme.primary}`,
              borderRadius: '8px',
              cursor: addSelect ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'background-color 0.2s',
            }}
          >
            <BookOpen size={14} />
            Generate Deep Dive
          </button>
        </div>
      )}

      {names.length > 0 && candidates.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: '16px' }}>
          <span style={{ fontSize: '12px', color: theme.textMuted }}>Add another:</span>
          <select
            value={addSelect}
            onChange={(e) => setAddSelect(e.target.value)}
            style={{
              padding: '6px 10px',
              fontSize: '12px',
              border: `1px solid ${theme.inputBorder}`,
              borderRadius: '6px',
              backgroundColor: theme.inputBg,
              color: theme.text,
              minWidth: '160px',
            }}
          >
            <option value="">Choose competitor...</option>
            {candidates.map((c) => (
              <option key={c.name} value={c.name}>{c.name}</option>
            ))}
          </select>
          <button
            type="button"
            disabled={!addSelect}
            onClick={() => {
              const comp = filteredCompetitors.find(c => c.name === addSelect);
              if (comp) onDeepDive(comp);
              setAddSelect('');
            }}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              backgroundColor: addSelect ? theme.primary : 'transparent',
              color: addSelect ? theme.primaryContrast : theme.primary,
              border: addSelect ? 'none' : `1px solid ${theme.primary}`,
              borderRadius: '6px',
              cursor: addSelect ? 'pointer' : 'not-allowed',
            }}
          >
            Generate
          </button>
        </div>
      )}

      {activeEntry?.loading && (
        <div style={{ padding: '24px', textAlign: 'center', color: theme.textMuted }}>
          <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite', display: 'inline-block', marginBottom: '8px' }} />
          <p style={{ margin: 0 }}>Generating deep dive for {activeName}...</p>
          <p style={{ margin: '8px 0 0 0', fontSize: '12px', opacity: 0.7 }}>Research may take up to a minute.</p>
        </div>
      )}

      {activeEntry?.error && (
        <div style={{
          padding: '16px',
          backgroundColor: theme.errorBg,
          border: `1px solid ${theme.errorBorder}`,
          borderRadius: '8px',
          color: theme.errorText,
        }}>
          <strong>{activeName}</strong>: {activeEntry.error}
        </div>
      )}

      {activeEntry?.data && <DeepDiveContent data={activeEntry.data} theme={theme} name={activeName} />}
    </div>
  );
}

// Renders one deep dive's structured data with enhanced visuals
function DeepDiveContent({ data, theme, name }) {
  // Helper to get competitive intensity/significance color
  const getIntensityColor = (level) => {
    const l = (level || '').toLowerCase();
    if (l.includes('high')) return theme.usageError || '#ef4444';
    if (l.includes('medium')) return theme.warning || '#f59e0b';
    if (l.includes('low')) return theme.accent || '#10b981';
    return theme.textMuted || '#6b7280';
  };

  // Helper to get sentiment trend color
  const getSentimentColor = (trend) => {
    const t = (trend || '').toLowerCase();
    if (t.includes('improv')) return theme.accent || '#10b981';
    if (t.includes('declin')) return theme.usageError || '#ef4444';
    return theme.textMuted || '#6b7280';
  };

  // Helper to get growth trajectory color
  const getGrowthColor = (growth) => {
    const g = (growth || '').toLowerCase();
    if (g.includes('grow')) return theme.accent || '#10b981';
    if (g.includes('declin') || g.includes('shrink')) return theme.usageError || '#ef4444';
    return theme.textMuted || '#6b7280';
  };

  return (
    <div style={{ marginTop: '8px' }}>
      {/* Executive Summary Card */}
      {data.detailed_business_summary && (
        <div style={{
          backgroundColor: theme.primaryLight || '#eff6ff',
          border: `1px solid ${theme.primary || '#3b82f6'}`,
          borderRadius: '10px',
          padding: '16px',
          marginBottom: '20px',
        }}>
          <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', fontWeight: '700', color: theme.primary || '#1d4ed8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Executive Summary
          </h4>
          <p style={{ margin: 0, fontSize: '14px', color: theme.text, lineHeight: '1.7' }}>
            {data.detailed_business_summary}
          </p>
        </div>
      )}

      {/* Key Metrics Grid */}
      {data.key_metrics && typeof data.key_metrics === 'object' && (
        <div style={{ marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: '600', color: theme.text }}>
            Key Metrics
          </h4>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '12px',
          }}>
            {data.key_metrics.revenue_estimate && (
              <MetricCard
                label="Revenue Scale"
                value={data.key_metrics.revenue_estimate}
                icon="💰"
                theme={theme}
              />
            )}
            {data.key_metrics.market_share_estimate && (
              <MetricCard
                label="Market Position"
                value={data.key_metrics.market_share_estimate}
                icon="📊"
                theme={theme}
              />
            )}
            {data.key_metrics.growth_trajectory && (
              <MetricCard
                label="Growth"
                value={data.key_metrics.growth_trajectory}
                icon="📈"
                theme={theme}
                valueColor={getGrowthColor(data.key_metrics.growth_trajectory)}
              />
            )}
            {data.key_metrics.customer_sentiment_trend && (
              <MetricCard
                label="Sentiment Trend"
                value={data.key_metrics.customer_sentiment_trend}
                icon="😊"
                theme={theme}
                valueColor={getSentimentColor(data.key_metrics.customer_sentiment_trend)}
              />
            )}
          </div>
        </div>
      )}

      {/* Target Market Table */}
      {data.target_market && typeof data.target_market === 'object' && (
        <div style={{ marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: '600', color: theme.text }}>
            Target Market
          </h4>
          <div style={{
            backgroundColor: theme.card,
            border: `1px solid ${theme.border}`,
            borderRadius: '8px',
            overflow: 'hidden',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <tbody>
                {data.target_market.primary_segment && (
                  <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                    <td style={{ padding: '10px 12px', fontWeight: '500', color: theme.textMuted, width: '40%', backgroundColor: theme.hoverBg }}>Primary Segment</td>
                    <td style={{ padding: '10px 12px', color: theme.text }}>{data.target_market.primary_segment}</td>
                  </tr>
                )}
                {data.target_market.secondary_segments?.length > 0 && (
                  <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                    <td style={{ padding: '10px 12px', fontWeight: '500', color: theme.textMuted, backgroundColor: theme.hoverBg }}>Secondary Segments</td>
                    <td style={{ padding: '10px 12px', color: theme.text }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {data.target_market.secondary_segments.map((seg, i) => (
                          <span key={i} style={{
                            fontSize: '11px',
                            padding: '2px 8px',
                            backgroundColor: theme.primaryLight,
                            color: theme.primary,
                            borderRadius: '4px',
                          }}>{seg}</span>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
                {data.target_market.geographic_focus && (
                  <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                    <td style={{ padding: '10px 12px', fontWeight: '500', color: theme.textMuted, backgroundColor: theme.hoverBg }}>Geography</td>
                    <td style={{ padding: '10px 12px', color: theme.text }}>{data.target_market.geographic_focus}</td>
                  </tr>
                )}
                {data.target_market.positioning && (
                  <tr>
                    <td style={{ padding: '10px 12px', fontWeight: '500', color: theme.textMuted, backgroundColor: theme.hoverBg }}>Positioning</td>
                    <td style={{ padding: '10px 12px', color: theme.text }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '3px 10px',
                        backgroundColor: theme.accentLight || '#d1fae5',
                        color: theme.accent || '#065f46',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500',
                      }}>
                        {data.target_market.positioning}
                      </span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Value Proposition */}
      {data.value_proposition && (
        <div style={{ marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', fontWeight: '600', color: theme.text }}>
            Value Proposition
          </h4>
          <div style={{
            padding: '14px',
            backgroundColor: theme.accentLight || '#f0fdf4',
            borderLeft: `4px solid ${theme.accent || '#10b981'}`,
            borderRadius: '0 8px 8px 0',
          }}>
            <p style={{ margin: 0, fontSize: '14px', color: theme.text, lineHeight: '1.6', fontStyle: 'italic' }}>
              "{data.value_proposition}"
            </p>
          </div>
        </div>
      )}

      {/* SWOT-style Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '16px',
        marginBottom: '20px',
      }}>
        {data.strengths?.length > 0 && (
          <SwotCard
            title="Strengths"
            items={data.strengths}
            icon="💪"
            theme={theme}
            accentColor={theme.accent || '#10b981'}
            bgColor={theme.accentLight || '#f0fdf4'}
          />
        )}
        {data.weaknesses?.length > 0 && (
          <SwotCard
            title="Weaknesses"
            items={data.weaknesses}
            icon="⚠️"
            theme={theme}
            accentColor={theme.usageError || '#ef4444'}
            bgColor={theme.errorBg || '#fef2f2'}
          />
        )}
        {data.opportunities?.length > 0 && (
          <SwotCard
            title="Opportunities"
            items={data.opportunities}
            icon="🚀"
            theme={theme}
            accentColor={theme.blue || '#3b82f6'}
            bgColor={theme.primaryLight || '#eff6ff'}
          />
        )}
        {data.risks?.length > 0 && (
          <SwotCard
            title="Risks"
            items={data.risks}
            icon="⚡"
            theme={theme}
            accentColor={theme.warning || '#f59e0b'}
            bgColor='#fef3c7'
          />
        )}
      </div>

      {/* Differentiators & Partnerships */}
      {(data.competitive_differentiators?.length > 0 || data.key_partnerships_or_channels?.length > 0) && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '16px',
          marginBottom: '20px',
        }}>
          {data.competitive_differentiators?.length > 0 && (
            <div>
              <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', fontWeight: '600', color: theme.text }}>
                Competitive Differentiators
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {data.competitive_differentiators.map((item, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px',
                    padding: '10px 12px',
                    backgroundColor: theme.card,
                    border: `1px solid ${theme.border}`,
                    borderRadius: '6px',
                  }}>
                    <span style={{ fontSize: '14px' }}>⭐</span>
                    <span style={{ fontSize: '13px', color: theme.text, lineHeight: '1.5' }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {data.key_partnerships_or_channels?.length > 0 && (
            <div>
              <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', fontWeight: '600', color: theme.text }}>
                Key Partnerships & Channels
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {data.key_partnerships_or_channels.map((item, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px',
                    padding: '10px 12px',
                    backgroundColor: theme.card,
                    border: `1px solid ${theme.border}`,
                    borderRadius: '6px',
                  }}>
                    <span style={{ fontSize: '14px' }}>🤝</span>
                    <span style={{ fontSize: '13px', color: theme.text, lineHeight: '1.5' }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Recent Developments Timeline */}
      {data.recent_notable_developments?.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: '600', color: theme.text }}>
            Recent Notable Developments
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {data.recent_notable_developments.map((item, i) => (
              <div key={i} style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                padding: '12px 14px',
                backgroundColor: theme.card,
                border: `1px solid ${theme.border}`,
                borderRadius: '8px',
                position: 'relative',
              }}>
                <div style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  backgroundColor: theme.primaryLight,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  flexShrink: 0,
                }}>
                  📰
                </div>
                <span style={{ fontSize: '13px', color: theme.text, lineHeight: '1.6', flex: 1 }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Competitive Significance / Intensity */}
      {(data.competitive_intensity || data.competitive_threat_level) && (
        <div style={{ marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', fontWeight: '600', color: theme.text }}>
            Competitive Significance
          </h4>
          <div style={{
            padding: '16px',
            backgroundColor: theme.card,
            border: `1px solid ${theme.border}`,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              backgroundColor: getIntensityColor(data.competitive_intensity || data.competitive_threat_level) + '20',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
              flexShrink: 0,
            }}>
              🎯
            </div>
            <div style={{ flex: 1 }}>
              <div style={{
                display: 'inline-block',
                padding: '4px 12px',
                backgroundColor: getIntensityColor(data.competitive_intensity || data.competitive_threat_level) + '20',
                color: getIntensityColor(data.competitive_intensity || data.competitive_threat_level),
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '6px',
              }}>
                {(data.competitive_intensity || data.competitive_threat_level)} significance
              </div>
              <p style={{ margin: 0, fontSize: '12px', color: theme.textMuted }}>
                Based on market position, growth trajectory, and competitive differentiation
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Summary Verdict */}
      {data.summary_verdict && (
        <div style={{
          backgroundColor: theme.card,
          border: `2px solid ${theme.primary || '#3b82f6'}`,
          borderRadius: '10px',
          padding: '16px',
        }}>
          <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', fontWeight: '700', color: theme.primary || '#1d4ed8' }}>
            🎯 Strategic Verdict
          </h4>
          <p style={{ margin: 0, fontSize: '14px', color: theme.text, lineHeight: '1.7' }}>
            {data.summary_verdict}
          </p>
        </div>
      )}

      <SourcesSection sources={data.grounding_sources} theme={theme} />
    </div>
  );
}

// Metric Card Component
function MetricCard({ label, value, icon, theme, valueColor }) {
  return (
    <div style={{
      backgroundColor: theme.card,
      border: `1px solid ${theme.border}`,
      borderRadius: '8px',
      padding: '12px',
      textAlign: 'center',
    }}>
      <div style={{ fontSize: '20px', marginBottom: '4px' }}>{icon}</div>
      <div style={{
        fontSize: '11px',
        color: theme.textMuted,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '4px',
      }}>
        {label}
      </div>
      <div style={{
        fontSize: '13px',
        fontWeight: '600',
        color: valueColor || theme.text,
        lineHeight: '1.3',
      }}>
        {value}
      </div>
    </div>
  );
}

// SWOT Card Component
function SwotCard({ title, items, icon, theme, accentColor, bgColor }) {
  return (
    <div style={{
      backgroundColor: bgColor,
      border: `1px solid ${accentColor}40`,
      borderRadius: '8px',
      padding: '14px',
    }}>
      <h5 style={{
        margin: '0 0 10px 0',
        fontSize: '12px',
        fontWeight: '700',
        color: accentColor,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
      }}>
        <span>{icon}</span> {title}
      </h5>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
        {items.map((item, i) => (
          <li key={i} style={{
            fontSize: '12px',
            color: theme.text,
            lineHeight: '1.5',
            padding: '4px 0',
            borderBottom: i < items.length - 1 ? `1px dashed ${accentColor}30` : 'none',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '6px',
          }}>
            <span style={{ color: accentColor, flexShrink: 0 }}>•</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Selected Competitors Comparison Component
function SelectedCompetitorsComparison({ competitors, onClose, onRemove }) {
  const [sortBy, setSortBy] = React.useState('sentiment'); // 'sentiment', 'price', 'name'
  const [sortOrder, setSortOrder] = React.useState('desc'); // 'asc', 'desc'

  if (competitors.length === 0) return null;

  const sortedCompetitors = [...competitors].sort((a, b) => {
    let comparison = 0;
    switch (sortBy) {
      case 'price':
        comparison = (a.current_price || 0) - (b.current_price || 0);
        break;
      case 'sentiment':
        comparison = (a.sentiment_score || 0) - (b.sentiment_score || 0);
        break;
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      default:
        comparison = 0;
    }
    return sortOrder === 'desc' ? -comparison : comparison;
  });

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return '⇅';
    return sortOrder === 'desc' ? '↓' : '↑';
  };

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      marginBottom: '24px',
      border: '2px solid #3b82f6',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
      }}>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#111827' }}>
          Comparing {competitors.length} Selected Competitors
        </h3>
        <button
          onClick={onClose}
          style={{
            padding: '6px 12px',
            fontSize: '12px',
            backgroundColor: '#f3f4f6',
            color: '#374151',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
          }}
        >
          Hide Comparison
        </button>
      </div>

      {/* Comparison Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
              <th 
                onClick={() => handleSort('name')}
                style={{ textAlign: 'left', padding: '12px', fontWeight: '600', color: '#374151', cursor: 'pointer', userSelect: 'none' }}
              >
                Competitor {getSortIcon('name')}
              </th>
              <th 
                onClick={() => handleSort('price')}
                style={{ textAlign: 'center', padding: '12px', fontWeight: '600', color: '#374151', cursor: 'pointer', userSelect: 'none' }}
              >
                Price {getSortIcon('price')}
              </th>
              <th 
                onClick={() => handleSort('sentiment')}
                style={{ textAlign: 'center', padding: '12px', fontWeight: '600', color: '#374151', cursor: 'pointer', userSelect: 'none' }}
              >
                Sentiment {getSortIcon('sentiment')}
              </th>
              <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600', color: '#374151' }}>Key Features</th>
              <th style={{ textAlign: 'center', padding: '12px', fontWeight: '600', color: '#374151' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {sortedCompetitors.map((comp, idx) => (
              <tr key={comp.name} style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: '12px' }}>
                  <div style={{ fontWeight: '600', color: '#111827' }}>{comp.name}</div>
                  {comp.website && (
                    <a href={comp.website} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: '#3b82f6' }}>
                      Visit Site
                    </a>
                  )}
                </td>
                <td style={{ padding: '12px', textAlign: 'center' }}>
                  <div style={{ fontWeight: '700', color: '#111827' }}>
                    ${comp.current_price?.toFixed(2) || 'N/A'}
                  </div>
                </td>
                <td style={{ padding: '12px', textAlign: 'center' }}>
                  <span style={{
                    display: 'inline-block',
                    padding: '4px 10px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '600',
                    backgroundColor: (comp.sentiment_score || 0) >= 0.85 ? '#d1fae5' :
                                    (comp.sentiment_score || 0) >= 0.5  ? '#fef3c7' : '#fee2e2',
                    color: (comp.sentiment_score || 0) >= 0.85 ? '#065f46' :
                           (comp.sentiment_score || 0) >= 0.5  ? '#92400e' : '#991b1b',
                  }}>
                    {((comp.sentiment_score || 0) * 100).toFixed(0)}%
                  </span>
                </td>
                <td style={{ padding: '12px' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {(comp.key_features || []).slice(0, 3).map((feature, i) => (
                      <span key={i} style={{
                        fontSize: '11px',
                        padding: '2px 8px',
                        backgroundColor: '#f3f4f6',
                        borderRadius: '4px',
                        color: '#374151',
                      }}>
                        {feature}
                      </span>
                    ))}
                  </div>
                </td>
                <td style={{ padding: '12px', textAlign: 'center' }}>
                  <button
                    onClick={() => onRemove(comp.name)}
                    style={{
                      padding: '6px',
                      backgroundColor: '#fee2e2',
                      color: '#991b1b',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                    title="Remove from comparison"
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Summary Stats */}
      <div style={{
        marginTop: '16px',
        padding: '16px',
        backgroundColor: '#f9fafb',
        borderRadius: '8px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '16px',
      }}>
        <div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Price Range</div>
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
            ${Math.min(...competitors.map(c => c.current_price || 0)).toFixed(2)} - 
            ${Math.max(...competitors.map(c => c.current_price || 0)).toFixed(2)}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Avg Sentiment</div>
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
            {(competitors.reduce((sum, c) => sum + (c.sentiment_score || 0), 0) / competitors.length * 100).toFixed(0)}%
          </div>
        </div>
        <div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Best Value</div>
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
            {competitors.reduce((best, c) => {
              const score = (c.sentiment_score || 0) / (c.current_price || 1);
              const bestScore = (best.sentiment_score || 0) / (best.current_price || 1);
              return score > bestScore ? c : best;
            }, competitors[0])?.name}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
